import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { createAction, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createAppSlice } from "../../app/createAppSlice";
import type { AppThunk } from "../../app/store";
import { JsonNumber, ng } from "@common/ngds";
import { IFile, AppType } from "@common/types/sharedTypes";
import { selectToken, selectUser, UserSlice } from "../user/userSlice";
import { updateActiveLcosInContext, GetSelfWeightGuid } from "../../helpers/funlib";
import { selfWeightLineLoads } from "@common/helpers/elementMass";
import type { ElementDbs, SelfWeightPiece } from "@common/helpers/elementMass";
import { createOutputPositions } from "../../helpers/auxiliary";
import { t } from "i18next";
import { v4 as uuidv4 } from "uuid";

import { BoundingBox } from "../../helpers/reinf/ReinfFun";

import { getClientId, createID, CreateGUID } from "@common/helpers/funlib";
import { classifySupport } from "@common/helpers/sharedTypes";
import {
  fetchSection,
  fetchMaterial,
  materialSectionSlice,
  useMaterials,
} from "../materialsSections/materialSectionSlice";
import {
  ErrorException,
  formatCalcErrorValues,
  getLogErrors,
  joinLogErrors,
  throwIfErrors,
  NgErrorPayload,
} from "@common/helpers/error";
import { showCalcWarnings } from "./calcWarnings";
import { getSectionGeometry, normalizeSectionLength } from "./sectionGeometry";
import { exactBarCheckMessage, failingExactBars } from "./exactBarChecks";
import { calcStepKey, readCalcStream } from "./calcProgress";
import type { CalcOperation } from "./calcProgress";
import { FEATURE_FLAG_HEADER, featureFlagHeaderValue } from "@common/types/featureFlags";
import { calcRunStarted, setCalcProgress, calcRunFinished, addAlert, setFlaggedExactBars } from "../ui/uiSlice";

const generateLoadCombsApi = createAsyncThunk<
  ng.endpoint_args.eurocode.LoadCombGenArgsOut | null,
  { input: ng.endpoint_args.eurocode.LoadCombGenArgsIn_json },
  { rejectValue: NgErrorPayload }
>("projects/generateLoadCombs", async (args: { input: ng.endpoint_args.eurocode.LoadCombGenArgsIn_json }, thunkAPI) => {
  try {
    const state = thunkAPI.getState() as { UserSlice: { token: string | null } };
    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "Content-Type": "application/json",
      "x-client-id": getClientId(),
    };

    const response = await fetch(`/api/code/generateLoadCombs`, {
      method: "POST",
      headers: tempheaders,
      body: JSON.stringify(args.input),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error("generateLoadCombs failed:", response.status, errorBody);
      throw new Error(`Failed to fetch generateLoadCombs: ${response.status} ${errorBody}`);
    }

    const json = await response.json();
    if (json === null || json === undefined) return null;
    const out = ng.endpoint_args.eurocode.LoadCombGenArgsOut.fromObject(json);

    throwIfErrors(joinLogErrors([getLogErrors(out.log)]));

    return out;
  } catch (error) {
    if (error instanceof ErrorException) {
      return thunkAPI.rejectWithValue({ ng_errors: error.ng_errors });
    }

    throw error;
  }
});

// Sends the entire project to the eurocode "/EN/gamma" endpoint (via /api/code/updateGamma) to
// recompute the timber creep factors (kdef/kcr) and partial safety factors. The server responds
// with the updated project Data, which replaces the current project's inner data wholesale.
// Triggered when the national annex, timber service class, or column cross section changes.
export const updateGammaApi = createAsyncThunk("projects/updateGamma", async (_: void, thunkAPI) => {
  const state = thunkAPI.getState() as { UserSlice: { token: string | null }; ProjectSlice: ProjectState };

  const projectData = state.ProjectSlice.project?.data?.data;
  if (!projectData) {
    throw new Error("updateGamma: no project loaded");
  }

  const tempheaders = {
    authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
    "Content-Type": "application/json",
    "x-client-id": getClientId(),
  };

  const response = await fetch(`/api/code/updateGamma`, {
    method: "POST",
    headers: tempheaders,
    body: JSON.stringify(projectData),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    console.error("updateGamma failed:", response.status, errorBody);
    throw new Error(`Failed to fetch updateGamma: ${response.status} ${errorBody}`);
  }

  // Server returns the updated ng.project.Data in toObject form.
  return (await response.json()) as ng.project.Data_json;
});

export const fetchCoverAndSpaces = createAsyncThunk(
  "projects/fetchCoverAndSpaces",
  async (args: { input: ng.endpoint_args.rc_design.CoverSpaceInput_json }, thunkAPI) => {
    const state = thunkAPI.getState() as { UserSlice: { token: string | null } };
    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "Content-Type": "application/json",
      "x-client-id": getClientId(),
    };

    const response = await fetch(`/api/projects/coverAndSpaces`, {
      method: "POST",
      headers: tempheaders,
      body: JSON.stringify(args.input),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error("coverAndSpaces failed:", response.status, errorBody);
      throw new Error(`Failed to fetch cover and spaces: ${response.status} ${errorBody}`);
    }

    return await response.json();
  },
);

/**
 * Guid of the self-weight load case, or undefined. Goes through fromObject because a load case held
 * as JSON carries its type as the enum name ("TYPE_SELF_WEIGHT") or its number, depending on whether
 * it came off the wire or from a reducer.
 */
const selfWeightCaseGuid = (loadcases: unknown[] | null | undefined): string | undefined =>
  (loadcases ?? []).map((lc) => ng.loadcase.Data.fromObject(lc)).find((lc) => lc.type === ng.loadcase.Type.TYPE_SELF_WEIGHT)
    ?.id?.guid;

/** One self-weight piece as the line load the project stores (see syncSelfWeightLoads). */
const selfWeightLoadJson = (piece: SelfWeightPiece, lcGuid: string, elemGuid: string) => ({
  id: { guid: CreateGUID(), name: "Self-weight" },
  type: ng.load.Type.TYPE_FORCE,
  distribution: ng.load.DistributionType.DISTRIBUTION_TYPE_LINE,
  direction: { x: -1, y: 0, z: 0 },
  positions: [
    { x: piece.start, y: 0, z: 0 },
    { x: piece.end, y: 0, z: 0 },
  ],
  values: [piece.qStart, piece.qEnd],
  lcaseGuid: lcGuid,
  assignedObjects: [elemGuid],
});

/** Carbon footprint of one element, in the JSON shape the project stores it in (`output.carbon`). */
type ElementCarbonJson = ng.environmental_parameters.ElementCarbonFootprint_json;

/**
 * Recomputes the embodied-carbon figure from the current input, without running a code check.
 *
 * A code check returns carbon as part of its output, but only when the user asks for one, which
 * left the readout stale (or empty) for the whole editing session. This calls the dedicated
 * /carbonfootprint endpoint instead, so changing the section, the material or (for concrete) the
 * reinforcement updates the number right away — carbon only informs those choices if it is on
 * screen while they are being made. The server picks the design service from the column's material
 * (see carbonPathForMaterial), so this is one call for concrete, steel and timber alike.
 *
 * Dispatched from the sync middleware for the input changes that actually move the figure (see
 * client/middleware/carbonTriggers.ts); the result is written into the project's `output.carbon`,
 * the same place a code check puts it.
 */
export const updateCarbonFootprint = createAsyncThunk<ElementCarbonJson[], void>(
  "projects/carbonFootprint",
  async (_: void, thunkAPI) => {
    const state = thunkAPI.getState() as { UserSlice: { token: string | null }; ProjectSlice: ProjectState };

    const input = state.ProjectSlice.project?.data?.data?.input;
    if (!input) {
      throw new Error("carbonFootprint: no project input");
    }

    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "Content-Type": "application/json",
      "x-client-id": getClientId(),
    };

    const response = await fetch(`/api/projects/carbonfootprint`, {
      method: "POST",
      headers: tempheaders,
      body: JSON.stringify({ input } as ng.endpoint_args.common_api_functions.CarbonFootPrintInput_json),
      signal: thunkAPI.signal,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error("carbonfootprint failed:", response.status, errorBody);
      throw new Error(`Failed to fetch carbon footprint: ${response.status}`);
    }

    // Round-trip through the generated message so enum casing and missing fields are normalised
    // the same way as a code check's carbon, which the readout also renders.
    const out = ng.endpoint_args.common_api_functions.CarbonFootPrintOutput.fromObject(await response.json());

    // The endpoint reports missing emission data and the like in its log. This is not worth a toast
    // — the readout already flags an incomplete total itself — but it should be traceable.
    const logErrors = getLogErrors(out.log);
    if (logErrors.length > 0) {
      console.warn("carbonfootprint returned log entries:", logErrors);
    }

    return ng.endpoint_args.common_api_functions.CarbonFootPrintOutput.toObject(out)?.carbon ?? [];
  },
);

export type CalcStatus = "idle" | "loading" | "failed" | "succeeded";
export type CalculationMethod = "stiffness" | "curvature" | "general";
export type FireExposureSide = "upperSide" | "bottomSide" | "leftSide" | "rightSide";
export const FIRE_EXPOSURE_SIDES: readonly FireExposureSide[] = ["upperSide", "bottomSide", "leftSide", "rightSide"];

/**
 * The concrete fire settings a freshly opened project may be missing, as SXInputConcreteFireSettings
 * works them out on mount (see initConcreteFireDefaults). Only the fields that actually need writing
 * are present — an empty patch is never dispatched.
 */
export type ConcreteFireDefaults = {
  fireTime?: number;
  aggregate?: ng.design.concrete.Aggregates;
  manufacturing?: ng.design.concrete.ManufacturingType;
  use02Epsilon?: boolean;
  sides?: Partial<Record<FireExposureSide, boolean>>;
};

export type ProjectState = {
  project: IFile | null;
  status: "idle" | "loading" | "failed" | "succeeded";
  analysisStatus: CalcStatus;
  codeCheckStatus: CalcStatus;
  designStatus: CalcStatus;
  bucklingStatus: CalcStatus;
  // Status of the standalone carbon-footprint call (see updateCarbonFootprint). Only tells the
  // readout whether the figure it is showing is currently being recomputed — a failure here is not
  // a failed calculation from the user's point of view and never becomes a toast.
  carbonStatus: CalcStatus;
  // requestId of the most recently started carbon call. Edits can outpace the endpoint, so two
  // calls can be in flight at once; only the newest one's answer may be applied, or a slow response
  // for an older input would overwrite the figure for the current one.
  carbonRequestId: string | null;
  generateLoadCombsStatus: "idle" | "loading";
  gammaStatus: "idle" | "loading";
  projects?: unknown[];
  activeLCOs: ng.loadcombination.Data[];
  // User-facing error message from the last failed calculation (analyse/codecheck/design),
  // already localised. Null when there is nothing to show. Rendered as a toast.
  calcError: string | null;
};

const initialState: ProjectState = {
  project: null,
  status: "idle",
  analysisStatus: "idle",
  codeCheckStatus: "idle",
  designStatus: "idle",
  bucklingStatus: "idle",
  carbonStatus: "idle",
  carbonRequestId: null,
  generateLoadCombsStatus: "idle",
  gammaStatus: "idle",
  projects: [],
  activeLCOs: [],
  calcError: null,
};

const isCalculationMethod = (value: unknown): value is CalculationMethod =>
  value === "stiffness" || value === "curvature" || value === "general";

/**
 * Drop stage references to load combinations that no longer exist.
 *
 * Combinations live in `loading.combinations` while the stages reference them by guid through
 * `stages[].lcombGuids`. Removing a combination -- directly, or implicitly when its last part
 * disappears together with a load case -- would otherwise leave an orphan guid in the stage.
 * Only unresolvable guids are dropped; combinations deliberately left out of a stage stay out.
 */
const pruneOrphanLcombGuids = (structure: any): void => {
  const existing = new Set(
    ((structure?.loading?.combinations ?? []) as any[]).map((lco) => lco?.id?.guid).filter((guid) => guid != null),
  );

  for (const stage of (structure?.stages ?? []) as any[]) {
    if (!stage?.lcombGuids) continue;
    stage.lcombGuids = stage.lcombGuids.filter((guid: string) => existing.has(guid));
  }
};

const ensureEndpointCache = (state: ProjectState): Record<string, unknown> | null => {
  const settings = state.project?.data?.settings as { endpointCache?: Record<string, unknown> } | undefined;
  if (!settings) return null;

  if (!settings.endpointCache) {
    settings.endpointCache = {};
  }

  return settings.endpointCache;
};

const ensureLoadCombGenSettings = (
  state: ProjectState,
): {
  lcTypes?: string[];
  loadcases?: string[];
  simple?: ng.endpoint_args.eurocode.Simple_json;
} | null => {
  const endpointCache = ensureEndpointCache(state) as
    | ({
        loadCombGenSettings?: {
          lcTypes?: string[];
          loadcases?: string[];
          simple?: ng.endpoint_args.eurocode.Simple_json;
        };
      } & Record<string, unknown>)
    | null;

  if (!endpointCache) {
    return null;
  }

  const allLoadcases =
    state.project?.data?.data?.input?.structures?.[0]?.loading?.loadcases
      ?.map((lc) => lc.id?.guid)
      .filter((guid): guid is string => typeof guid === "string") ?? [];

  if (!endpointCache.loadCombGenSettings) {
    endpointCache.loadCombGenSettings = {
      lcTypes: [ng.loadcombination.Type_ToObject(ng.loadcombination.Type.TYPE_ULTIMATE) ?? "TYPE_ULTIMATE"],
      loadcases: allLoadcases,
      simple: ng.endpoint_args.eurocode.Simple.create({
        includePermanentLoadFavorable: false,
      }),
    };
  }

  if (!endpointCache.loadCombGenSettings.simple) {
    endpointCache.loadCombGenSettings.simple = ng.endpoint_args.eurocode.Simple.create({
      includePermanentLoadFavorable: false,
    });
  }

  if (!endpointCache.loadCombGenSettings.loadcases) {
    endpointCache.loadCombGenSettings.loadcases = allLoadcases;
  }

  if (!endpointCache.loadCombGenSettings.lcTypes || endpointCache.loadCombGenSettings.lcTypes.length === 0) {
    endpointCache.loadCombGenSettings.lcTypes = [
      ng.loadcombination.Type_ToObject(ng.loadcombination.Type.TYPE_ULTIMATE) ?? "TYPE_ULTIMATE",
    ];
  }

  return endpointCache.loadCombGenSettings;
};

const getAutoDesignFromEndpointCache = (
  endpointCache: Record<string, unknown>,
): ng.endpoint_args.common_api_functions.AutoDesign_json | null => {
  // AutoDesign is optional in endpointCache until design settings are initialized.
  const autoDesign = endpointCache.autoDesign as ng.endpoint_args.common_api_functions.AutoDesign_json | undefined;
  if (!autoDesign?.settings?.length) {
    return null;
  }

  return autoDesign;
};

const createDefaultFireBeam = (): ng.design.concrete.FireBeam_json => {
  return {
    upperSide: false,
    bottomSide: true,
    leftSide: true,
    rightSide: true,
    fireTime: 1,
    aggregate:
      ng.design.concrete.Aggregates_ToObject(ng.design.concrete.Aggregates.AGGREGATE_CALCAREOUS) ??
      "AGGREGATE_CALCAREOUS",
    use02Epsilon: false,
    manufacturingRebars:
      ng.design.concrete.ManufacturingType_ToObject(
        ng.design.concrete.ManufacturingType.MANUFACTURING_TYPE_HOT_ROLLED,
      ) ?? "MANUFACTURING_TYPE_HOT_ROLLED",
    manufacturingStrands:
      ng.design.concrete.ManufacturingType_ToObject(
        ng.design.concrete.ManufacturingType.MANUFACTURING_TYPE_HOT_ROLLED,
      ) ?? "MANUFACTURING_TYPE_HOT_ROLLED",
  };
};

// This app only designs columns, so every element specific rc design setting lives in the `column`
// member of the `element_type` oneof. Never write `rc.beam` here: the oneof would carry two members
// and the RC engine (project2concobj.cpp) checks `has_beam()` before `has_column()`, which makes it
// read the beam defaults and silently ignore everything set from this app.
const ensureRcColumnSettings = (state: ProjectState): ng.design.concrete.Column_json | null => {
  const element = getPrimaryElementJson(state) as {
    designSettings?: {
      rc?: {
        column?: ng.design.concrete.Column_json;
      };
    };
  } | null;
  if (!element) {
    return null;
  }

  if (!element.designSettings) {
    element.designSettings = {};
  }
  if (!element.designSettings.rc) {
    element.designSettings.rc = {};
  }
  if (!element.designSettings.rc.column) {
    element.designSettings.rc.column = {};
  }

  return element.designSettings.rc.column;
};

const ensureConcreteFireColumn = (state: ProjectState): ng.design.concrete.FireBeam_json | null => {
  const column = ensureRcColumnSettings(state);
  if (!column) {
    return null;
  }

  if (!column.fire) {
    column.fire = createDefaultFireBeam();
  }

  return column.fire;
};

const DEFAULT_STEEL_FIRE_DURATION_MINUTES = 60;
const DEFAULT_STEEL_TIME_STEP_SECONDS = 10;

const createDefaultSteelFire = (): ng.design.steel.Fire_json => {
  const fire: ng.design.steel.Fire_json = {
    sectionExposure:
      ng.design.steel.SectionExposure_ToObject(ng.design.steel.SectionExposure.SECTION_EXPOSURE_ALL_SIDES) ??
      "SECTION_EXPOSURE_ALL_SIDES",
    critDeflection: false,
    temperature: 400,
  };

  (fire as any).protected = false;
  (fire as any).protection_type = {
    type: "temperature",
    temperature: 400,
  };

  return fire;
};

const setSteelFireTemperatureJson = (fire: ng.design.steel.Fire_json, temperature: number) => {
  delete fire.protectingMaterial;
  fire.temperature = temperature;
  (fire as any).protection_type = {
    type: "temperature",
    temperature,
  };
};

const setSteelFireProtectingMaterialJson = (
  fire: ng.design.steel.Fire_json,
  protectingMaterial: NonNullable<ng.design.steel.Fire_json["protectingMaterial"]>,
) => {
  delete fire.temperature;
  fire.protectingMaterial = protectingMaterial;
  (fire as any).protection_type = {
    type: "protectingMaterial",
    protectingMaterial,
  };
};

const createDefaultGeneralFire = (): ng.design.general.FireGeneral_json => ({
  durationOfFire: DEFAULT_STEEL_FIRE_DURATION_MINUTES,
  timeStep: DEFAULT_STEEL_TIME_STEP_SECONDS,
  temperatureCurve:
    ng.design.general.TemperatureCurve_ToObject(ng.design.general.TemperatureCurve.TEMPERATURE_CURVE_STANDARD) ??
    "TEMPERATURE_CURVE_STANDARD",
});

type SteelFireElement = {
  designSettings?: {
    steel?: { fire?: ng.design.steel.Fire_json };
    general?: { generalFire?: ng.design.general.FireGeneral_json };
  };
};

const ensureSteelFire = (state: ProjectState): ng.design.steel.Fire_json | null => {
  const element = getPrimaryElementJson(state) as SteelFireElement | null;
  if (!element) return null;
  if (!element.designSettings) element.designSettings = {};
  if (!element.designSettings.steel) element.designSettings.steel = {};
  if (!element.designSettings.steel.fire) element.designSettings.steel.fire = createDefaultSteelFire();
  const fire = element.designSettings.steel.fire;
  if (fire.protectingMaterial) {
    setSteelFireProtectingMaterialJson(fire, {
      guid: fire.protectingMaterial.guid ?? "none_",
      thickness: Number(fire.protectingMaterial.thickness ?? 10e-3),
    });
  } else if (fire.temperature != null) {
    setSteelFireTemperatureJson(fire, Number(fire.temperature));
  } else if ((fire as any).protection_type?.type === "protectingMaterial") {
    const currentGuid = (fire as any).protection_type?.protectingMaterial?.guid ?? "none_";
    const currentThickness = Number((fire as any).protection_type?.protectingMaterial?.thickness ?? 10e-3);
    setSteelFireProtectingMaterialJson(fire, {
      guid: currentGuid,
      thickness: Number.isFinite(currentThickness) ? currentThickness : 10e-3,
    });
  } else {
    const currentTemperature = Number((fire as any).protection_type?.temperature ?? 400);
    setSteelFireTemperatureJson(fire, Number.isFinite(currentTemperature) ? currentTemperature : 400);
  }
  if (typeof fire.protected !== "boolean") {
    fire.protected = false;
  }
  return fire;
};

const ensureGeneralFire = (state: ProjectState): ng.design.general.FireGeneral_json | null => {
  const element = getPrimaryElementJson(state) as SteelFireElement | null;
  if (!element) return null;
  if (!element.designSettings) element.designSettings = {};
  if (!element.designSettings.general) element.designSettings.general = {};
  if (!element.designSettings.general.generalFire)
    element.designSettings.general.generalFire = createDefaultGeneralFire();
  return element.designSettings.general.generalFire;
};

type TimberFireElement = {
  designSettings?: {
    timber?: {
      fire?: ng.design.timber.Fire_json;
    };
    general?: { generalFire?: ng.design.general.FireGeneral_json };
  };
};

const createDefaultTimberCharacteristicData = (): ng.fire_protection.timber.CharacteristicData_json => ({
  materialType:
    ng.fire_protection.timber.MaterialType_ToObject(ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_NONE) ??
    "MATERIAL_TYPE_NONE",
  thickness: 13e-3,
  thicknessInner: 13e-3,
  thicknessOuter: 13e-3,
  charringRate: 0.8,
  failureTime: 10,
  charringStartTime: 10,
  charringRateModFactor: 0.7,
});

const createDefaultTimberFire = (): ng.design.timber.Fire_json => ({
  protected: false,
  charringRate: { calculateAuto: true, betaN: 0.7 },
  protection: {
    structProtectionTop: false,
    structProtectionBottom: false,
    structProtectionLeft: false,
    structProtectionRight: false,
    charringStartTimeTop: 0,
    charringStartTimeBottom: 0,
    charringStartTimeLeft: 0,
    charringStartTimeRight: 0,
    mtrl: { properties: createDefaultTimberCharacteristicData() },
  },
});

const ensureTimberFire = (state: ProjectState): ng.design.timber.Fire_json | null => {
  const element = getPrimaryElementJson(state) as TimberFireElement | null;
  if (!element) return null;
  if (!element.designSettings) element.designSettings = {};
  if (!element.designSettings.timber) element.designSettings.timber = {};
  if (!element.designSettings.timber.fire) element.designSettings.timber.fire = createDefaultTimberFire();
  const fire = element.designSettings.timber.fire;
  if (!fire.charringRate) fire.charringRate = { calculateAuto: true, betaN: 0.7 };
  if (!fire.protection) fire.protection = createDefaultTimberFire().protection;
  if (!fire.protection!.mtrl) fire.protection!.mtrl = { properties: createDefaultTimberCharacteristicData() };
  if (!fire.protection!.mtrl.properties) fire.protection!.mtrl.properties = createDefaultTimberCharacteristicData();
  return fire;
};

const getPrimaryElementJson = (state: ProjectState): ng.element.Data_json | null =>
  state.project?.data?.data?.input?.structures?.[0]?.elements?.[0] ?? null;

const getNodesJson = (state: ProjectState): ng.topology.ElementNode_json[] | null =>
  state.project?.data?.data?.input?.structures?.[0]?.nodes ?? null;

const getPrimaryBeamData = (state: ProjectState): ng.beam.Data | null => {
  const elementJson = getPrimaryElementJson(state);
  if (!elementJson?.beam) {
    return null;
  }

  return ng.beam.Data.fromObject(elementJson.beam);
};

const getNodesData = (state: ProjectState): ng.topology.ElementNode[] | null => {
  const nodes = getNodesJson(state);
  if (!nodes) {
    return null;
  }
  let nodesobj: ng.topology.ElementNode[] = nodes.map((node) => {
    return ng.topology.ElementNode.fromObject(node);
  });

  return nodesobj;
};

const setProjectCodeEc = (state: ProjectState, ec: ng.eurocode.DesignConfiguration) => {
  const src = ng.eurocode.DesignConfiguration.toObject(ec);
  const input = state.project?.data?.data?.input;
  if (!input) return;
  if (!src) return;
  input.ec = src;
  // note: ec is a oneof... should we invalidated the entire input with ngds?
};

const setPrimaryBeamData = (state: ProjectState, beam: ng.beam.Data) => {
  const input = state.project?.data?.data?.input;
  const structures = input?.structures;
  if (!input || !structures || structures.length === 0) {
    return;
  }

  const primaryStructure = structures[0];
  const elements = primaryStructure?.elements;
  if (!elements || elements.length === 0) {
    return;
  }

  const beamJson = ng.beam.Data.toObject(beam);
  if (!beamJson) {
    return;
  }

  const nextElements = [...elements];
  nextElements[0] = {
    ...nextElements[0],
    beam: beamJson,
  };

  const nextStructures = [...structures];
  nextStructures[0] = {
    ...primaryStructure,
    elements: nextElements,
  };

  input.structures = nextStructures;
};

const toMillimeters = (value: number): number => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return 0;
  }

  return Math.abs(num) > 1 ? num : num * 1.0e3;
};

const getPrimarySection = (state: ProjectState, beam: ng.beam.Data): ng.sections.Section | null => {
  const secGuid = beam?.segments?.[0]?.layer?.start?.secGuid;
  if (!secGuid) {
    return null;
  }

  const secJson = state.project?.data?.data?.input?.secDb?.find((section) => section?.id?.guid === secGuid) ?? null;
  return secJson ? ng.sections.Section.fromObject(secJson) : null;
};

const createEvenlySpacedPositions = (count: number, start: number, end: number): number[] => {
  if (count <= 0) {
    return [];
  }

  if (count === 1 || !Number.isFinite(start) || !Number.isFinite(end)) {
    return [(start + end) / 2];
  }

  if (end < start) {
    const mid = (start + end) / 2;
    return Array.from({ length: count }, () => mid);
  }

  const step = (end - start) / (count - 1);
  return Array.from({ length: count }, (_, index) => start + step * index);
};

const isHorizontalRebarLayer = (layer: ng.reinf.Layer): boolean =>
  layer.zone === ng.design.concrete.BeamSide.BEAM_SIDE_TOP ||
  layer.zone === ng.design.concrete.BeamSide.BEAM_SIDE_BOTTOM;

const isVerticalRebarLayer = (layer: ng.reinf.Layer): boolean =>
  layer.zone === ng.design.concrete.BeamSide.BEAM_SIDE_LEFT ||
  layer.zone === ng.design.concrete.BeamSide.BEAM_SIDE_RIGHT;

const standaloneBarsOverlap = (
  first: ng.reinf.StandAloneBar,
  secondY: number,
  secondZ: number,
  secondDiameter: number,
): boolean => {
  const firstY = Number(first.startPoint?.y ?? 0);
  const firstZ = Number(first.startPoint?.z ?? 0);
  const firstDiameter = normalizeSectionLength(Number(first.diameter));
  const secondDiameterMeters = normalizeSectionLength(Number(secondDiameter));
  const centerDistance = Math.hypot(firstY - secondY, firstZ - secondZ);
  const overlapDistance = firstDiameter / 2 + secondDiameterMeters / 2;

  return centerDistance + 1.0e-9 < overlapDistance;
};

const getStandaloneBarZoneFromPosition = (y: number, z: number): ng.design.concrete.BeamSide => {
  if (Math.abs(z) >= Math.abs(y)) {
    return z >= 0 ? ng.design.concrete.BeamSide.BEAM_SIDE_TOP : ng.design.concrete.BeamSide.BEAM_SIDE_BOTTOM;
  }

  return y >= 0 ? ng.design.concrete.BeamSide.BEAM_SIDE_RIGHT : ng.design.concrete.BeamSide.BEAM_SIDE_LEFT;
};

const getBeamLength = (beam: ng.beam.Data): number => {
  const beamLength = Number(beam?.segments?.[0]?.length);
  return Number.isFinite(beamLength) ? beamLength : 0;
};

const getStiffBuckling = (beam: ng.beam.Data): ng.beam.FlexBuckling | null => {
  const material = beam.getConcrete() ?? beam.getSteel() ?? beam.getTimber();
  return material?.fbStiff ?? null;
};

const getWeakBuckling = (beam: ng.beam.Data): ng.beam.FlexBuckling | null => {
  const material = beam.getConcrete() ?? beam.getSteel() ?? beam.getTimber();
  return material?.fbWeak ?? null;
};

const getBucklingByAxis = (beam: ng.beam.Data, axis: "Y" | "Z"): ng.beam.FlexBuckling | null =>
  axis === "Y" ? getStiffBuckling(beam) : getWeakBuckling(beam);

export type BucklingRow = {
  spanIndex: number;
  start: number;
  end: number;
  spanLength: number;
  beta: number;
  lc: number;
};

const normalizeFbBuckling = (fb: ng.beam.FlexBuckling): void => {
  const len = Math.max(fb.betas.length, fb.data.length);
  while (fb.betas.length < len) fb.betas.push(1.0);
  while (fb.data.length < len) {
    fb.data.push(ng.beam.BucklingData.create({ beta: 1.0 }));
  }
  for (let i = 0; i < len; i++) {
    fb.data[i].setBeta(fb.betas[i]);
  }
};

const deriveSpansForAxis = (sups: ng.stage.Support[], columnLength: number, axis: "Y" | "Z"): number[] => {
  const positions = new Set<number>([0, columnLength]);
  for (const sup of sups) {
    if (sup.lateral) continue;
    const x = sup.getCoord()?.x ?? 0;
    if (classifySupport(sup, axis) !== "free") {
      positions.add(x);
    }
  }
  return Array.from(positions).sort((a, b) => a - b);
};

const resyncBucklingSpans = (fb: ng.beam.FlexBuckling, newPositions: number[]): void => {
  const oldData = fb.data;
  const newData: ng.beam.BucklingData[] = [];
  const newBetas: number[] = [];

  for (let i = 0; i < newPositions.length - 1; i++) {
    const start = newPositions[i];
    const end = newPositions[i + 1];
    const match = oldData.find(
      (d) => d.position !== null && Math.abs(d.position.start - start) < 1e-9 && Math.abs(d.position.end - end) < 1e-9,
    );
    const beta = match?.getBeta() ?? 1.0;
    newData.push(
      ng.beam.BucklingData.create({
        position: ng.beam.BucklingSpan.create({ start, end }),
        beta,
      }),
    );
    newBetas.push(beta);
  }

  fb.data = newData;
  fb.betas = newBetas;
};

const resyncLtbSpans = (ltb: ng.beam.LTBuckling, newPositions: number[]): void => {
  const oldData = ltb.data;
  const oldBetas = ltb.betas;
  const newData: ng.beam.BucklingData[] = [];
  const newBetas: number[] = [];

  for (let i = 0; i < newPositions.length - 1; i++) {
    const start = newPositions[i];
    const end = newPositions[i + 1];
    const oldIndex = oldData.findIndex(
      (d) => d.position !== null && Math.abs(d.position.start - start) < 1e-9 && Math.abs(d.position.end - end) < 1e-9,
    );
    const oldBeta = oldIndex >= 0 ? (oldBetas[oldIndex] ?? oldData[oldIndex]?.getBeta()) : undefined;
    const beta = Number.isFinite(Number(oldBeta)) && Number(oldBeta) > 0 ? Number(oldBeta) : 1.0;
    newData.push(
      ng.beam.BucklingData.create({
        position: ng.beam.BucklingSpan.create({ start, end }),
        beta,
      }),
    );
    newBetas.push(beta);
  }

  ltb.data = newData;
  ltb.betas = newBetas;
};

const resyncLtsbSpans = (ltsb: ng.beam.LTSBuckling, newPositions: number[]): void => {
  const oldData = ltsb.data;
  const oldSupportConditions = ltsb.supportConditions ?? [];
  const newData: ng.beam.BucklingData[] = [];
  const newSupportConditions: ng.beam.SupportCondition[] = [];

  for (let i = 0; i < newPositions.length - 1; i++) {
    const start = newPositions[i];
    const end = newPositions[i + 1];
    const oldIndex = oldData.findIndex(
      (d) => d.position !== null && Math.abs(d.position.start - start) < 1e-9 && Math.abs(d.position.end - end) < 1e-9,
    );
    const oldBeta = oldIndex >= 0 ? oldData[oldIndex]?.getBeta() : undefined;
    const beta = Number.isFinite(Number(oldBeta)) && Number(oldBeta) > 0 ? Number(oldBeta) : 1.0;
    const supportCondition =
      oldIndex >= 0
        ? (oldSupportConditions[oldIndex] ??
          oldData[oldIndex]?.getSupportCondition() ??
          ng.beam.SupportCondition.SUPPORT_CONDITION_SIMPLY)
        : ng.beam.SupportCondition.SUPPORT_CONDITION_SIMPLY;
    newData.push(
      ng.beam.BucklingData.create({
        position: ng.beam.BucklingSpan.create({ start, end }),
        beta,
        supportCondition,
      }),
    );
    newSupportConditions.push(supportCondition);
  }

  ltsb.data = newData;
  ltsb.supportConditions = newSupportConditions;
};

const resyncBucklingForBeam = (state: ProjectState, beam: ng.beam.Data): void => {
  const columnLength = getBeamLength(beam);
  const supsJson = state.project?.data?.data?.input?.structures?.[0]?.stages?.[0]?.sups ?? [];
  const sups = supsJson.map((s: ng.stage.Support_json) => ng.stage.Support.fromObject(s));

  const material = beam.getConcrete() ?? beam.getSteel() ?? beam.getTimber();
  if (material) {
    for (const axis of ["Y", "Z"] as const) {
      const fb = axis === "Y" ? material.fbStiff : material.fbWeak;
      if (!fb) {
        const newFb = ng.beam.FlexBuckling.create();
        const positions = deriveSpansForAxis(sups, columnLength, axis);
        resyncBucklingSpans(newFb, positions);
        if (axis === "Y") material.fbStiff = newFb;
        else material.fbWeak = newFb;
      } else {
        const positions = deriveSpansForAxis(sups, columnLength, axis);
        resyncBucklingSpans(fb, positions);
      }
    }
  }
};

const resyncLateralTorsionalForBeam = (state: ProjectState, beam: ng.beam.Data): void => {
  const columnLength = getBeamLength(beam);
  const supsJson = state.project?.data?.data?.input?.structures?.[0]?.stages?.[0]?.sups ?? [];
  const sups = supsJson.map((s: ng.stage.Support_json) => ng.stage.Support.fromObject(s));
  const positions = deriveSpansForAxis(sups, columnLength, "Z");

  const steel = beam.getSteel();
  if (steel) {
    if (!steel.ltbTop) {
      steel.ltbTop = ng.beam.LTBuckling.create();
    }
    if (!steel.ltbBtm) {
      steel.ltbBtm = ng.beam.LTBuckling.create();
    }
    resyncLtbSpans(steel.ltbTop, positions);
    resyncLtbSpans(steel.ltbBtm, positions);
  }

  const timber = beam.getTimber();
  if (timber) {
    if (!timber.ltsb) {
      timber.ltsb = ng.beam.LTSBuckling.create();
    }
    resyncLtsbSpans(timber.ltsb, positions);
  }
};

const roundMetersToMillimeter = (value: number): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }

  return Math.round(numeric * 1.0e3) / 1.0e3;
};

const createStandaloneBarEndpoints = (y: number, z: number, beamLength: number) => ({
  startPoint: ng.geometry.Point3D.create({
    x: roundMetersToMillimeter(0),
    y: roundMetersToMillimeter(y),
    z: roundMetersToMillimeter(z),
  }),
  endPoint: ng.geometry.Point3D.create({
    x: roundMetersToMillimeter(beamLength),
    y: roundMetersToMillimeter(y),
    z: roundMetersToMillimeter(z),
  }),
});

const getOrCreateRebars = (beam: ng.beam.Data): ng.rebar.Data | null => {
  const concrete = beam.getConcrete();
  if (!concrete) {
    return null;
  }

  const rebars = concrete.rebars ?? ng.rebar.Data.create();
  concrete.rebars = rebars;
  return rebars;
};

const normalizeStandaloneBars = (beam: ng.beam.Data) => {
  const standaloneBars = beam.getConcrete()?.rebars?.standaloneBars;
  if (!standaloneBars) {
    return;
  }

  const beamLength = getBeamLength(beam);
  for (const standaloneBar of standaloneBars) {
    const y = Number(standaloneBar.startPoint?.y ?? standaloneBar.endPoint?.y ?? 0);
    const z = Number(standaloneBar.startPoint?.z ?? standaloneBar.endPoint?.z ?? 0);
    const { startPoint, endPoint } = createStandaloneBarEndpoints(y, z, beamLength);
    standaloneBar.startPoint = startPoint;
    standaloneBar.endPoint = endPoint;
    standaloneBar.zone = getStandaloneBarZoneFromPosition(y, z);
  }
};

const createStandaloneBars = (state: ProjectState, sourceBeam?: ng.beam.Data): ng.reinf.StandAloneBar[] => {
  const beam = sourceBeam ?? getPrimaryBeamData(state);
  if (!beam) {
    return [];
  }

  const concrete = beam.getConcrete();
  const rebars = concrete?.rebars;
  const sectionGeometry = getSectionGeometry(getPrimarySection(state, beam));
  if (!concrete || !rebars || !sectionGeometry) {
    return [];
  }

  const groups = rebars.grps ?? [];
  const layers = rebars.lays ?? [];
  const beamLength = getBeamLength(beam);
  const maxStirrupDiameter = Math.max(
    0,
    ...((concrete.links?.linkGrps ?? []).map((group) => normalizeSectionLength(Number(group.diameter))) || [0]),
  );

  // Returns the inset from a face to the bar center: cover(side) + stirrup_dia + bar_dia/2.
  const edgeOffset = (side: ng.design.concrete.BeamSide, diaM: number): number => {
    const distance = concrete.distances.find((item) => item.side === side);
    return normalizeSectionLength(Number(distance?.cover ?? 0)) + maxStirrupDiameter + diaM / 2;
  };

  type BarCandidate = {
    axis: "Y" | "Z";
    zone: ng.design.concrete.BeamSide;
    y: number;
    z: number;
    diaM: number;
    isCorner: boolean;
    group: ng.reinf.Group;
  };

  const candidatesOverlap = (a: BarCandidate, b: BarCandidate): boolean => {
    const dist = Math.hypot(a.y - b.y, a.z - b.z);
    return dist + 1.0e-9 < a.diaM / 2 + b.diaM / 2;
  };

  // Higher-priority candidate sorts earlier: larger diameter > Y axis > corner placement.
  const candidatePriority = (c: BarCandidate): [number, number, number] => [
    -c.diaM,
    c.axis === "Y" ? 0 : 1,
    c.isCorner ? 0 : 1,
  ];

  // How many bars of diameter diaM fit in a span with clear distance = 0.
  const maxBarsForSpan = (span: number, diaM: number): number => (diaM > 0 ? Math.floor(span / diaM + 1.0e-9) + 1 : 1);

  const candidates: BarCandidate[] = [];

  if (sectionGeometry.circular) {
    // All layers contribute candidates on one shared ring at the largest computable radius.
    let maxRadius = 0;

    const validCircularLayers: Array<{
      layer: ng.reinf.Layer;
      group: ng.reinf.Group;
      count: number;
      axis: "Y" | "Z";
      radius: number;
    }> = [];

    for (const layer of layers) {
      const group = groups.find((g) => g.id?.guid === layer.grpGuid);
      if (!group) {
        continue;
      }
      const count = Math.max(0, Math.round(Number(layer.getNum?.() ?? 0)));
      if (count <= 0) {
        continue;
      }
      const diaM = normalizeSectionLength(Number(group.diameter));
      const offset = edgeOffset(ng.design.concrete.BeamSide.BEAM_SIDE_TOP, diaM);
      const r = Math.max(0, sectionGeometry.radius - offset);
      maxRadius = Math.max(maxRadius, r);
      const axis: "Y" | "Z" = isHorizontalRebarLayer(layer) ? "Y" : "Z";
      validCircularLayers.push({ layer, group, count, axis, radius: r });
    }

    for (const { group, count, axis } of validCircularLayers) {
      const diaM = normalizeSectionLength(Number(group.diameter));
      for (let i = 0; i < count; i++) {
        const angle = Math.PI / 2 - (2 * Math.PI * i) / count;
        const y = maxRadius * Math.cos(angle);
        const z = maxRadius * Math.sin(angle);
        candidates.push({
          axis,
          zone: getStandaloneBarZoneFromPosition(y, z),
          y,
          z,
          diaM,
          isCorner: false,
          group,
        });
      }
    }
  } else {
    // Rectangular / square sections.
    for (const layer of layers) {
      const group = groups.find((item) => item.id?.guid === layer.grpGuid);
      if (!group) {
        continue;
      }
      const N = Math.max(0, Math.round(Number(layer.getNum?.() ?? 0)));
      if (N <= 0) {
        continue;
      }
      const diaM = normalizeSectionLength(Number(group.diameter));
      const axis: "Y" | "Z" = isHorizontalRebarLayer(layer) ? "Y" : "Z";

      if (
        layer.zone === ng.design.concrete.BeamSide.BEAM_SIDE_TOP ||
        layer.zone === ng.design.concrete.BeamSide.BEAM_SIDE_BOTTOM
      ) {
        const isTop = layer.zone === ng.design.concrete.BeamSide.BEAM_SIDE_TOP;
        const fixedZ = isTop
          ? sectionGeometry.maxZ - edgeOffset(ng.design.concrete.BeamSide.BEAM_SIDE_TOP, diaM)
          : sectionGeometry.minZ + edgeOffset(ng.design.concrete.BeamSide.BEAM_SIDE_BOTTOM, diaM);
        const yStart = sectionGeometry.minY + edgeOffset(ng.design.concrete.BeamSide.BEAM_SIDE_LEFT, diaM);
        const yEnd = sectionGeometry.maxY - edgeOffset(ng.design.concrete.BeamSide.BEAM_SIDE_RIGHT, diaM);
        const span = yEnd - yStart;
        const target = Math.min(N, maxBarsForSpan(span, diaM));
        const ys = createEvenlySpacedPositions(target, yStart, yEnd);
        for (let i = 0; i < ys.length; i++) {
          candidates.push({
            axis,
            zone: layer.zone,
            y: ys[i],
            z: fixedZ,
            diaM,
            isCorner: i === 0 || i === ys.length - 1,
            group,
          });
        }
      } else if (
        layer.zone === ng.design.concrete.BeamSide.BEAM_SIDE_LEFT ||
        layer.zone === ng.design.concrete.BeamSide.BEAM_SIDE_RIGHT
      ) {
        const isLeft = layer.zone === ng.design.concrete.BeamSide.BEAM_SIDE_LEFT;
        const fixedY = isLeft
          ? sectionGeometry.minY + edgeOffset(ng.design.concrete.BeamSide.BEAM_SIDE_LEFT, diaM)
          : sectionGeometry.maxY - edgeOffset(ng.design.concrete.BeamSide.BEAM_SIDE_RIGHT, diaM);
        const zStart = sectionGeometry.minZ + edgeOffset(ng.design.concrete.BeamSide.BEAM_SIDE_BOTTOM, diaM);
        const zEnd = sectionGeometry.maxZ - edgeOffset(ng.design.concrete.BeamSide.BEAM_SIDE_TOP, diaM);
        const span = zEnd - zStart;
        const target = Math.min(N, maxBarsForSpan(span, diaM));
        const zs = createEvenlySpacedPositions(target, zStart, zEnd);
        for (let i = 0; i < zs.length; i++) {
          candidates.push({
            axis,
            zone: layer.zone,
            y: fixedY,
            z: zs[i],
            diaM,
            isCorner: i === 0 || i === zs.length - 1,
            group,
          });
        }
      }
    }
  }

  // Sort by priority then accept candidates in order, rejecting any that collide with an
  // already-accepted bar.  Larger diameter wins; Y axis beats Z on equal diameter; corner
  // beats interior on equal axis and diameter.
  candidates.sort((a, b) => {
    const [a0, a1, a2] = candidatePriority(a);
    const [b0, b1, b2] = candidatePriority(b);
    return a0 !== b0 ? a0 - b0 : a1 !== b1 ? a1 - b1 : a2 - b2;
  });

  const accepted: BarCandidate[] = [];
  for (const candidate of candidates) {
    if (!accepted.some((acc) => candidatesOverlap(acc, candidate))) {
      accepted.push(candidate);
    }
  }

  return accepted.map((c) => {
    const endpoints = createStandaloneBarEndpoints(c.y, c.z, beamLength);
    return ng.reinf.StandAloneBar.create({
      id: ng.utils.ID.create({ guid: CreateGUID() }),
      startPoint: endpoints.startPoint,
      endPoint: endpoints.endPoint,
      mtrlGuid: c.group.mtrlGuid,
      diameter: c.group.diameter,
      zone: c.zone,
      preStress: c.group.preStress,
    });
  });
};

const syncStandaloneBarsWithCurrentRebars = (state: ProjectState, beam: ng.beam.Data, forceRegenerate = false) => {
  const rebars = getOrCreateRebars(beam);
  if (!rebars) {
    return;
  }

  if (forceRegenerate || !rebars.exactBarCoordinates) {
    rebars.standaloneBars = createStandaloneBars(state, beam);
    return;
  }

  normalizeStandaloneBars(beam);
};

const enforceMinimumLongitudinalBarsPerDirection = (state: ProjectState, beam: ng.beam.Data, minBars = 2) => {
  const rebars = getOrCreateRebars(beam);
  const layers = rebars?.lays;
  if (!rebars || !layers) {
    return;
  }

  const sectionGeometry = getSectionGeometry(getPrimarySection(state, beam));
  if (sectionGeometry?.circular) {
    return;
  }

  for (const axis of ["Y", "Z"] as const) {
    const axisLayers = getLayersByAxis(layers, axis);
    if (axisLayers.length === 0) {
      continue;
    }

    const currentCount = Math.max(
      0,
      ...axisLayers.map((layer) => Math.max(0, Math.round(Number(layer.getNum?.() ?? 0)))),
    );
    if (currentCount >= minBars) {
      continue;
    }

    for (const layer of axisLayers) {
      layer.setNum(minBars);
      layer.distr = ng.reinf.Distribution.DISTRIBUTION_EVEN;
    }
  }
};

const updateLayerDValuesFromCover = (state: ProjectState, beam: ng.beam.Data) => {
  const concrete = beam.getConcrete();
  const rebars = concrete?.rebars;
  const layers = rebars?.lays;
  if (!concrete || !rebars || !layers) {
    return;
  }

  const groups = rebars.grps ?? [];
  const maxStirrupDiameter = Math.max(
    0,
    ...((concrete.links?.linkGrps ?? []).map((group) => toMillimeters(Number(group.diameter))) || [0]),
  );

  // The section is deliberately not read here: `d` is measured from the layer's own face, so it does
  // not depend on the section's height or width. See the note at the assignment below.
  for (const layer of layers) {
    const coverForZone = toMillimeters(
      Number(concrete.distances.find((distance) => distance.side === layer.zone)?.cover ?? 0),
    );
    const group = groups.find((item) => item.id?.guid === layer.grpGuid) ?? null;
    const barDiameter = toMillimeters(Number(group?.diameter ?? 0));
    const centerFromFace = Math.max(0, coverForZone + maxStirrupDiameter + barDiameter / 2);

    // `d` is the distance from the layer's OWN face to the bar centre, for every zone. It is not a
    // section-frame coordinate, and must not be flipped here.
    //
    // The engine does the flip itself when it reads the layer back: AutoBarGeneration
    // (project2concobj.cpp) computes `z = d` and then, for BEAM_SIDE_TOP only, `z = H - z`. Writing
    // `sectionHeight - centerFromFace` for TOP therefore composed with that flip to put the top
    // layer's bars at `z = centerFromFace` — down at the BOTTOM face. The engine's own column writer
    // (BuildColumnReinforcementForOutput) emits the same `d` for all four zones, confirming the
    // convention: a 600 mm section gets d = 60 on top and bottom alike.
    //
    // This was masked rather than harmless: project2concobj.cpp prefers `standalone_bars` when they
    // are present and only falls back to these layers when they are not, and sx-column regenerates
    // standalone bars on load and on every cover change. The wrong `d` rode along unused.
    layer.d = Math.round(centerFromFace);
  }
};

const syncStandaloneBarsOnProjectLoad = (state: ProjectState) => {
  const beam = getPrimaryBeamData(state);
  if (!beam) {
    return;
  }

  const rebars = getOrCreateRebars(beam);
  if (rebars && !rebars.exactBarCoordinates) {
    enforceMinimumLongitudinalBarsPerDirection(state, beam, 2);
  }

  syncStandaloneBarsWithCurrentRebars(state, beam);
  setPrimaryBeamData(state, beam);
};

const getReferencedReinforcementMaterialGuids = (beam: ng.beam.Data): Set<string> => {
  const concrete = beam.getConcrete();
  const referenced = new Set<string>();

  for (const group of concrete?.rebars?.grps ?? []) {
    if (typeof group.mtrlGuid === "string" && group.mtrlGuid.length > 0) {
      referenced.add(group.mtrlGuid);
    }
  }

  for (const linkGroup of concrete?.links?.linkGrps ?? []) {
    if (typeof linkGroup.mtrlGuid === "string" && linkGroup.mtrlGuid.length > 0) {
      referenced.add(linkGroup.mtrlGuid);
    }
  }

  for (const standaloneBar of concrete?.rebars?.standaloneBars ?? []) {
    if (typeof standaloneBar.mtrlGuid === "string" && standaloneBar.mtrlGuid.length > 0) {
      referenced.add(standaloneBar.mtrlGuid);
    }
  }

  return referenced;
};

// Every material guid the beam currently depends on: its own (non-reinforcement) material
// plus all reinforcement materials. Used to decide which entries in mtrlDb are still live.
const getReferencedMaterialGuids = (beam: ng.beam.Data): Set<string> => {
  const referenced = getReferencedReinforcementMaterialGuids(beam);
  if (typeof beam.mtrlGuid === "string" && beam.mtrlGuid.length > 0) {
    referenced.add(beam.mtrlGuid);
  }
  return referenced;
};

const cleanupUnusedReinforcementMaterialsInDb = (state: ProjectState, referencedGuids: Set<string>) => {
  const mtrlDb = state.project?.data?.data?.input?.mtrlDb;
  if (!mtrlDb) {
    return;
  }

  const reinforcementType = ng.material.Mtrl_ToObject(ng.material.Mtrl.MTRL_REINFORCEMENT);
  state.project!.data.data!.input!.mtrlDb = mtrlDb.filter((material) => {
    const materialType = material.type;
    const isReinforcement =
      materialType === "MTRL_REINFORCEMENT" ||
      (typeof reinforcementType === "string" && materialType === reinforcementType);

    if (!isReinforcement) {
      return true;
    }

    const guid = material.id?.guid;
    return typeof guid === "string" && referencedGuids.has(guid);
  });
};

const getGroupByAxis = (groups: ng.reinf.Group[], axis: "Y" | "Z") => {
  return groups[axis === "Y" ? 0 : 1] ?? null;
};

const getAxisZones = (axis: "Y" | "Z") =>
  axis === "Y"
    ? [ng.design.concrete.BeamSide.BEAM_SIDE_TOP, ng.design.concrete.BeamSide.BEAM_SIDE_BOTTOM]
    : [ng.design.concrete.BeamSide.BEAM_SIDE_LEFT, ng.design.concrete.BeamSide.BEAM_SIDE_RIGHT];

const getLayersByAxis = (layers: ng.reinf.Layer[], axis: "Y" | "Z") => {
  const zones = getAxisZones(axis);
  return layers.filter((layer) => zones.includes(layer.zone));
};

const syncBeamDependentLengths = (
  beam: ng.beam.Data,
  groups: ng.reinf.Group[],
  linkGrps: ng.link.Group[],
  length: number,
) => {
  if (!Number.isFinite(length)) {
    return;
  }

  for (const group of groups) {
    group.length = length;
  }

  for (const linkGrp of linkGrps) {
    linkGrp.length = length;
  }

  if (beam.segments?.[0]) {
    beam.segments[0].length = length;
  }

  normalizeStandaloneBars(beam);
};

const setStructureNodes = (state: ProjectState, nodes: ng.topology.ElementNode[]) => {
  if (!state.project?.data?.data?.input?.structures?.[0]) {
    return;
  }
  if (
    nodes &&
    state.project.data.data.input.structures &&
    state.project.data.data.input.structures.length > 0 &&
    state.project.data.data.input.structures[0].nodes
  ) {
    state.project.data.data.input.structures[0].nodes =
      nodes
        .map((node) => (node ? ng.topology.ElementNode.toObject(node) : null))
        .filter((node): node is ng.topology.ElementNode_json => node !== null) ?? [];
  }
};

export const delProject = createAsyncThunk("projects/delete", async (args: { id: number; type: AppType }, thunkAPI) => {
  let state = thunkAPI.getState() as { UserSlice: { token: string | null } };
  const tempheaders = {
    authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
    "x-client-id": getClientId(),
  };

  const response = await fetch(`api/projects/${args.id}/${args.type}`, {
    method: "DELETE",
    headers: tempheaders,
  });

  if (!response.ok) {
    throw new Error("Failed to delete project");
  }
});

export const fetchProject = createAsyncThunk("projects/fetchProject", async (args: { guid: string }, thunkAPI) => {
  let state = thunkAPI.getState() as { UserSlice: { token: string | null } };
  const tempheaders = {
    authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
    "Content-Type": "application/json",
    "x-client-id": getClientId(),
  };

  let response;
  response = await fetch(`/api/projects/${args.guid}/`, {
    method: "GET",
    headers: tempheaders,
  });

  if (!response || !response.ok) {
    thunkAPI.dispatch(
      addAlert({
        type: "danger",
        text: t("err_file_not_found_error_.label"),
        id: uuidv4(),
        status: response.status + "",
      }),
    );
    throw new Error("Failed to fetch project");
  }

  let outjson = await response.json();
  let outproject = null;
  if (outjson.length > 0) {
    outproject = outjson[0];
  }
  return outproject;
});

export const updateProject = createAsyncThunk(
  "projects/updateProject",
  async (args: { guid: string; project: unknown }, thunkAPI) => {
    let state = thunkAPI.getState() as { UserSlice: { token: string | null } };
    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "Content-Type": "application/json",
      "x-client-id": getClientId(),
    };

    let response;
    response = await fetch(`/api/projects/${args.guid}/`, {
      method: "PUT",
      headers: tempheaders,
      body: JSON.stringify(args.project),
    });

    if (!response || !response.ok) {
      throw new Error("Failed to fetch project");
    }

    let outjson = await response.json();
    let outproject = null;
    if (outjson.length > 0) {
      outproject = outjson[0];
    }
  },
);

export const saveTempFile = createAsyncThunk(
  "projects/saveTemp",
  async (args: { name: string; project: { guid: string; name: string } }, thunkAPI) => {
    let state = thunkAPI.getState() as { UserSlice: { token: string | null } };
    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "Content-Type": "application/json",
      "x-client-id": getClientId(),
    };
    let response;
    response = await fetch(`/api/projects/saveas/`, {
      method: "POST",
      headers: tempheaders,
      body: JSON.stringify({ name: args.name, project: args.project }),
    });

    if (!response || !response.ok) {
      throw new Error("Failed to fetch project");
    }

    let outjson = await response.json();

    return outjson;
  },
);

// Defined alongside the progress wire format, since the server keys its progress lines by it.
export type { CalcOperation };

const calcEndpointMap: Record<CalcOperation, string> = {
  analyse: "/api/projects/analyse/",
  codecheck: "/api/projects/codecheck/",
  design: "/api/projects/design/",
};

const isProjectLike = (value: unknown): value is ng.frontend.column.Project_json => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as {
    meta?: unknown;
    settings?: unknown;
    data?: { input?: unknown };
  };

  return candidate.meta != null || candidate.settings != null || candidate.data?.input != null;
};

const resolveProjectForCalc = (file: IFile): ng.frontend.column.Project_json | null => {
  const root: unknown = file?.data;
  if (!root) {
    return null;
  }

  if (isProjectLike(root)) {
    return root;
  }

  const nestedData = (root as { data?: unknown }).data;
  if (isProjectLike(nestedData)) {
    return nestedData;
  }

  return null;
};

// Mirrors the server's CalcError: `label` (when set) is an i18next key; otherwise show `text`.
type CalcError = { label?: string; text: string };

// Resolve a single analyser error to a user-facing string via i18next (the `t`
// function), the same system the rest of the UI uses.
// - Labelled entries are calc-domain errors meant for the user. The string lives
//   under `<label>.label` in the translation files (e.g. err_section_not_concrete_.label);
//   if it is missing we fall back to the raw label key, never the internal `text`
//   (flagged "not intended for end user" in the log contract).
// - Label-less entries are transport/infrastructure errors synthesised server-side
//   (timeout, connection failure, …); these carry only `text` and are shown as-is.
const translateCalcError = (err: CalcError): string => {
  // The translated sentence says WHAT went wrong; the log values say WHERE. The engine sends the
  // position as a value with an empty key, so this appends e.g. " (x = 6.870 m)" - the same thing
  // sx-beam's inspectLog renders. Without it a yielding error names no location at all.
  const where = formatCalcErrorValues(err);
  const suffix = where === "" ? "" : ` ${where}`;
  if (err.label) {
    return t(`${err.label}.label`, err.label) + suffix;
  }
  return (err.text ?? "") + suffix;
};

// Collapse a CalcError[] from the server into a single localised, newline-joined message ("" when
// there is nothing to show). Fed by the `errors` array on a failed call's body — which is where
// calc-domain errors arrive too, since a calc that logs an error fails the run rather than
// answering with a result (see doCodeCalculation on the server).
const localiseCalcErrors = (errors: unknown): string =>
  (Array.isArray(errors) ? (errors as CalcError[]) : [])
    .map((e) => translateCalcError(e))
    .filter((m) => m.trim() !== "")
    .join("\n");

/** Minimum gap between progress dispatches, so a chatty stream cannot thrash the overlay. */
const PROGRESS_DISPATCH_MS = 250;

type CalcThunkApi = {
  getState: () => unknown;
  dispatch: (
    action:
      | ReturnType<typeof calcSavedRevision>
      | ReturnType<typeof calcRunStarted>
      | ReturnType<typeof setCalcProgress>
      | ReturnType<typeof calcRunFinished>
      | ReturnType<typeof setFlaggedExactBars>,
  ) => unknown;
  /** Aborted by abortCurrentCalc() when the user cancels — RTK wires this to the thunk promise. */
  signal: AbortSignal;
};

// A calculation that has to (re)analyse saves the project with a bumped revision *before* it
// calculates (ensureFreshAnalysis on the server), so a run that then fails has still moved the
// stored revision on. The failure answer carries that project; dropping it left the client one
// revision behind, and the next run was refused as "newer saved version" (#423). Only the revision
// is taken: nothing else in a failed run's project is meant to be applied. The server tolerates a
// client left behind its own save anyway (#424); this covers the runs that do answer.
//
// `projects/`, not `ProjectSlice/`, so syncMiddleware neither prunes output nor schedules a save -
// the server already holds this revision.
const calcSavedRevision = createAction<number>("projects/calcSavedRevision");

const savedRevisionOf = (blob: unknown): number | null => {
  const revision = (blob as { data?: { meta?: { revision?: unknown } } } | null | undefined)?.data?.meta?.revision;
  return typeof revision === "number" && Number.isFinite(revision) ? revision : null;
};

const runCalcThunk = async (file: IFile, operation: CalcOperation, thunkAPI: CalcThunkApi): Promise<any> => {
  let state = thunkAPI.getState() as {
    UserSlice: { token: string | null };
    uiSlice: { featureFlags: Record<string, boolean> };
  };
  const tempheaders: Record<string, string> = {
    authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
    "Content-Type": "application/json",
    "x-client-id": getClientId(),
  };

  // Runtime feature flags travel with the request rather than living in the server's environment,
  // because they are switched in the admin view of a running browser. Only the enabled ones are
  // named, so an empty value (or no header at all, on any older client) means "everything off" —
  // which is what the server assumes anyway.
  const enabledFlags = featureFlagHeaderValue(state.uiSlice?.featureFlags ?? {});
  if (enabledFlags !== "") {
    tempheaders[FEATURE_FLAG_HEADER] = enabledFlags;
  }
  const projectForCalc = resolveProjectForCalc(file);
  if (!projectForCalc) {
    throw new Error(`Failed to run ${operation}: missing project payload`);
  }

  // Bars entered by hand are checked here, when the user asks for results, rather than while they
  // are still placing them — see exactBarChecks. Design is not gated: it lays out bars of its own.
  if (operation === "analyse" || operation === "codecheck") {
    const input = projectForCalc.data?.input;
    const reinforcementProblem = exactBarCheckMessage(input);
    // Marks the failing bars where they are drawn, or clears the marks of a layout that now passes.
    thunkAPI.dispatch(setFlaggedExactBars([...failingExactBars(input).keys()]));
    if (reinforcementProblem) {
      throw new Error(reinforcementProblem);
    }
  }

  let lcom = projectForCalc?.data?.input?.structures[0]?.loading?.combinations?.map((comb) => comb.id?.guid);
  const guid = file?.guid ?? projectForCalc?.meta?.guid;

  thunkAPI.dispatch(calcRunStarted(operation));
  try {
    let response = await fetch(calcEndpointMap[operation], {
      method: "POST",
      headers: tempheaders,
      body: JSON.stringify({ blob: { data: projectForCalc, lcombs: lcom, guid } }),
      signal: thunkAPI.signal,
    });

    if (!response || !response.ok) {
      let message = `Failed to run ${operation}`;
      try {
        const body = await response.json();
        const revision = savedRevisionOf(body?.blob);
        if (revision != null) thunkAPI.dispatch(calcSavedRevision(revision));
        const localised = localiseCalcErrors(body?.errors);
        if (localised !== "") {
          message = localised;
        } else if (typeof body?.error === "string" && body.error.trim() !== "") {
          // Guard failures before the calc stage is reached (bad payload, missing guid, gateway
          // problems) answer with a single { error: string } instead of the localisable `errors`
          // array — surface that message as-is.
          message = body.error;
        }
      } catch {
        // Response body was not JSON (or already consumed) — keep the generic message.
      }
      throw new Error(message);
    }

    // Slow calls stream NDJSON progress lines before the result; fast ones answer with a single
    // plain JSON body exactly as they always have. readCalcStream accepts both and returns the
    // result body either way — see server/calculation/progress.ts for the format.
    let lastDispatchedAt = 0;
    let lastStep: string | null = null;
    const outcome = await readCalcStream(response, (line) => {
      const now = Date.now();
      // Reaching a new step is the informative part, so it always gets through; a line that only
      // moves a counter along is throttled. Dropping a step would leave a hole in the list the
      // overlay builds from these, which is why `detail` counts as part of the identity here.
      const step = calcStepKey(line);
      if (step !== lastStep || now - lastDispatchedAt >= PROGRESS_DISPATCH_MS) {
        lastStep = step;
        lastDispatchedAt = now;
        thunkAPI.dispatch(setCalcProgress(line));
      }
    });

    if (outcome.kind === "error") {
      // The run failed after progress had already been streamed, so the status code was locked at
      // 200 and the failure arrived as an error line instead. Same localisation as the !ok path.
      const revision = savedRevisionOf(outcome.blob);
      if (revision != null) thunkAPI.dispatch(calcSavedRevision(revision));
      const localised = localiseCalcErrors(outcome.errors);
      throw new Error(localised !== "" ? localised : `Failed to run ${operation}`);
    }
    return outcome.body;
  } finally {
    thunkAPI.dispatch(calcRunFinished());
  }
};

export const analyseProject = createAsyncThunk("projects/analyse", async (args: { file: IFile }, thunkAPI) => {
  const out = await runCalcThunk(args.file, "analyse", thunkAPI);
  showCalcWarnings(out?.blob?.data, thunkAPI.dispatch);
  return out.blob.data;
});

// A successful code check answers with the checked project and nothing else — a calc that logged
// an error never gets here, it fails the run instead (see doCodeCalculation on the server). A calc
// that logged a *warning* does get here: the results are valid and the warning rides along in the
// project's log, so it is read out and raised here (see showCalcWarnings).
export const codeCheckProject = createAsyncThunk<ng.frontend.column.Project_json, { file: IFile }>(
  "projects/codecheck",
  async (args, thunkAPI) => {
    const out = await runCalcThunk(args.file, "codecheck", thunkAPI);
    showCalcWarnings(out?.blob?.data, thunkAPI.dispatch);
    return out.blob.data as ng.frontend.column.Project_json;
  },
);

// Payload of a successful design run. The recommended project is what the design engine
// applied to CodeCheckOutput.project; the suggestions let the user review and choose. Neither
// is applied to the current project here — that only happens after the user approves in the
// review modal (see designReviewSlice + applyRecommendedDesign / applyDesignSuggestion).
export interface DesignReviewPayload {
  recommendedProject: ng.frontend.column.Project_json;
  suggestions: ng.endpoint_args.common_api_functions.AutoDesignSuggestion_json[];
}

export const designProject = createAsyncThunk<DesignReviewPayload, { file: IFile }>(
  "projects/design",
  async (args, thunkAPI) => {
    const out = await runCalcThunk(args.file, "design", thunkAPI);
    // [design-suggestions debug] What the client received from /api/projects/design.
    console.log(
      "[design-suggestions debug] designProject thunk — response keys:",
      out && typeof out === "object" ? Object.keys(out) : typeof out,
      "| out.suggestions length:",
      Array.isArray(out?.suggestions) ? out.suggestions.length : `(not an array: ${typeof out?.suggestions})`,
    );
    // Warnings from the design run describe the recommended project the user is about to review,
    // so they belong on screen next to the review panel.
    showCalcWarnings(out?.blob?.data, thunkAPI.dispatch);
    return {
      recommendedProject: out.blob.data as ng.frontend.column.Project_json,
      suggestions: (out.suggestions ?? []) as ng.endpoint_args.common_api_functions.AutoDesignSuggestion_json[],
    };
  },
);

/**
 * Cancels the calculation currently in flight. Kept in a module-level ref rather than in Redux
 * because it is a live function, not serialisable state — and only one calculation can run at a
 * time, since the buttons that start them are disabled while one is.
 *
 * Aborting only stops the client waiting for the answer. The calculation continues in the engine;
 * there is no cancellation path through the gateway to stop it.
 */
let currentCalcAbort: (() => void) | null = null;

export const abortCurrentCalc = () => {
  currentCalcAbort?.();
};

/**
 * Did this rejection come from the user pressing Cancel rather than from a real failure?
 * A cancelled run must go back to "idle" without setting calcError — otherwise the user gets an
 * error toast for something they asked for themselves.
 */
const wasCancelled = (action: { meta?: { aborted?: boolean }; error?: { name?: string } }) =>
  action.meta?.aborted === true || action.error?.name === "AbortError";

/**
 * Dispatch a calculation thunk with its abort handle registered for the Cancel button.
 * `unwrap` mirrors what the caller did before: rethrow the rejection instead of swallowing it.
 */
const dispatchCancellableCalc = async (dispatch: any, thunk: any, unwrap = false) => {
  const promise = dispatch(thunk);
  currentCalcAbort = () => promise.abort();
  try {
    return unwrap ? await promise.unwrap() : await promise;
  } finally {
    currentCalcAbort = null;
  }
};

/**
 * Wall-clock time since `start`, formatted for the calculation console messages, e.g. "18.952s".
 */
const elapsedSince = (start: number) => `${((performance.now() - start) / 1000).toFixed(3)}s`;

export const fetchAutoBuckling = createAsyncThunk(
  "projects/fetchAutoBuckling",
  async (args: { file: IFile }, thunkAPI) => {
    const state = thunkAPI.getState() as { UserSlice: { token: string | null } };
    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "Content-Type": "application/json",
      "x-client-id": getClientId(),
    };
    const projectForCalc = resolveProjectForCalc(args.file);
    if (!projectForCalc) {
      throw new Error("Failed to fetch auto buckling: missing project payload");
    }

    const response = await fetch("/api/projects/bucklingjson", {
      method: "POST",
      headers: tempheaders,
      body: JSON.stringify({ blob: { data: projectForCalc } }),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error("bucklingjson failed:", response.status, errorBody);
      throw new Error(`Failed to fetch auto buckling: ${response.status}`);
    }

    return await response.json();
  },
);

export type SupportConnectivityType = "rigid" | "pinned" | "roller" | "free" | "rigid_roller";

export { getStiffBuckling, getWeakBuckling, getBucklingByAxis, getPrimaryBeamData };

export function useProject() {
  const dispatch = useAppDispatch();
  const token = useAppSelector(selectToken);
  const user = useAppSelector(selectUser);
  const project = useAppSelector(selectProject);
  const generateLoadCombsStatus = useAppSelector(selectGenerateLoadCombsStatus);
  const secondOrder = useAppSelector(selectSecondOrder);
  const { getMaterial, getSection } = useMaterials();
  const deleteProject = async (id: number, type: AppType) => {
    try {
      if (!token) {
        console.error("No token found. Please log in.");
        return;
      }
      console.log(token);
      await dispatch(delProject({ id: id, type: type }));
      console.log(`Project with ID ${id} deleted successfully.`);
    } catch (error) {
      console.error("Failed to delete project:", error);
    }
  };

  // The project as it stands in the store right now, rather than the copy a component captured on
  // its last render. Anything that mutates the project and then wants to calculate on the result
  // needs this: dispatches are synchronous, so the store is already up to date while the render
  // that would deliver the new copy has not happened yet.
  const getLatestProject = () => {
    return dispatch((_, getState) => {
      const state = getState() as { ProjectSlice: ProjectState };
      return state.ProjectSlice.project;
    }) as unknown as IFile | null;
  };

  const analyse = async (file: IFile) => {
    const start = performance.now();
    try {
      await dispatchCancellableCalc(dispatch, analyseProject({ file }));
      console.log(`Project ${file.name} analysed successfully (${elapsedSince(start)}).`);
    } catch (error) {
      console.error("Failed to analyse project:", error);
    }
  };

  const codeCheck = async (file: IFile) => {
    const start = performance.now();
    try {
      await dispatchCancellableCalc(dispatch, codeCheckProject({ file }), true);
      console.log(`Project ${file.name} code check completed (${elapsedSince(start)}).`);
    } catch (error) {
      // The user-facing message is already localised and stored on ProjectSlice.calcError by the
      // rejected reducer (App renders it as a toast), so there is nothing to surface here.
      console.error("Failed to run code check:", error);
    }
  };

  /**
   * Run the analysis and then the code check over the project as it currently stands, exactly as
   * pressing the two toolbar buttons in turn would. Used after a design result is applied (see
   * SXDesignReviewPanel): the design engine compares its candidates against the section forces of
   * the analysis that was already in the project, so a result it settled on stays provisional until
   * both have run over the new section.
   *
   * Each step reads the project back out of the store instead of taking a file argument: the calc
   * thunks post the payload they are handed, and the code check needs the output the analysis just
   * wrote — while a caller's copy predates the design being applied, let alone analysed.
   *
   * A failed (or cancelled) analysis stops the sequence rather than checking a project whose output
   * is stale; unwrap makes the rejection throw so it does. Nothing is surfaced from here for the
   * same reason as in `codeCheck` — the localised message is already on ProjectSlice.calcError,
   * which App renders as a toast.
   */
  const analyseThenCodeCheck = async () => {
    const start = performance.now();
    try {
      const toAnalyse = getLatestProject() ?? project;
      if (!toAnalyse) return;
      await dispatchCancellableCalc(dispatch, analyseProject({ file: toAnalyse }), true);
      const analysed = getLatestProject() ?? toAnalyse;
      await dispatchCancellableCalc(dispatch, codeCheckProject({ file: analysed }), true);
      console.log(`Project ${analysed.name} analysed and code checked (${elapsedSince(start)}).`);
    } catch (error) {
      console.error("Failed to re-run analysis and code check:", error);
    }
  };

  const designAsync = async (file: IFile) => {
    // Do NOT regenerate load combinations (or otherwise mutate the project) here: the design
    // result is only staged for review, not applied, so running generateLoadCombs() would prune
    // output and auto-save the still-undesigned project before the user has approved anything.
    // Load combinations depend on load cases, which a design run does not change, so there is
    // nothing to regenerate at this point anyway.
    const start = performance.now();
    await dispatchCancellableCalc(dispatch, designProject({ file }));
    console.log(`Project ${file.name} design completed (${elapsedSince(start)}).`);
  };

  const getProject = async (guid: string) => {
    await dispatch(fetchProject({ guid: guid }));
    // await generateLoadCombs(); // todo(Gustav): should this be done?
  };

  const saveTempProject = async (name: string, project: { name: string; guid: string }): Promise<string> => {
    let guid = await dispatch(saveTempFile({ name: name, project: project }));
    return guid.payload.guid;
  };

  const setProject = (project: IFile) => {
    dispatch(ProjectSlice.actions.setProject(project));
  };
  const setUpdateSettings = async (settings: {
    cc: ng.structure.ConsequenceClass;
    generation: ng.eurocode.Generation;
    nationalAnnex: ng.eurocode.Annex;
    reliability: ng.structure.ReliabilityClass;
  }) => {
    dispatch(ProjectSlice.actions.updateSettings(settings));
    // National annex (and adjacent code config) affects kdef/kcr and partial factors.
    await dispatch(updateGammaApi());
    await generateLoadCombs();
  };

  /** LOADCASE, LOAD, LOAD COMBINATION related actions */

  const addLoadcaseRedux = async (loadcase: ng.loadcase.Data_json) => {
    dispatch(ProjectSlice.actions.addLoadcase({ loadcase }));
    await generateLoadCombs();
  };
  const deleteLoadcaseRedux = async (guid: string) => {
    dispatch(ProjectSlice.actions.deleteLoadcase({ guid }));
    await generateLoadCombs();
  };
  const setLoadCaseName = (guid: string, name: string) => {
    dispatch(ProjectSlice.actions.updateLoadCaseName({ guid, name }));
  };

  const setLoadcaseType = (guid: string, type: ng.loadcase.Type) => {
    dispatch(ProjectSlice.actions.updateLoadcaseType({ guid, type }));
  };
  const setLoadcaseCategory = async (guid: string, category: ng.loadcase.Category) => {
    dispatch(ProjectSlice.actions.updateLoadcaseCategory({ guid, category }));
    await generateLoadCombs();
  };
  const setLoadcaseDuration = async (guid: string, durationClass: ng.loadcase.DurationClass) => {
    dispatch(ProjectSlice.actions.updateLoadcaseDuration({ guid, durationClass }));
    await generateLoadCombs();
  };
  const setLoadCaseVisuals = (visuals: ng.frontend.column.LoadCaseVisuals_json[]) => {
    dispatch(ProjectSlice.actions.updateLoadCaseVisuals(visuals));
  };

  const addLoad = (load: ng.load.IData) => {
    dispatch(ProjectSlice.actions.addLoad({ load }));
  };
  const updateLoad = (guid: string, load: Partial<ng.load.IData>) => {
    dispatch(ProjectSlice.actions.updateLoad({ guid, load }));
  };
  const updateLoadPosition = (guid: string, pointIndex: number, position: Partial<ng.geometry.IPoint3D>) => {
    dispatch(ProjectSlice.actions.updateLoadPosition({ guid, pointIndex, position }));
  };
  const updateLoadValue = (guid: string, valueIndex: number, value: number) => {
    dispatch(ProjectSlice.actions.updateLoadValue({ guid, valueIndex, value }));
  };
  const deleteLoad = (guid: string) => {
    dispatch(ProjectSlice.actions.deleteLoad({ guid }));
  };

  const addSelfWeights = async () => {
    dispatch(ProjectSlice.actions.addSelfWeights({}));
    await generateLoadCombs();
  };

  const delSelfWeights = async () => {
    dispatch(ProjectSlice.actions.delSelfWeights({}));
    await generateLoadCombs();
  };

  // ---- LOAD COMBINATIONS ----
  const addLoadCombination = (loadcombination: ng.loadcombination.IData) => {
    dispatch(ProjectSlice.actions.addLoadCombination({ loadcombination }));
  };
  const deleteLoadCombination = (guid: string) => {
    dispatch(ProjectSlice.actions.deleteLoadCombination({ guid }));
  };
  const updateLoadCombinationName = (guid: string, name: string) => {
    dispatch(ProjectSlice.actions.updateLoadCombinationName({ guid, name }));
  };
  const updateLoadCombinationType = async (guid: string, type: ng.loadcombination.Type) => {
    dispatch(ProjectSlice.actions.updateLoadCombinationType({ guid, type }));
    await generateLoadCombs();
  };
  const addLoadCombinationPart = async (guid: string, part: ng.loadcombination.ICombinationPart) => {
    dispatch(ProjectSlice.actions.addLoadCombinationPart({ guid, part }));
    await generateLoadCombs();
  };
  const updateLoadCombinationPart = (
    lcoGuid: string,
    currentLcaGuid: string,
    nextLcaGuid: string,
    coefficient: number,
  ) => {
    dispatch(ProjectSlice.actions.updateLoadCombinationPart({ lcoGuid, currentLcaGuid, nextLcaGuid, coefficient }));
  };
  const deleteLoadCombinationPart = async (guid: string, partGuid: string) => {
    dispatch(ProjectSlice.actions.deleteLoadCombinationPart({ guid, partGuid }));
    await generateLoadCombs();
  };
  const updateLoadCombinationCoa = async (guid: string, coa: ng.loadcombination.CoaType) => {
    dispatch(ProjectSlice.actions.updateLoadCombinationCoa({ guid, coa }));
    await generateLoadCombs();
  };
  const updateDominatingLCA = async (lcaGuid: string, lcoGuid: string, checked: boolean) => {
    dispatch(ProjectSlice.actions.updateDominatingLCA({ lcaGuid, lcoGuid, checked }));
    await generateLoadCombs();
  };
  const updateFavorableLCA = async (lcaGuid: string, lcoGuid: string, checked: boolean) => {
    dispatch(ProjectSlice.actions.updateFavorableLCA({ lcaGuid, lcoGuid, checked }));
    await generateLoadCombs();
  };

  const considerInAnalysis = (checked: boolean, tab: string) => {
    dispatch(ProjectSlice.actions.considerInAnalysis({ checked, tab }));
  };

  const getProjectSection = () => {
    if (
      project &&
      project.data &&
      project.data.data &&
      project?.data?.data?.input?.structures[0]?.elements[0].beam?.segments[0].layer?.start
    ) {
      let secguid = project?.data.data?.input?.structures[0].elements[0].beam?.segments[0].layer.start.secGuid;
      return secguid ?? "";
    }
  };

  const getProjectSectionObj = () => {
    let secguid = getProjectSection();
    if (secguid) {
      return project?.data.data?.input?.secDb.find((s) => s?.id?.guid === secguid) ?? new ng.sections.Section();
    }
    return null;
  };

  const setProjectSectionGuid = async (guid: string, forceupdate: boolean = false) => {
    if (!project?.data.data?.input?.secDb.find((s) => s?.id?.guid === guid) || forceupdate) {
      const section = await dispatch(fetchSection({ guid })).unwrap();
      if (!section?.id?.guid || section.id.guid !== guid) {
        throw new Error(`Fetched section does not match requested GUID ${guid}`);
      }
      dispatch(ProjectSlice.actions.AddSectionToSecDb(section));
    }

    dispatch(ProjectSlice.actions.setProjectSection(guid));
    // Changing the column cross section affects kdef/kcr and partial factors.
    // Must be awaited: updateGammaApi snapshots project.data.data at dispatch time and its
    // fulfilled handler replaces project.data.data wholesale. If a caller mutates the project
    // (e.g. adding a material to mtrlDb) while the request is in flight, that mutation is silently
    // overwritten by the stale snapshot when the response lands.
    await dispatch(updateGammaApi()).unwrap();
  };

  const setProjectMaterialGuid = async (guid: string) => {
    // Material into mtrlDb first, then point the column at it — the same order as
    // setProjectSectionGuid. The self-weight is recomputed on setProjectMaterial, and needs the
    // material's density to be there when it is.
    if (!project?.data.data?.input?.mtrlDb.find((s) => s?.id?.guid === guid)) {
      let obj = await dispatch(fetchMaterial({ guid }));
      dispatch(ProjectSlice.actions.AddMaterialToMtrlDb(obj.payload.data));
    }

    dispatch(ProjectSlice.actions.setProjectMaterial(guid));
  };

  // Apply a NON-recommended design suggestion to the current project: swap the element's section
  // and material (resolved from the AutoDesign cache, the project's own dbs, or fetched as a
  // fallback), then replace its reinforcement. Output is left to be invalidated so the user
  // re-runs a check to get fresh results for the chosen suggestion. Throws if a referenced
  // section/material cannot be resolved, or if the gamma recompute fails, so the caller can
  // surface an error instead of leaving a physically inconsistent member.
  const applyDesignSuggestion = async (
    suggestion: ng.endpoint_args.common_api_functions.AutoDesignSuggestion_json,
    secDb: ng.sections.Section_json[],
    mtrlDb: ng.material.Data_json[],
  ) => {
    // Resolve a section/material by guid: AutoDesign cache → the project's own db → server fetch.
    const resolveSection = async (guid: string): Promise<ng.sections.Section_json | undefined> => {
      const section =
        secDb.find((s) => s.id?.guid === guid) ??
        project?.data.data?.input?.secDb?.find((s) => s?.id?.guid === guid);
      if (section) {
        return section;
      }

      const fetchedSection = await dispatch(fetchSection({ guid })).unwrap();
      return fetchedSection?.id?.guid === guid ? fetchedSection : undefined;
    };

    const resolveMaterial = async (guid: string): Promise<ng.material.Data_json | undefined> =>
      mtrlDb.find((m) => m.id?.guid === guid) ??
      project?.data.data?.input?.mtrlDb?.find((m) => m?.id?.guid === guid) ??
      ((await dispatch(fetchMaterial({ guid }))).payload?.data as ng.material.Data_json | undefined);

    // 1. Section — an empty sectionGuid means the suggestion keeps the current section; otherwise
    //    resolve it (throw if we can't) and point the element at it. Gamma recompute happens last.
    if (suggestion.sectionGuid) {
      const section = await resolveSection(suggestion.sectionGuid);
      if (!section) {
        throw new Error(t("design_apply_section_missing_.label", "Could not resolve the suggested section."));
      }
      dispatch(ProjectSlice.actions.AddSectionToSecDb(section));
      dispatch(ProjectSlice.actions.setProjectSection(suggestion.sectionGuid));
    }

    // 2. Materials — resolve every material the suggestion references (beam + reinforcement) and
    //    upsert it into the project db. Throw if any cannot be resolved rather than leaving the
    //    beam/reinforcement pointing at a material absent from mtrlDb.
    const concrete = suggestion.concrete;
    const referenced = new Set<string>();
    if (suggestion.materialGuid) referenced.add(suggestion.materialGuid);
    for (const grp of concrete?.rebars?.grps ?? []) if (grp.mtrlGuid) referenced.add(grp.mtrlGuid);
    for (const grp of concrete?.strands?.grps ?? []) if (grp.mtrlGuid) referenced.add(grp.mtrlGuid);
    for (const grp of concrete?.links?.linkGrps ?? []) if (grp.mtrlGuid) referenced.add(grp.mtrlGuid);
    for (const guid of referenced) {
      const material = await resolveMaterial(guid);
      if (!material) {
        throw new Error(t("design_apply_material_missing_.label", "Could not resolve a suggested material."));
      }
      dispatch(ProjectSlice.actions.UpsertMaterialInMtrlDb(material));
    }

    // 3. Reinforcement — concrete only; pass the full set so absent parts (e.g. strands) are
    //    cleared, not kept. Steel and timber suggestions carry no reinforcement (they are designed
    //    by changing the section alone), and dispatching an empty set for them would register as an
    //    edit with the sync middleware for no change at all.
    if (concrete) {
      dispatch(
        ProjectSlice.actions.setElementReinforcement({
          rebars: concrete.rebars ?? null,
          strands: concrete.strands ?? null,
          links: concrete.links ?? null,
        }),
      );
    }

    // 4. Beam material — set after the reinforcement so setProjectMaterial's orphan-cleanup checks
    //    against the NEW reinforcement (materials used only by the old reinforcement get pruned).
    if (suggestion.materialGuid) {
      dispatch(ProjectSlice.actions.setProjectMaterial(suggestion.materialGuid));
    }

    // 5. Fire protection — the design varies the protection alongside the section, so the values it
    //    settled on come with the suggestion the user picked. Steel carries one arm of its oneof:
    //    the thickness of a protecting material, or the maximum member temperature it was checked
    //    against. Timber carries the thicknesses of the layers its protection material uses. A
    //    suggestion that designed no protection carries none, and nothing is dispatched.
    const steelFire = suggestion.steel;
    if (steelFire?.temperature != null) {
      dispatch(ProjectSlice.actions.setSteelFireProtectionTemperature(Number(steelFire.temperature)));
    } else if (steelFire?.protectingMaterial?.thickness != null) {
      dispatch(ProjectSlice.actions.setSteelFireProtectionThickness(Number(steelFire.protectingMaterial.thickness)));
    }

    const timberFire = suggestion.timber?.protectingMaterial;
    if (timberFire) {
      // Only the thicknesses — the material type and its characteristics are the user's choice and
      // the design does not touch them.
      const { thickness, thicknessInner, thicknessOuter } = timberFire;
      dispatch(ProjectSlice.actions.setTimberFireMaterialProps({ thickness, thicknessInner, thicknessOuter }));
    }

    // 6. Recompute partial factors / kdef / kcr for the new section LAST — updateGammaApi replaces
    //    project.data.data wholesale, so it must run after every input mutation above. unwrap() so
    //    a failed recompute rejects (and is surfaced) instead of silently resolving.
    await dispatch(updateGammaApi()).unwrap();
  };

  // Apply the recommended design result the engine returned. No gamma recompute here: the returned
  // project is the member the design service itself designed and checked, so its partial factors /
  // kdef / kcr already belong to the section it settled on. (Unlike applyDesignSuggestion, which
  // rebuilds a suggestion from its guids and has to recompute them.) The output it arrives with is
  // dropped rather than kept — the approval re-runs the analysis and the code check over it, see
  // the applyRecommendedDesign reducer.
  const applyRecommendedDesignResult = (recommendedProject: ng.frontend.column.Project_json) => {
    dispatch(ProjectSlice.actions.applyRecommendedDesign(recommendedProject));
  };

  const setSection = (guid: string) => {};

  const setEc = (ec: unknown) => {
    dispatch(ProjectSlice.actions.setEc(ec));
    dispatch(updateGammaApi());
  };

  const setColumnLength = (length: number, support?: { guid: string }) => {
    // A zero (or negative) column length corrupts the model, so ignore such values.
    if (!Number.isFinite(length) || length <= 0) {
      return;
    }
    dispatch(ProjectSlice.actions.setColumnLength(length));
    if (support) {
      dispatch(ProjectSlice.actions.updateSupportPosition({ guid: support.guid, newPos: length }));
    }
    let rebars = project?.data.data?.input?.structures[0].elements[0].beam?.concrete?.rebars;

    rebars?.grps.forEach((group) => {
      dispatch(ProjectSlice.actions.updateGroupLength({ length: length, guid: group.id?.guid ?? "" }));
    });

    let oldLength: number =
      (project?.data.data?.input?.structures[0].elements[0].beam?.segments[0].length as number) ?? 0;
    const loadingBefore = project?.data.data?.input?.structures[0].loading;
    // The self-weight follows the new length by itself (the self-weight middleware recomputes it on
    // the setColumnLength above), and `project` here is the state from before that — stretching
    // its pieces as well would undo the recomputation.
    const selfWeightGuid = selfWeightCaseGuid(loadingBefore?.loadcases);
    loadingBefore?.loads.forEach((load) => {
      let l = ng.load.Data.fromObject(load);
      if (selfWeightGuid && l.lcaseGuid === selfWeightGuid) return;
      if (l.distribution == ng.load.DistributionType.DISTRIBUTION_TYPE_POINT) {
        let position = l.positions?.[0];
        if (position) {
          let x = position.x;
          if (x == oldLength || x > length) {
            dispatch(
              ProjectSlice.actions.updateLoadPosition({
                guid: load.id?.guid ?? "",
                pointIndex: 0,
                position: { x: length },
              }),
            );
          }
        }
      } else if (l.distribution == ng.load.DistributionType.DISTRIBUTION_TYPE_LINE) {
        let start = l.positions?.[0];
        let end = l.positions?.[1];

        if (start && end) {
          if (end.x == oldLength || end.x > length) {
            dispatch(
              ProjectSlice.actions.updateLoadPosition({
                guid: load.id?.guid ?? "",
                pointIndex: 1,
                position: { x: length },
              }),
            );
          }

          let dt = Math.abs(length - Math.abs(start.x - end.x));
          if (end.x > length) {
            dispatch(
              ProjectSlice.actions.updateLoadPosition({
                guid: load.id?.guid ?? "",
                pointIndex: 1,
                position: { x: length },
              }),
            );
            if (start.x > length) {
              dispatch(
                ProjectSlice.actions.updateLoadPosition({
                  guid: load.id?.guid ?? "",
                  pointIndex: 0,
                  position: { x: dt },
                }),
              );
            }
          }
        }
      }
    });
  };

  const setConcreteCover = (cover: number) => {
    dispatch(ProjectSlice.actions.updateConcreteCover({ cover }));
  };

  const setLongitudinalBarsY = (num: number) => {
    dispatch(ProjectSlice.actions.updateLongitudinalBarsCount({ axis: "Y", num }));
  };

  const setLongitudinalDiameterY = (diameter: number) => {
    dispatch(ProjectSlice.actions.updateLongitudinalDiameter({ axis: "Y", diameter }));
  };

  const setLongitudinalBarsZ = (num: number) => {
    dispatch(ProjectSlice.actions.updateLongitudinalBarsCount({ axis: "Z", num }));
  };

  const setLongitudinalDiameterZ = (diameter: number) => {
    dispatch(ProjectSlice.actions.updateLongitudinalDiameter({ axis: "Z", diameter }));
  };

  const setRadialBars = (num: number) => {
    dispatch(ProjectSlice.actions.updateRadialBarsCount({ num }));
  };

  const setRadialDiameter = (diameter: number) => {
    dispatch(ProjectSlice.actions.updateRadialDiameter({ diameter }));
  };

  const setStirrupDiameter = (diameter: number) => {
    dispatch(ProjectSlice.actions.updateStirrupDiameter({ diameter }));
  };

  const setStirrupSpacing = (spacing: number) => {
    dispatch(ProjectSlice.actions.updateStirrupSpacing({ spacing }));
  };

  const setExactBarCoordinates = (enabled: boolean) => {
    dispatch(ProjectSlice.actions.setExactBarCoordinates({ enabled }));
  };

  const regenerateStandaloneBarsFromCurrentRebars = () => {
    dispatch(ProjectSlice.actions.regenerateStandaloneBarsFromCurrentRebars());
  };

  const clearStandaloneBars = () => {
    dispatch(ProjectSlice.actions.clearStandaloneBars());
  };

  const addStandaloneBar = (bar?: { y?: number; z?: number; diameter?: number; mtrlGuid?: string }) => {
    dispatch(ProjectSlice.actions.addStandaloneBar(bar ?? {}));
  };

  const updateStandaloneBar = (bar: { guid: string; y?: number; z?: number; diameter?: number }) => {
    dispatch(ProjectSlice.actions.updateStandaloneBar(bar));
  };

  const deleteStandaloneBar = (guid: string) => {
    dispatch(ProjectSlice.actions.deleteStandaloneBar(guid));
  };

  const ensureMaterialInProjectDb = async (guid: string): Promise<boolean> => {
    if (!guid) {
      return false;
    }

    const existsInDb =
      project?.data?.data?.input?.mtrlDb?.some(
        (material: { id?: { guid?: string } } | null | undefined) => material?.id?.guid === guid,
      ) ?? false;
    if (existsInDb) {
      return true;
    }

    const fetched = await dispatch(fetchMaterial({ guid }));
    const payload = fetched.payload as {
      kind?: string;
      data?: { dbMtrl?: ng.material.Data_json };
    };
    if (payload?.kind !== "data" || !payload?.data?.dbMtrl) {
      return false;
    }

    dispatch(ProjectSlice.actions.UpsertMaterialInMtrlDb(payload.data.dbMtrl));
    return true;
  };

  const setLongitudinalMaterialGuid = async (guid: string) => {
    const ready = await ensureMaterialInProjectDb(guid);
    if (!ready) {
      return;
    }
    dispatch(ProjectSlice.actions.updateLongitudinalMaterialGuid({ guid }));
  };

  const setStirrupMaterialGuid = async (guid: string) => {
    const ready = await ensureMaterialInProjectDb(guid);
    if (!ready) {
      return;
    }
    dispatch(ProjectSlice.actions.updateStirrupMaterialGuid({ guid }));
  };

  const getCoverAndSpaces = async (input: ng.endpoint_args.rc_design.CoverSpaceInput_json) => {
    const result = await dispatch(fetchCoverAndSpaces({ input }));
    if (fetchCoverAndSpaces.rejected.match(result)) {
      throw result.error;
    }
    return result.payload;
  };

  const setAutoBuckling = (active: boolean) => {
    dispatch(ProjectSlice.actions.setAutoBucklingLength(active));
  };

  const setAutoDesign = (autoDesign: unknown) => {
    dispatch(ProjectSlice.actions.setAutoDesign(autoDesign));
  };

  // Convenience setter used by design panels to update only the utilization threshold.
  const setAutoDesignLimitUtilization = (value: number) => {
    dispatch(ProjectSlice.actions.setAutoDesignLimitUtilization(value));
  };

  const setCalculationMethod = (method: CalculationMethod) => {
    dispatch(ProjectSlice.actions.setCalculationMethod(method));
  };

  const setConsiderCracking = (enabled: boolean) => {
    dispatch(ProjectSlice.actions.setConsiderCracking(enabled));
  };

  const setCrackConvergence = (value: number) => {
    dispatch(ProjectSlice.actions.setCrackConvergence(value));
  };

  const setFireDuration = (index: number) => {
    dispatch(ProjectSlice.actions.setFireDuration(index));
  };

  const setFireExposureSide = (side: FireExposureSide, enabled: boolean) => {
    dispatch(ProjectSlice.actions.setFireExposureSide({ side, enabled }));
  };

  const setFireAggregate = (aggregate: ng.design.concrete.Aggregates) => {
    dispatch(ProjectSlice.actions.setFireAggregate(aggregate));
  };

  const setFireManufacturing = (manufacturing: ng.design.concrete.ManufacturingType) => {
    dispatch(ProjectSlice.actions.setFireManufacturing(manufacturing));
  };

  const initConcreteFireDefaults = (defaults: ConcreteFireDefaults) => {
    dispatch(ProjectSlice.actions.initConcreteFireDefaults(defaults));
  };

  const setFireUse02Epsilon = (enabled: boolean) => {
    dispatch(ProjectSlice.actions.setFireUse02Epsilon(enabled));
  };

  const setSteelFireDuration = (minutes: number) => {
    dispatch(ProjectSlice.actions.setSteelFireDuration(minutes));
  };

  const setSteelFireTimeStep = (seconds: number) => {
    dispatch(ProjectSlice.actions.setSteelFireTimeStep(seconds));
  };

  const setSteelFireExposure = (exposure: ng.design.steel.SectionExposure) => {
    dispatch(ProjectSlice.actions.setSteelFireExposure(exposure));
  };

  const setSteelFireTemperatureCurve = (curve: ng.design.general.TemperatureCurve) => {
    dispatch(ProjectSlice.actions.setSteelFireTemperatureCurve(curve));
  };

  const setSteelFireCritDeflection = (enabled: boolean) => {
    dispatch(ProjectSlice.actions.setSteelFireCritDeflection(enabled));
  };

  const setSteelFireProtectionMode = (mode: "mtrl" | "temp") => {
    dispatch(ProjectSlice.actions.setSteelFireProtectionMode(mode));
  };

  const setSteelFireProtectingMaterial = async (guid: string) => {
    if (guid && guid !== "none_") {
      await ensureMaterialInProjectDb(guid);
    }
    dispatch(ProjectSlice.actions.setSteelFireProtectingMaterial(guid));
  };

  const setSteelFireProtectionThickness = (thicknessMeters: number) => {
    dispatch(ProjectSlice.actions.setSteelFireProtectionThickness(thicknessMeters));
  };

  const setSteelFireProtectionTemperature = (temperature: number) => {
    dispatch(ProjectSlice.actions.setSteelFireProtectionTemperature(temperature));
  };

  const setTimberFireDuration = (minutes: number) => {
    dispatch(ProjectSlice.actions.setTimberFireDuration(minutes));
  };

  const setTimberCharringRateAuto = (enabled: boolean) => {
    dispatch(ProjectSlice.actions.setTimberCharringRateAuto(enabled));
  };

  const setTimberCharringRate = (value: number) => {
    dispatch(ProjectSlice.actions.setTimberCharringRate(value));
  };

  const setTimberStructProtection = (side: "top" | "bottom" | "left" | "right", enabled: boolean) => {
    dispatch(ProjectSlice.actions.setTimberStructProtection({ side, enabled }));
  };

  const setTimberCharringStartTime = (side: "top" | "bottom" | "left" | "right", value: number) => {
    dispatch(ProjectSlice.actions.setTimberCharringStartTime({ side, value }));
  };

  const setTimberFireMaterialType = async (materialType: ng.fire_protection.timber.MaterialType) => {
    dispatch(ProjectSlice.actions.setTimberFireMaterialType(materialType));
  };

  const setTimberFireMaterialProps = (
    props: Partial<Omit<ng.fire_protection.timber.CharacteristicData_json, "materialType">>,
  ) => {
    dispatch(ProjectSlice.actions.setTimberFireMaterialProps(props));
  };

  const updateBucklingBeta = (axis: "Y" | "Z", spanIndex: number, beta: number) => {
    dispatch(ProjectSlice.actions.updateBucklingBeta({ axis, spanIndex, beta }));
  };

  const updateBucklingLc = (axis: "Y" | "Z", spanIndex: number, lc: number) => {
    dispatch(ProjectSlice.actions.updateBucklingLc({ axis, spanIndex, lc }));
  };

  const setSteelBucklingLoadPosition = (position: ng.beam.Alignment) => {
    dispatch(ProjectSlice.actions.setSteelBucklingLoadPosition(position));
  };

  const setTimberBucklingLoadPosition = (position: ng.beam.Alignment) => {
    dispatch(ProjectSlice.actions.setTimberBucklingLoadPosition(position));
  };

  const updateSteelLtbBeta = (loc: "top" | "btm", spanIndex: number, beta: number) => {
    dispatch(ProjectSlice.actions.updateSteelLtbBeta({ loc, spanIndex, beta }));
  };

  const updateSteelLtbLc = (loc: "top" | "btm", spanIndex: number, lc: number) => {
    dispatch(ProjectSlice.actions.updateSteelLtbLc({ loc, spanIndex, lc }));
  };

  const setSteelContinuouslyRestrained = (loc: "top" | "btm", value: boolean) => {
    dispatch(ProjectSlice.actions.setSteelContinuouslyRestrained({ loc, value }));
  };

  const updateTimberLtsbLc = (spanIndex: number, lc: number) => {
    dispatch(ProjectSlice.actions.updateTimberLtsbLc({ spanIndex, lc }));
  };

  const updateTimberLtsbSupportCondition = (spanIndex: number, supportCondition: ng.beam.SupportCondition) => {
    dispatch(ProjectSlice.actions.updateTimberLtsbSupportCondition({ spanIndex, supportCondition }));
  };

  const setTimberLtsbContinuouslyRestrained = (value: boolean) => {
    dispatch(ProjectSlice.actions.setTimberLtsbContinuouslyRestrained(value));
  };

  const addSupport = (support: ng.stage.Support_json) => {
    dispatch(ProjectSlice.actions.addSupport(support));
  };

  const deleteSupport = (guid: string) => {
    dispatch(ProjectSlice.actions.deleteSupport(guid));
  };

  const updateSupportPosition = (guid: string, newPos: number) => {
    dispatch(ProjectSlice.actions.updateSupportPosition({ guid, newPos }));
  };

  const updateSupportConnectivity = (guid: string, axis: "Y" | "Z", type: SupportConnectivityType) => {
    dispatch(ProjectSlice.actions.updateSupportConnectivity({ guid, axis, type }));
  };

  const setSecondOrder = (secondOrder: Boolean) => {
    dispatch(ProjectSlice.actions.setSecondOrder(secondOrder));
  };

  const setInitialBowImperfection = (val: number) => {
    dispatch(ProjectSlice.actions.setInitBowImp(val));
  };

  // ==============================================================================================
  // LCO Generator

  const generateLoadCombsCall = async (input: ng.endpoint_args.eurocode.LoadCombGenArgsIn_json) => {
    return await dispatch(generateLoadCombsApi({ input })).unwrap();
  };

  const generateLoadCombs = async () => {
    const latestProject = getLatestProject() ?? project;
    const proj = latestProject?.data?.data;
    const cache = latestProject?.data?.settings?.endpointCache;

    if (!proj) throw new Error("c.proj is undefined");

    let projInput = proj.input;
    let structure = projInput?.structures?.[0];
    let loading = structure?.loading;
    let stage = structure?.stages?.[0];

    // Create a new cache with default values if one does not exist
    const allLoadcases = loading?.loadcases.map((lc) => lc.id?.guid).filter((guid) => guid !== undefined);
    const defaultLCTypes = [ng.loadcombination.Type.TYPE_ULTIMATE];

    const loadCombGenSettings = cache?.loadCombGenSettings ?? {
      lcTypes: defaultLCTypes.map((x) => ng.loadcombination.Type_ToObject(x) ?? ""),
      loadcases: allLoadcases ?? [],
      simple: ng.endpoint_args.eurocode.Simple.create({
        includePermanentLoadFavorable: false,
      }),
    };

    // Prepare the input for the load combination generator endpoint
    const input_json: ng.endpoint_args.eurocode.LoadCombGenArgsIn_json = {
      projectData: projInput,
      settings: loadCombGenSettings,
    };

    // Call the load combination generator endpoint
    const output = await generateLoadCombsCall(input_json);
    const log = output?.log?.entries; //.filter(x => x.type === ng.log.LogType.)
    console.log("loadcombs log", log);
    if (output === null) {
      console.error("Caught null output from generateLoadCombs");
      return;
    }
    dispatch(ProjectSlice.actions.generateLoadCombs_apply(output));
  };

  const lcoGenerator_updateAlt = async (val: number) => {
    dispatch(ProjectSlice.actions.lcoGenerator_updateAlt(val));
    await generateLoadCombs();
  };

  const lcoGenerator_update_snowload = async (val: number) => {
    dispatch(ProjectSlice.actions.lcoGenerator_update_snowload(val));
    await generateLoadCombs();
  };

  const lcoGenerator_update_use610ab = async (val: boolean) => {
    dispatch(ProjectSlice.actions.lcoGenerator_update_use610ab(val));
    await generateLoadCombs();
  };

  const lcoGenerator_update_usePsi1 = async (val: boolean) => {
    dispatch(ProjectSlice.actions.lcoGenerator_update_usePsi1(val));
    await generateLoadCombs();
  };
  const lcoGenerator_update_selLCAs = async (val: string[]) => {
    dispatch(ProjectSlice.actions.lcoGenerator_update_selLCAs(val));
    await generateLoadCombs();
  };
  const lcoGenerator_update_selLCOs = async (val: ng.loadcombination.Type[]) => {
    dispatch(ProjectSlice.actions.lcoGenerator_update_selLCOs(val));
    await generateLoadCombs();
  };
  const lcoGenerator_inclPermLoadFavor = async (val: boolean) => {
    dispatch(ProjectSlice.actions.lcoGenerator_inclPermLoadFavor(val));
    await generateLoadCombs();
  };

  return {
    project,
    generateLoadCombs,
    generateLoadCombsStatus,
    lcoGenerator_updateAlt,
    lcoGenerator_update_snowload,
    lcoGenerator_update_use610ab,
    lcoGenerator_update_usePsi1,
    lcoGenerator_update_selLCAs,
    lcoGenerator_update_selLCOs,
    lcoGenerator_inclPermLoadFavor,
    deleteProject,
    analyse,
    codeCheck,
    analyseThenCodeCheck,
    designAsync,
    getProject,
    saveTempProject,
    setProject,
    setEc,
    setUpdateSettings,
    addLoadcaseRedux,
    deleteLoadcaseRedux,
    setLoadCaseName,
    setLoadcaseType,
    setLoadcaseCategory,
    setLoadcaseDuration,
    setLoadCaseVisuals,
    addLoadCombination,
    deleteLoadCombination,
    updateLoadCombinationName,
    updateLoadCombinationType,
    addLoadCombinationPart,
    updateLoadCombinationPart,
    deleteLoadCombinationPart,
    updateLoadCombinationCoa,
    updateDominatingLCA,
    updateFavorableLCA,
    considerInAnalysis,
    setProjectMaterialGuid,
    setProjectSectionGuid,
    applyDesignSuggestion,
    applyRecommendedDesignResult,
    getProjectSection,
    addLoad,
    updateLoad,
    updateLoadValue,
    updateLoadPosition,
    deleteLoad,
    addSelfWeights,
    delSelfWeights,
    setColumnLength,
    setConcreteCover,
    setLongitudinalBarsY,
    setLongitudinalDiameterY,
    setLongitudinalBarsZ,
    setLongitudinalDiameterZ,
    setRadialBars,
    setRadialDiameter,
    setStirrupDiameter,
    setStirrupSpacing,
    setExactBarCoordinates,
    setLongitudinalMaterialGuid,
    setStirrupMaterialGuid,
    regenerateStandaloneBarsFromCurrentRebars,
    clearStandaloneBars,
    addStandaloneBar,
    updateStandaloneBar,
    deleteStandaloneBar,
    getCoverAndSpaces,
    setAutoBuckling,
    setAutoDesign,
    setAutoDesignLimitUtilization,
    setCalculationMethod,
    setConsiderCracking,
    setCrackConvergence,
    setFireDuration,
    setFireExposureSide,
    setFireAggregate,
    setFireManufacturing,
    setFireUse02Epsilon,
    initConcreteFireDefaults,
    setSteelFireDuration,
    setSteelFireTimeStep,
    setSteelFireExposure,
    setSteelFireTemperatureCurve,
    setSteelFireCritDeflection,
    setSteelFireProtectionMode,
    setSteelFireProtectingMaterial,
    setSteelFireProtectionThickness,
    setSteelFireProtectionTemperature,
    setTimberFireDuration,
    setTimberCharringRateAuto,
    setTimberCharringRate,
    setTimberStructProtection,
    setTimberCharringStartTime,
    setTimberFireMaterialType,
    setTimberFireMaterialProps,
    updateBucklingBeta,
    updateBucklingLc,
    setSteelBucklingLoadPosition,
    setTimberBucklingLoadPosition,
    updateSteelLtbBeta,
    updateSteelLtbLc,
    setSteelContinuouslyRestrained,
    updateTimberLtsbLc,
    updateTimberLtsbSupportCondition,
    setTimberLtsbContinuouslyRestrained,
    addSupport,
    deleteSupport,
    updateSupportPosition,
    updateSupportConnectivity,
    getProjectSectionObj,
    setSecondOrder,
    secondOrder,
    setInitialBowImperfection,
  };
}

const normalizeIncomingProject = (
  incomingProject: IFile,
): { project: IFile; projObj: ng.frontend.column.Project | null } => {
  let projectData = incomingProject?.data;

  if (typeof projectData === "string") {
    projectData = JSON.parse(projectData);
  }

  let projObj: ng.frontend.column.Project | null = null;
  if (projectData) {
    const rawEndpointCache = (projectData?.settings?.endpointCache ?? {}) as Record<string, unknown>;
    const rawAutoBuckling = rawEndpointCache.autoBucklingLength;
    const rawCalculationMethod = rawEndpointCache.calculationMethod;
    const rawConsiderCracking = rawEndpointCache.considerCracking;
    const rawCrackConvergence = rawEndpointCache.crackConvergence;

    projObj = ng.frontend.column.Project.fromObject(projectData);
    projectData = ng.frontend.column.Project.toObject(projObj) ?? projectData;

    if (projectData?.settings) {
      if (!projectData.settings.endpointCache) {
        projectData.settings.endpointCache = {};
      }
      const endpointCache = projectData.settings.endpointCache as Record<string, unknown>;

      if (rawAutoBuckling !== undefined) {
        endpointCache.autoBucklingLength = rawAutoBuckling;
      }
      if (isCalculationMethod(rawCalculationMethod)) {
        endpointCache.calculationMethod = rawCalculationMethod;
      }
      if (typeof rawConsiderCracking === "boolean") {
        endpointCache.considerCracking = rawConsiderCracking;
      }
      if (typeof rawCrackConvergence === "number") {
        endpointCache.crackConvergence = rawCrackConvergence;
      }
    }

    // crkConv is a ratio (1e-3 by default), but a dropdown that briefly replaced its edit field
    // offered per mille numbers (0.5 ... 2) and stored them unconverted - a 50-200 % tolerance that
    // stops the crack loop on its first pass. The edit field accepts at most 0.1 (100 per mille), so
    // anything above that can only have come from the dropdown: read it as per mille.
    const rcDs = projectData?.data?.input?.structures?.[0]?.designSettings?.rc;
    if (rcDs && typeof rcDs.crkConv === "number" && rcDs.crkConv > 0.1) {
      rcDs.crkConv = rcDs.crkConv / 1000;
    }
  }

  return {
    project: {
      ...incomingProject,
      data: projectData,
    } as IFile,
    projObj,
  };
};

// ===============================================================================================
//                                CARBON FOOTPRINT (output.carbon)
// ===============================================================================================

/**
 * The carbon footprint is the one part of `output` that depends on the input alone — quantities of
 * material times an emission factor — and not on an analysis or a code check. So it does not go
 * stale when the rest of the output does: changing a load, a support or a fire setting invalidates
 * every result except this one, and the figure is carried across that invalidation rather than
 * blinking out on an edit it has nothing to do with.
 *
 * What DOES invalidate it — the section, the material, the length, the reinforcement — resets it
 * instead, immediately, via the resetCarbonFootprint reducer (see the sync middleware). A stale
 * figure is never shown as if it were current, and that holds whether or not a recalculation can
 * follow: reset does not depend on the endpoint. All three materials have one now (RC-Design for
 * concrete, FEM-Design for steel and timber), so the reset is normally followed by a fresh figure;
 * a material with no live endpoint would reset to an empty readout and stay there until the next
 * code check.
 */
const carbonOf = (state: ProjectState): ElementCarbonJson[] => state.project?.data?.data?.output?.carbon ?? [];

/**
 * Same, but detached from the store: a plain deep copy, so it can be written back after the subtree
 * it came from has been dropped. Reading it out of the immer draft and re-parenting the draft itself
 * would make the restore depend on immer's handling of a proxy whose parent no longer exists — for
 * an object this small (one element, a handful of items) a copy is the cheaper thing to reason about.
 */
const carbonSnapshot = (state: ProjectState): ElementCarbonJson[] => {
  const carbon = carbonOf(state);
  return carbon.length > 0 ? (JSON.parse(JSON.stringify(carbon)) as typeof carbon) : [];
};

/**
 * Does this carbon result actually state a figure?
 *
 * Not the same question as "is the array non-empty": a design service answers with one entry per
 * element, and an entry can arrive with no footprint at all, or with a footprint of 0 and no items —
 * which is what a run with no emission data available looks like. The readout shows those as no
 * result, so everything that asks "do we already have a figure?" has to agree with it. Counting
 * entries instead would let an empty placeholder block a real figure from being restored, and would
 * make an opened project skip the recalculation that would have produced one.
 */
const carbonHasFigure = (carbon: ElementCarbonJson[]): boolean =>
  carbon.some((element) => {
    const footprint = element?.carbon;
    if (!footprint) return false;
    return (footprint.items?.length ?? 0) > 0 || (typeof footprint.gwp === "number" && footprint.gwp !== 0);
  });

/** As above, for callers outside this slice that hold the project rather than the slice state. */
export const projectHasCarbonFigure = (project: IFile | null | undefined): boolean =>
  carbonHasFigure(project?.data?.data?.output?.carbon ?? []);

/** An all-empty `output`, used when carbon is the first thing to be written into a pruned project. */
const emptyOutput = (): ng.output.Data_json => ng.output.Data.toObject(new ng.output.Data())!;

/** Writes the carbon result into `output.carbon`, creating `output` if there is none. */
const setCarbon = (state: ProjectState, carbon: ElementCarbonJson[]) => {
  const projectData = state.project?.data?.data;
  if (!projectData) return;

  if (!projectData.output) {
    projectData.output = emptyOutput();
  }
  projectData.output.carbon = carbon;
};

/**
 * Puts `carbon` back after `output` has been replaced wholesale (prune, analysis, gamma), unless
 * the replacement brought its own carbon — a code check's result wins over the one we kept.
 */
const restoreCarbon = (state: ProjectState, carbon: ElementCarbonJson[]) => {
  if (!carbonHasFigure(carbon)) return;
  if (carbonHasFigure(carbonOf(state))) return;
  setCarbon(state, carbon);
};

// ===============================================================================================
//                                PROJECT SLICE DEFINITION
// ===============================================================================================

export const ProjectSlice = createAppSlice({
  name: "ProjectSlice",
  initialState: initialState,
  reducers: {
    setProject: (state, action) => {
      const incomingProject = action.payload as IFile;
      const { project, projObj } = normalizeIncomingProject(incomingProject);

      const hadOutput = !!project?.data?.data?.output;
      const beam = project?.data?.data?.input?.structures?.[0]?.elements?.[0]?.beam as
        | Record<string, unknown>
        | undefined;
      const materialType = beam?.concrete ? "concrete" : beam?.steel ? "steel" : beam?.timber ? "timber" : "unknown";
      console.log(
        `[projectSlice] setProject guid=${project?.guid} hasOutput=${hadOutput} materialType=${materialType}`,
      );

      state.project = project;

      syncStandaloneBarsOnProjectLoad(state);

      if (projObj?.data) {
        const activeLCOs = updateActiveLcosInContext(projObj.data);
        state.activeLCOs = activeLCOs;
      }
    },
    // Wholesale replacement of the project with a copy the user just edited in the UI - the
    // structuredClone/mutate/dispatch pattern used by the material settings panel and modal.
    // Deliberately NOT setProject: setProject means "a project was loaded from the server", so
    // syncMiddleware excludes it from both the debounced auto-save and output pruning. Edits
    // dispatched through setProject therefore lived in memory only and were lost on reload. This
    // action is in neither denylist, so an edit saves and invalidates stale results like any other
    // input change. The payload is already a normalized project, so no re-normalization here.
    applyProjectEdit: (state, action) => {
      state.project = action.payload;
    },

    setActiveLCOs: (state, action) => {
      state.activeLCOs = action.payload;
    },

    dismissCalcError: (state) => {
      state.calcError = null;
    },

    updateSettings: (state, action) => {
      if (state.project && state.project.data && state.project.data.data && state.project.data.data.input) {
        state.project.data.data.input.structures[0].cc =
          ng.structure.ConsequenceClass_ToObject(action.payload.cc) ?? action.payload.cc;
        state.project.data.data.input.ec!.generation =
          ng.eurocode.Generation_ToObject(action.payload.generation) ?? action.payload.generation;
        state.project.data.data.input.ec!.nationalAnnex =
          ng.eurocode.Annex_ToObject(action.payload.nationalAnnex) ?? action.payload.nationalAnnex;
        state.project.data.data.input.structures[0].reliabilityClass =
          ng.structure.ReliabilityClass_ToObject(action.payload.reliability) ?? action.payload.reliability;
      }
    },

    setProjectSection(state, action) {
      let project = state.project;
      if (
        project &&
        project.data &&
        project.data.data &&
        project?.data?.data?.input?.structures[0]?.elements[0].beam?.segments[0].layer
      ) {
        if (project.data.data.input.structures[0].elements[0].beam.segments[0].layer.start) {
          project.data.data.input.structures[0].elements[0].beam.segments[0].layer.start = { secGuid: action.payload };
        }
        if (project.data.data.input.structures[0].elements[0].beam.segments[0].layer.end) {
          project.data.data.input.structures[0].elements[0].beam.segments[0].layer.end = { secGuid: action.payload };
        }

        const beam = getPrimaryBeamData(state);
        if (beam) {
          syncStandaloneBarsWithCurrentRebars(state, beam, true);
          setPrimaryBeamData(state, beam);
        }
      }
    },

    setProjectMaterial(state, action) {
      let project = state.project;
      if (project && project.data && project.data.data && project?.data?.data?.input?.structures[0]?.elements[0].beam) {
        const beam = project.data.data.input.structures[0].elements[0].beam;
        const previousGuid = beam.mtrlGuid;
        const nextGuid = action.payload;
        beam.mtrlGuid = nextGuid;

        // Out with the old: AddMaterialToMtrlDb only ever upserts, so without this the
        // previously selected beam material would linger in mtrlDb as an orphan. Drop it
        // once nothing (beam or reinforcement) references it — mirroring
        // cleanupUnusedReinforcementMaterialsInDb, but driven off the beam's own material.
        if (previousGuid && previousGuid !== nextGuid) {
          const beamData = getPrimaryBeamData(state);
          const mtrlDb = project.data.data.input.mtrlDb;
          if (beamData && mtrlDb && !getReferencedMaterialGuids(beamData).has(previousGuid)) {
            project.data.data.input.mtrlDb = mtrlDb.filter((material) => material?.id?.guid !== previousGuid);
          }
        }
      }
    },
    AddMaterialToMtrlDb(state, action) {
      if (state.project && state.project.data && state.project.data.data && state.project.data.data.input) {
        const dbMtrl = action.payload.dbMtrl;
        const guid = dbMtrl?.id?.guid;
        if (!guid) {
          return;
        }

        const mtrlDb = state.project.data.data.input.mtrlDb ?? [];
        const existingIndex = mtrlDb.findIndex((material) => material?.id?.guid === guid);
        if (existingIndex >= 0) {
          mtrlDb[existingIndex] = dbMtrl;
        } else {
          mtrlDb.push(dbMtrl);
        }

        state.project.data.data.input.mtrlDb = mtrlDb;
      }
    },
    UpsertMaterialInMtrlDb(state, action) {
      const projectInput = state.project?.data?.data?.input;
      if (!projectInput) {
        return;
      }

      const dbMtrl = action.payload as ng.material.Data_json;
      const guid = dbMtrl?.id?.guid;
      if (!guid) {
        return;
      }

      const mtrlDb = projectInput.mtrlDb ?? [];
      const existingIndex = mtrlDb.findIndex((material) => material?.id?.guid === guid);
      if (existingIndex >= 0) {
        mtrlDb[existingIndex] = dbMtrl;
      } else {
        mtrlDb.push(dbMtrl);
      }

      projectInput.mtrlDb = mtrlDb;
    },
    AddSectionToSecDb(state, action) {
      if (state.project && state.project.data && state.project.data.data && state.project.data.data.input) {
        const secDb = state.project.data.data.input.secDb ?? [];
        const existingIndex = secDb.findIndex((section) => section?.id?.guid === action.payload?.id?.guid);
        if (existingIndex >= 0) {
          secDb[existingIndex] = action.payload;
        } else {
          secDb.push(action.payload);
        }
        state.project.data.data.input.secDb = secDb;
      }
    },

    // Commit the recommended design result (the CodeCheckOutput.project the design engine
    // returned) to the project after the user approves it in the review modal. Replaces the inner
    // project data wholesale — the returned project is the designed member, so its input is what
    // the user approved, partial factors and all.
    //
    // Its OUTPUT, however, is not what the user ends up reading. This action prunes like any other
    // input change (it is not in the middleware's PRUNE_EXCLUDED_ACTION_TYPES), because approving
    // re-runs the analysis and then the code check over the applied section — see applySelected in
    // SXDesignReviewPanel. The design engine never re-analyses between candidates, so the output
    // that arrives here was computed from the section forces of the *previous* section; the
    // re-run's controls supersede it, and pre-populating the report with these first would only
    // show the user something less correct for the few seconds until they land. The review panel
    // still reads these controls, but from the staged result in DesignReviewSlice, not from here.
    //
    // Not save-excluded either: approval is the moment the applied input is persisted.
    applyRecommendedDesign(state, action: PayloadAction<ng.frontend.column.Project_json>) {
      if (state.project) {
        // Cloned, not assigned straight through. Immer does not draft an assigned subtree: the
        // payload object itself becomes the new state, and it arrives already frozen (it is an
        // Immer result from the staged design in DesignReviewSlice). The bar rebuild below writes
        // through setPrimaryBeamData, which would then throw "Cannot assign to read only property
        // 'structures'" and abort the whole approval.
        state.project.data = structuredClone(action.payload);
      }
      // Rebuild the viewport standalone bars from the applied layers, exactly as the
      // setElementReinforcement path does. Without this the engine's own bar coordinates go
      // straight into the viewport and are then silently replaced on the next project load by
      // syncStandaloneBarsOnProjectLoad — so the layout the user approved was not the layout they
      // got back. Issue #355.
      //
      // Must run AFTER the assignment above: getPrimarySection resolves the section from the
      // project's own secDb, so regenerating first would place the bars in the section the design
      // replaced rather than the one it chose.
      const beam = getPrimaryBeamData(state);
      if (beam?.getConcrete()) {
        syncStandaloneBarsWithCurrentRebars(state, beam, true);
        setPrimaryBeamData(state, beam);
      }
      state.status = "succeeded";
      state.designStatus = "succeeded";
      // No carbon is carried over here, unlike the other wholesale replacements: an approved design
      // changes the section (and for concrete the reinforcement with it), so the sync middleware
      // treats it as an edit and resets — and recalculates — the figure anyway.
    },

    // Replace the primary element's reinforcement (rebars / strands / links) with a chosen
    // (non-recommended) suggestion's reinforcement. Used by the applyDesignSuggestion thunk in
    // useProject. The payload is the COMPLETE intended reinforcement: each field is set from the
    // payload or cleared to an empty container when absent, so a suggestion with (e.g.) no strands
    // clears any pre-existing prestressing rather than leaving it mixed with the new rebars. This
    // is a normal input mutation: it prunes stale output and auto-saves.
    setElementReinforcement(
      state,
      action: PayloadAction<{
        rebars?: ng.rebar.Data_json | null;
        strands?: ng.strand.Data_json | null;
        links?: ng.link.Data_json | null;
      }>,
    ) {
      const beam = getPrimaryBeamData(state);
      const concrete = beam?.getConcrete();
      if (!beam || !concrete) return;

      const { rebars, strands, links } = action.payload;
      concrete.rebars = rebars ? ng.rebar.Data.fromObject(rebars) : ng.rebar.Data.create();
      concrete.strands = strands ? ng.strand.Data.fromObject(strands) : ng.strand.Data.create();
      concrete.links = links ? ng.link.Data.fromObject(links) : ng.link.Data.create();

      // Rebuild the viewport standalone-bar representation for the freshly applied reinforcement.
      syncStandaloneBarsWithCurrentRebars(state, beam, true);
      setPrimaryBeamData(state, beam);
    },

    /** START / SXProjectInputLoadCases state management */

    // addLoadcase: (state, action) => {
    //   const { loadcase } = action.payload;
    //   const loadcases = state.project?.data?.data?.input?.structures?.[0]?.loading?.loadcases;
    //     if (loadcases) {
    //       loadcases.push(loadcase);
    //     }
    // },
    addLoadcase: (state, action) => {
      const { loadcase } = action.payload;
      const structures = state.project?.data?.data?.input?.structures;
      if (structures && structures[0] && structures[0].loading) {
        const loading = structures[0].loading;
        loading.loadcases = [...(loading.loadcases ?? []), loadcase];

        // A new load case goes straight into the generator's chosen list. When the generator
        // settings do not exist yet the generator defaults to all load cases, so nothing to do.
        const guid = loadcase?.id?.guid;
        const lcGenSettings = (ensureEndpointCache(state) as any)?.loadCombGenSettings;
        if (guid && lcGenSettings?.loadcases && !lcGenSettings.loadcases.includes(guid)) {
          lcGenSettings.loadcases.push(guid);
        }
      }
    },
    deleteLoadcase: (state, action) => {
      const { guid } = action.payload;
      const structures = state.project?.data?.data?.input?.structures;
      if (!structures || !structures[0] || !structures[0].loading) return;
      const loading = structures[0].loading;

      // Remove from loadcases
      loading.loadcases = (loading.loadcases ?? []).filter((lc: any) => lc.id?.guid !== guid);

      // Remove from loadcombinations (user defined)
      loading.combinations = (loading.combinations ?? [])
        .map((lco: any) => ({
          ...lco,
          parts: (lco.parts ?? []).filter((part: any) => part.lcaseGuid !== guid),
        }))
        .filter((lco: any) => (lco.parts ?? []).length > 0);
      pruneOrphanLcombGuids(structures[0]);

      // Remove from loads
      loading.loads = (loading.loads ?? []).filter((load: any) => load.lcaseGuid !== guid);

      // If you store loadCombGenSettings in Redux, remove guid there as well:
      // if (loading.loadCombGenSettings && loading.loadCombGenSettings.loadcases) {
      //   loading.loadCombGenSettings.loadcases = loading.loadCombGenSettings.loadcases.filter((lcGuid: string) => lcGuid !== guid);
      // }
    },
    updateLoadCaseName: (state, action) => {
      const { guid, name } = action.payload;
      const loadcases = state.project?.data?.data?.input?.structures?.[0]?.loading?.loadcases;
      if (loadcases) {
        const lc = loadcases.find((lc: any) => lc.id?.guid === guid);
        if (lc && lc.id) {
          lc.id.name = name;
        }
      }
    },

    updateLoadcaseType: (state, action) => {
      const { guid, type } = action.payload;
      const loadcases = state.project?.data?.data?.input?.structures?.[0]?.loading?.loadcases;
      if (loadcases) {
        const lc = loadcases.find((lc: any) => lc.id?.guid === guid);
        if (lc) {
          lc.type = ng.loadcase.Type_ToObject(type) ?? type;
          if (type === ng.loadcase.Type.TYPE_IMPOSED_LOAD) {
            lc.category = ng.loadcase.Category_ToObject(ng.loadcase.Category.CATEGORY_A) ?? "CATEGORY_A";
          }
        }
      }
    },
    updateLoadcaseCategory: (state, action) => {
      const { guid, category } = action.payload;
      const loadcases = state.project?.data?.data?.input?.structures?.[0]?.loading?.loadcases;
      if (loadcases) {
        const lc = loadcases.find((lc: any) => lc.id?.guid === guid);
        if (lc && lc.id) {
          lc.category = ng.loadcase.Category_ToObject(category) ?? category;
        }
      }
    },
    updateLoadcaseDuration: (state, action) => {
      const { guid, durationClass } = action.payload;
      const loadcases = state.project?.data?.data?.input?.structures?.[0]?.loading?.loadcases;
      if (loadcases) {
        const lc = loadcases.find((lc: any) => lc.id?.guid === guid);
        if (lc && lc.id) {
          lc.durationClass = ng.loadcase.DurationClass_ToObject(durationClass) ?? durationClass;
        }
      }
    },
    updateLoadCaseVisuals: (state, action: PayloadAction<ng.frontend.column.LoadCaseVisuals_json[]>) => {
      // Assumes visuals is at project.data.settings.visuals
      if (state?.project?.data.settings?.visuals) {
        state.project.data.settings.visuals.loadcases = action.payload;
      } else {
        console.error("Missing visuals type");
      }
    },
    /** END / SXProjectInputLoadCases state management */

    addLoad: (state, action) => {
      const { load } = action.payload;
      const structures = state.project?.data?.data?.input?.structures;
      if (structures && structures[0] && structures[0].loading) {
        const loading = structures[0].loading;
        const incoming = load as any;
        let normalizedLoad: any = incoming;

        // Keep store values as plain JSON so ng.loading.Data.fromObject can parse them.
        if (incoming?.__proto_name === "ng.load.Data") {
          normalizedLoad = ng.load.Data.toObject(incoming as ng.load.Data) ?? incoming;
        }

        if (normalizedLoad?.direction) {
          const direction = normalizedLoad.direction as any;
          normalizedLoad = {
            ...normalizedLoad,
            direction: {
              x: typeof direction.x === "number" ? direction.x : 0,
              y: typeof direction.y === "number" ? direction.y : 0,
              z: typeof direction.z === "number" ? direction.z : 0,
            },
          };
        }

        loading.loads = [...(loading.loads ?? []), normalizedLoad];
      }
    },
    updateLoad: (state, action) => {
      const { guid, load } = action.payload;
      const structures = state.project?.data?.data?.input?.structures;
      if (structures && structures[0] && structures[0].loading && structures[0].loading.loads) {
        const loads = structures[0].loading.loads;
        const index = loads.findIndex((l: any) => l.id?.guid === guid);
        if (index !== -1) {
          const mergedLoad = { ...loads[index], ...load } as any;

          // Keep direction as plain JSON (no __proto_name), otherwise
          // ng.loading.Data.fromObject may drop/ignore it on next read.
          if (load?.direction) {
            const direction = load.direction as any;
            mergedLoad.direction = {
              x: typeof direction.x === "number" ? direction.x : 0,
              y: typeof direction.y === "number" ? direction.y : 0,
              z: typeof direction.z === "number" ? direction.z : 0,
            };
          }

          loads[index] = mergedLoad;
        }
      }
    },
    updateLoadPosition: (
      state,
      action: PayloadAction<{
        guid: string;
        pointIndex: number;
        position: Partial<ng.geometry.IPoint3D>;
      }>,
    ) => {
      const { guid, pointIndex, position } = action.payload;
      const loads = state.project?.data?.data?.input?.structures?.[0]?.loading?.loads;
      if (!loads) {
        return;
      }

      const load = loads.find((l: any) => l.id?.guid === guid);
      if (!load) {
        return;
      }

      if (!load.positions) {
        load.positions = [];
      }

      const current = load.positions[pointIndex] as any;
      const currentPoint = {
        x: typeof current?.x === "number" ? current.x : 0,
        y: typeof current?.y === "number" ? current.y : 0,
        z: typeof current?.z === "number" ? current.z : 0,
      };

      // Store plain JSON (no __proto_name) so ng.loading.Data.fromObject keeps the value.
      load.positions[pointIndex] = {
        x: position.x !== undefined ? position.x : currentPoint.x,
        y: position.y !== undefined ? position.y : currentPoint.y,
        z: position.z !== undefined ? position.z : currentPoint.z,
      };
    },
    updateLoadValue: (
      state,
      action: PayloadAction<{
        guid: string;
        valueIndex: number;
        value: number;
      }>,
    ) => {
      const { guid, valueIndex, value } = action.payload;
      const loads = state.project?.data?.data?.input?.structures?.[0]?.loading?.loads;
      if (!loads) {
        return;
      }

      const load = loads.find((l: any) => l.id?.guid === guid);
      if (!load) {
        return;
      }

      if (!load.values) {
        load.values = [];
      }

      load.values[valueIndex] = value;
    },
    deleteLoad: (state, action) => {
      const { guid } = action.payload;
      const structures = state.project?.data?.data?.input?.structures;
      if (structures && structures[0] && structures[0].loading && structures[0].loading.loads) {
        structures[0].loading.loads = structures[0].loading.loads.filter((l: any) => l.id?.guid !== guid);
      }
    },
    addSelfWeights: (state, action) => {
      const structures = state.project?.data?.data?.input?.structures;
      if (!structures?.[0]?.loading) return;
      const struct = structures[0];
      const loading = struct.loading!;
      const projInput = state.project?.data?.data?.input;
      if (!projInput) return;

      // --- Create self-weight load case ---
      const lcGuid = CreateGUID();
      const lc = {
        id: { guid: lcGuid, name: t("self-weight_short_.label") },
        type: ng.loadcase.Type.TYPE_SELF_WEIGHT,
        durationClass: ng.loadcase.DurationClass.DURATION_CLASS_PERMANENT,
        category: ng.loadcase.Category.CATEGORY_UNSPECIFIED,
        numberOfStoreys: 0,
        description: "A load case for self weight loads, automatically generated by the application.",
      };
      loading.loadcases = [...(loading.loadcases ?? []), lc as any];

      // Register in loadCombGenSettings if auto-generated combos exist
      const hasAutoCombos = (loading.combinations ?? []).some((lco: any) => lco.userDefined === false);
      if (hasAutoCombos) {
        const cache = ensureEndpointCache(state);
        const lcs = (cache as any)?.loadCombGenSettings;
        if (lcs?.loadcases) {
          lcs.loadcases.push(lcGuid);
        }
      }

      // --- Calculate and add self-weight loads ---
      const elementJson = struct.elements?.[0];
      if (!elementJson?.beam) return;
      const pieces = selfWeightLineLoads(
        projInput as unknown as ElementDbs,
        ng.beam.Data.fromObject(elementJson.beam),
      );
      const elemGuid = elementJson.id?.guid as string;
      loading.loads = [...(loading.loads ?? []), ...pieces.map((p) => selfWeightLoadJson(p, lcGuid, elemGuid))] as any;
    },
    /**
     * Rewrites the self-weight line loads in place, keeping their load case — and with it every
     * combination that refers to it. Dispatched by the self-weight middleware, and only when the
     * pieces it computed differ from the ones stored; does nothing when self-weight is switched off.
     */
    syncSelfWeightLoads: (state, action: PayloadAction<{ pieces: SelfWeightPiece[] }>) => {
      const struct = state.project?.data?.data?.input?.structures?.[0];
      const loading = struct?.loading;
      if (!loading) return;
      const lcGuid = selfWeightCaseGuid(loading.loadcases);
      if (!lcGuid) return;
      const elemGuid = struct.elements?.[0]?.id?.guid as string;
      const loads = loading.loads ?? [];
      // Put the new pieces where the old ones were, so the loads table does not reorder.
      const at = loads.findIndex((l: any) => l.lcaseGuid === lcGuid);
      const kept = loads.filter((l: any) => l.lcaseGuid !== lcGuid);
      const fresh = action.payload.pieces.map((p) => selfWeightLoadJson(p, lcGuid, elemGuid));
      kept.splice(at < 0 ? kept.length : at, 0, ...(fresh as any[]));
      loading.loads = kept;
    },
    delSelfWeights: (state, action) => {
      const structures = state.project?.data?.data?.input?.structures;
      if (!structures?.[0]?.loading) return;
      const loading = structures[0].loading!;

      const swGuid = GetSelfWeightGuid((loading.loadcases ?? []) as any);
      if (!swGuid) return;

      // Remove self-weight loads
      loading.loads = (loading.loads ?? []).filter((l: any) => l.lcaseGuid !== swGuid);

      // Remove self-weight load case
      loading.loadcases = (loading.loadcases ?? []).filter((lc: any) => lc.id?.guid !== swGuid);

      // Remove from user-defined combinations
      loading.combinations = (loading.combinations ?? [])
        .map((lco: any) => ({ ...lco, parts: (lco.parts ?? []).filter((p: any) => p.lcaseGuid !== swGuid) }))
        .filter((lco: any) => (lco.parts ?? []).length > 0);
      pruneOrphanLcombGuids(structures[0]);

      // Remove from loadCombGenSettings cache
      const cache = ensureEndpointCache(state);
      const lcs = (cache as any)?.loadCombGenSettings;
      if (lcs?.loadcases) {
        lcs.loadcases = lcs.loadcases.filter((lcGuid: string) => lcGuid !== swGuid);
      }
    },

    // LOAD COMBINATIONS
    addLoadCombination: (state, action) => {
      const { loadcombination } = action.payload;
      const structures = state.project?.data?.data?.input?.structures;
      if (structures && structures[0] && structures[0].loading) {
        const loading = structures[0].loading;
        const stage = structures[0].stages?.[0];
        const incoming = loadcombination as any;
        let normalizedLoadCombo: any = incoming;

        if (incoming?.__proto_name === "ng.loadcombination.Data") {
          normalizedLoadCombo = ng.loadcombination.Data.toObject(incoming as ng.loadcombination.Data) ?? incoming;
        }
        loading.combinations = [...(loading.combinations ?? []), normalizedLoadCombo];
        if (normalizedLoadCombo?.id?.guid && stage) {
          stage.lcombGuids = [...(stage.lcombGuids ?? []), normalizedLoadCombo.id.guid];
        }
      }
    },
    deleteLoadCombination: (state, action) => {
      const { guid } = action.payload;
      const structures = state.project?.data?.data?.input?.structures;
      if (!structures || !structures[0] || !structures[0].loading) return;
      const loading = structures[0].loading;

      loading.combinations = (loading.combinations ?? []).filter((lco: any) => lco.id?.guid !== guid);
      pruneOrphanLcombGuids(structures[0]);
    },
    updateLoadCombinationName: (state, action) => {
      const { guid, name } = action.payload;
      const combinations = state.project?.data?.data?.input?.structures?.[0]?.loading?.combinations;
      if (combinations) {
        const lco = combinations.find((lco: any) => lco.id?.guid === guid);
        if (lco && lco.id) {
          lco.id.name = name;
        }
      }
    },
    updateGroupLength: (state, action) => {
      const { length } = action.payload;
      const beam = getPrimaryBeamData(state);
      beam?.getConcrete()?.rebars?.grps.forEach((group) => {
        group.length = length;
      });
    },

    updateLoadCombinationType: (state, action) => {
      const { guid, type } = action.payload;
      const combinations = state.project?.data?.data?.input?.structures?.[0]?.loading?.combinations;
      if (combinations) {
        const lco = combinations.find((lco: any) => lco.id?.guid === guid);
        if (lco) {
          lco.type = ng.loadcombination.Type_ToObject(type) ?? type;
        }
      }
    },
    addLoadCombinationPart: (state, action) => {
      const { guid, part } = action.payload;
      const combinations = state.project?.data?.data?.input?.structures?.[0]?.loading?.combinations;
      if (combinations) {
        const lco = combinations.find((lco: any) => lco.id?.guid === guid);
        if (lco) {
          const incoming = part as any;
          let normalizedPart: any = incoming;

          if (incoming?.__proto_name === "ng.loadcombination.CombinationPart") {
            normalizedPart =
              ng.loadcombination.CombinationPart.toObject(incoming as ng.loadcombination.CombinationPart) ?? incoming;
          }

          lco.parts = [...(lco.parts ?? []), normalizedPart];
        }
      }
    },
    updateLoadCombinationPart: (state, action) => {
      const { lcoGuid, currentLcaGuid, nextLcaGuid, coefficient } = action.payload;
      const combinations = state.project?.data?.data?.input?.structures?.[0]?.loading?.combinations;
      if (!combinations) {
        return;
      }

      const lco = combinations.find((loadCombination: any) => loadCombination.id?.guid === lcoGuid);
      if (!lco?.parts || !currentLcaGuid) {
        return;
      }

      const part = lco.parts.find((combinationPart: any) => combinationPart.lcaseGuid === currentLcaGuid);
      if (!part) {
        return;
      }

      if (nextLcaGuid && part.lcaseGuid !== nextLcaGuid) {
        part.lcaseGuid = nextLcaGuid;
      }

      if (typeof coefficient === "number") {
        if (!part.coefficients || part.coefficients.length === 0) {
          part.coefficients = [
            {
              type:
                ng.loadcombination.CoefficientType_ToObject(
                  ng.loadcombination.CoefficientType.COEFFICIENT_TYPE_GAMMA,
                ) ?? "",
              value: coefficient,
            },
          ];
        } else {
          part.coefficients[0].value = coefficient;
          part.coefficients[0].type =
            part.coefficients[0].type ??
            ng.loadcombination.CoefficientType_ToObject(ng.loadcombination.CoefficientType.COEFFICIENT_TYPE_GAMMA) ??
            "";
        }
      }
    },
    deleteLoadCombinationPart: (state, action) => {
      const { guid, partGuid } = action.payload;
      const structure = state.project?.data?.data?.input?.structures?.[0];
      const combinations = structure?.loading?.combinations;
      if (combinations) {
        const lcoIndex = combinations.findIndex((lco: any) => lco.id?.guid === guid);
        if (lcoIndex > -1) {
          const lco = combinations[lcoIndex];
          if (lco && lco.parts) {
            lco.parts = lco.parts.filter((part: any) => part.lcaseGuid !== partGuid);
            if (lco.parts.length === 0) {
              combinations.splice(lcoIndex, 1);
              pruneOrphanLcombGuids(structure);
            }
          }
        }
      }
    },
    updateLoadCombinationCoa: (state, action) => {
      const { guid, coa } = action.payload;
      const combinations = state.project?.data?.data?.input?.structures?.[0]?.loading?.combinations;
      if (combinations) {
        const lco = combinations.find((lco: any) => lco.id?.guid === guid);
        if (lco) {
          lco.coa = ng.loadcombination.CoaType_ToObject(coa) ?? coa;
        }
      }
    },
    updateDominatingLCA: (state, action) => {
      const { lcaGuid, lcoGuid, checked } = action.payload;
      const combinations = state.project?.data?.data?.input?.structures?.[0]?.loading?.combinations;
      if (combinations) {
        const lco = combinations.find((lco) => lco.id?.guid === lcoGuid);
        if (lco) {
          lco.dominatingLcaseGuid = checked ? lcaGuid : "";
        }
      }
    },
    updateFavorableLCA: (state, action) => {
      const { lcaGuid, lcoGuid, checked } = action.payload;
      const combinations = state.project?.data?.data?.input?.structures?.[0]?.loading?.combinations;
      if (combinations) {
        const lco = combinations.find((lco: any) => lco.id?.guid === lcoGuid);
        if (lco && lco.parts) {
          const part = lco.parts.find((part: any) => part.lcaseGuid === lcaGuid);
          if (part) {
            part.favourablePermanentLoad = checked;
          }
        }
      }
    },
    considerInAnalysis: (state, action: PayloadAction<{ checked: boolean; tab: string }>) => {
      const { checked, tab } = action.payload;
      const combinations = state.project?.data?.data?.input?.structures?.[0]?.loading?.combinations;
      if (combinations) {
        for (const lco of combinations) {
          if (tab === "userDefined" && lco.userDefined === true) {
            lco.deactive = !checked;
          }
          if (tab === "generated" && lco.userDefined === false) {
            lco.deactive = !checked;
          }
        }
      }
    },
    // LCO Generator
    generateLoadCombs_apply: (state, action: PayloadAction<ng.endpoint_args.eurocode.LoadCombGenArgsOut>) => {
      const proj = state.project?.data?.data;
      let projInput = proj?.input;
      let structure = projInput?.structures?.[0];
      let loading = structure?.loading;
      let stage = structure?.stages?.[0];

      if (stage === undefined) {
        console.log("stage is undefined, not applying load combs");
        return;
      }

      const output = action.payload;

      // Parse the updated project and update the context
      // let argsOut = ng.endpoint_args.eurocode.LoadCombGenArgsOut.fromObject(output);
      const argsOut = output;

      if (loading && loading.combinations) {
        // state is good, apply
      } else {
        console.warn("[generateLoadCombs]: missing loading and combinations, new data ignored");
        return;
      }

      // Filter out the user defined load combinations
      let userDefined = loading.combinations.filter((lco) => lco.userDefined === true);

      // Filter out the user defined load combinations, and check if any are deactivated (means all are)
      let generated = loading.combinations.filter((lco) => lco.userDefined === false);

      // Inherit the "use in analysis" state from the generated combinations we are replacing: if the
      // user turned the group off, keep it off. When there is nothing to inherit from there is no
      // such intent to honour, so the fresh combinations must be active - generating into an empty
      // group used to produce a set that was deactivated from the start, which left the load
      // combination list empty with nothing to select and no hint as to why.
      const deactivated = generated.some((lco) => lco.deactive === true);

      // Re-set deactive flag for the new load combinations
      var newLoadcombinations = [...argsOut.loadcombinations]
        .map(ng.loadcombination.Data.toObject)
        .filter((x) => x != null);
      for (var newLC of newLoadcombinations) {
        newLC.deactive = deactivated;
      }

      // Let's overwrite the existing load combinations for now
      // Add the user defined load combinations back
      loading.combinations = newLoadcombinations.concat(userDefined);

      // Update the stage with the new load combinations guids
      stage.lcombGuids = loading.combinations
        .map((lco) => lco.id?.guid)
        .filter((guid) => guid !== undefined) as string[];
    },

    // shouldn't be called direction, use the wrapper above
    lcoGenerator_updateAlt: (state, action: PayloadAction<number>) => {
      const inputJson = state.project?.data.data?.input;
      if (!inputJson) {
        console.error("Incomplete project: missing inputJson");
        return;
      }

      const project = ng.input.Data.fromObject(inputJson);
      const val = Math.max(0, action.payload);

      if (project.code?.type !== "ec") {
        console.error("Project not ec");
        return;
      }
      project.code.ec.altitude = val;
      setProjectCodeEc(state, project.code.ec);
    },

    // shouldn't be called direction, use the wrapper above
    lcoGenerator_update_snowload: (state, action: PayloadAction<number>) => {
      const inputJson = state.project?.data.data?.input;
      if (!inputJson) {
        console.error("Incomplete project: missing inputJson");
        return;
      }

      const project = ng.input.Data.fromObject(inputJson);
      const val = Math.max(0, action.payload);

      if (project.code?.type !== "ec") {
        console.error("Project not ec");
        return;
      }
      project.code.ec.snowLoad = val;
      setProjectCodeEc(state, project.code.ec);
    },

    lcoGenerator_update_use610ab: (state, action: PayloadAction<boolean>) => {
      const inputJson = state.project?.data.data?.input;
      if (!inputJson) {
        console.error("Incomplete project: missing inputJson");
        return;
      }

      const project = inputJson;
      const val = action.payload;

      const structure = project?.structures?.[0] ?? undefined;
      const genDesSet_load = structure?.designSettings?.load;

      if (!genDesSet_load) {
        console.error("Incomplete project, missing genDesSet_load");
        return;
      }
      genDesSet_load.use610ab = val;
    },

    lcoGenerator_update_usePsi1: (state, action: PayloadAction<boolean>) => {
      const inputJson = state.project?.data.data?.input;
      if (!inputJson) {
        console.error("Incomplete project: missing inputJson");
        return;
      }

      const project = inputJson;
      const val = action.payload;

      const structure = project?.structures?.[0] ?? undefined;
      const genDesSet_load = structure?.designSettings?.load;

      if (!genDesSet_load) {
        console.error("Incomplete project, missing genDesSet_load");
        return;
      }
      genDesSet_load.usePsi1 = val;
    },

    lcoGenerator_update_selLCAs: (state, action: PayloadAction<string[]>) => {
      const settings = ensureLoadCombGenSettings(state);
      if (!settings) {
        console.error("Incomplete project: missing settings in enpoint cache");
        return;
      }

      settings.loadcases = action.payload;
    },
    lcoGenerator_update_selLCOs: (state, action: PayloadAction<ng.loadcombination.Type[]>) => {
      const settings = ensureLoadCombGenSettings(state);
      if (!settings) {
        console.error("Incomplete project: missing settings in enpoint cache");
        return;
      }

      settings.lcTypes = action.payload.map(ng.loadcombination.Type_ToObject).filter((x) => x != null);

      // if (e.includes("4")) {
      //     if (!context.proj?.data?.input?.structures[0].designSettings?.load) {
      //         context.proj!.data!.input!.structures[0]!.designSettings!.load = ng.design.load.GeneralDesignSettings.create({ use610ab: true });
      //     }
      //     else {
      //         context.proj!.data!.input!.structures[0]!.designSettings!.load.use610ab = true;
      //     }
      //     props.pushChanges(context);
      // }
    },
    lcoGenerator_inclPermLoadFavor: (state, action: PayloadAction<boolean>) => {
      const settings = ensureLoadCombGenSettings(state);
      if (!settings) {
        console.error("Incomplete project: missing settings in enpoint cache");
        return;
      }

      const simple = settings?.simple;
      if (!simple) {
        console.error("Missing simple type");
        return;
      }

      simple.includePermanentLoadFavorable = action.payload;
    },

    // COLUMN
    setColumnLength: (state, action: PayloadAction<number>) => {
      const length = Number(action.payload);
      if (!Number.isFinite(length) || length <= 0) {
        return;
      }
      const beam = getPrimaryBeamData(state);
      if (!beam) {
        return;
      }

      const concrete = beam.getConcrete();
      const groups = concrete?.rebars?.grps ?? [];
      const linkGrps = concrete?.links?.linkGrps ?? [];
      const nodes = getNodesData(state);

      nodes?.forEach((node) => {
        if (node && node.coord && node.id?.name === "n_1") {
          node.coord.x = length;
          node.coord.y = 0;
          node.coord.z = 0;
        }
      });
      let L = 0;
      let H = 0;
      let conc = false;
      const seg = beam.segments[0];
      if (beam.segments[0] && beam.segments[0].length) {
        L = beam.segments[0].length;

        const secGuid = seg?.layer?.start?.secGuid;
        const secDB = state.project?.data?.data?.input?.secDb ?? [];
        let sec = secDB.find((m) => m.id?.guid === secGuid);

        let inPeri = sec?.polylines?.perimeter || null;
        let bb = BoundingBox((inPeri?.points as any[]) || []); // [m]
        H = bb.maxZ - bb.minZ;
      }
      let outputPositions = createOutputPositions(21, length, H, conc);

      if (
        state.project &&
        state.project.data &&
        state.project.data.data &&
        state.project.data.data.input &&
        state.project.data.data.input.structures
      ) {
        state.project.data.data.input.structures[0].stages[0].elems[0].beam!.positions = outputPositions;
      }
      if (nodes && nodes?.length > 0) setStructureNodes(state, nodes ?? []);
      syncBeamDependentLengths(beam, groups, linkGrps, length);
      resyncBucklingForBeam(state, beam);
      resyncLateralTorsionalForBeam(state, beam);
      setPrimaryBeamData(state, beam);
    },

    updateConcreteCover: (state, action: PayloadAction<{ cover: number }>) => {
      const cover = Number(action.payload.cover);
      if (!Number.isFinite(cover)) {
        return;
      }

      const beam = getPrimaryBeamData(state);
      if (!beam) {
        return;
      }

      const concrete = beam.getConcrete();
      if (!concrete) {
        return;
      }

      for (const distance of concrete.distances) {
        distance.cover = cover;
      }

      updateLayerDValuesFromCover(state, beam);

      // Cover affects bar positions, so always regenerate (even in exact-coordinates mode)
      syncStandaloneBarsWithCurrentRebars(state, beam, true);
      setPrimaryBeamData(state, beam);
    },

    updateLongitudinalBarsCount: (state, action: PayloadAction<{ axis: "Y" | "Z"; num: number }>) => {
      const requestedNum = Math.round(Number(action.payload.num));
      if (!Number.isFinite(requestedNum)) {
        return;
      }

      const beam = getPrimaryBeamData(state);
      const concrete = beam?.getConcrete();
      const rebars = concrete?.rebars;
      const layers = rebars?.lays;
      if (!beam || !rebars || !layers) {
        return;
      }

      const sectionGeometry = getSectionGeometry(getPrimarySection(state, beam));
      const minBars = !rebars.exactBarCoordinates && !sectionGeometry?.circular ? 2 : 0;
      const num = Math.max(minBars, requestedNum);

      for (const layer of getLayersByAxis(layers, action.payload.axis)) {
        layer.setNum(num);
        layer.distr = ng.reinf.Distribution.DISTRIBUTION_EVEN;
      }

      syncStandaloneBarsWithCurrentRebars(state, beam);
      setPrimaryBeamData(state, beam);
    },

    updateLongitudinalDiameter: (state, action: PayloadAction<{ axis: "Y" | "Z"; diameter: number }>) => {
      const diameter = Math.max(0, Number(action.payload.diameter));
      if (!Number.isFinite(diameter)) {
        return;
      }

      const beam = getPrimaryBeamData(state);
      const concrete = beam?.getConcrete();
      const groups = concrete?.rebars?.grps ?? [];
      const linkGrps = concrete?.links?.linkGrps ?? [];
      if (!beam || !concrete) {
        return;
      }

      const group = getGroupByAxis(groups, action.payload.axis);
      if (!group) {
        return;
      }

      const beamLength = Number(beam?.segments?.[0]?.length);
      group.diameter = diameter;
      if (Number.isFinite(beamLength)) {
        syncBeamDependentLengths(beam, groups, linkGrps, beamLength);
      }

      syncStandaloneBarsWithCurrentRebars(state, beam);
      setPrimaryBeamData(state, beam);
    },

    updateRadialBarsCount: (state, action: PayloadAction<{ num: number }>) => {
      const num = Math.max(0, Math.round(Number(action.payload.num)));
      if (!Number.isFinite(num)) {
        return;
      }

      const beam = getPrimaryBeamData(state);
      const concrete = beam?.getConcrete();
      const layers = concrete?.rebars?.lays;
      if (!beam || !layers) {
        return;
      }

      for (const layer of getLayersByAxis(layers, "Y")) {
        layer.setNum(num);
        layer.distr = ng.reinf.Distribution.DISTRIBUTION_EVEN;
      }
      for (const layer of getLayersByAxis(layers, "Z")) {
        layer.setNum(num);
        layer.distr = ng.reinf.Distribution.DISTRIBUTION_EVEN;
      }

      syncStandaloneBarsWithCurrentRebars(state, beam);
      setPrimaryBeamData(state, beam);
    },

    updateRadialDiameter: (state, action: PayloadAction<{ diameter: number }>) => {
      const diameter = Math.max(0, Number(action.payload.diameter));
      if (!Number.isFinite(diameter)) {
        return;
      }

      const beam = getPrimaryBeamData(state);
      const concrete = beam?.getConcrete();
      const groups = concrete?.rebars?.grps ?? [];
      const linkGrps = concrete?.links?.linkGrps ?? [];
      if (!beam || !concrete) {
        return;
      }

      const yGroup = getGroupByAxis(groups, "Y");
      const zGroup = getGroupByAxis(groups, "Z");
      if (!yGroup) {
        return;
      }

      yGroup.diameter = diameter;
      if (zGroup) {
        zGroup.diameter = diameter;
      }

      const beamLength = Number(beam?.segments?.[0]?.length);
      if (Number.isFinite(beamLength)) {
        syncBeamDependentLengths(beam, groups, linkGrps, beamLength);
      }

      syncStandaloneBarsWithCurrentRebars(state, beam);
      setPrimaryBeamData(state, beam);
    },

    updateStirrupDiameter: (state, action: PayloadAction<{ diameter: number }>) => {
      const diameter = Number(action.payload.diameter);
      if (!Number.isFinite(diameter)) {
        return;
      }
      const diameterMeters = normalizeSectionLength(diameter);

      const beam = getPrimaryBeamData(state);
      const concrete = beam?.getConcrete();
      const linkGrps = concrete?.links?.linkGrps;
      if (!beam || !linkGrps || linkGrps.length === 0) {
        return;
      }

      // In exact-coordinates mode, shift each standalone bar inward by the change in stirrup diameter.
      // The stirrup sits between the cover and the bar, so a thicker stirrup pushes bars toward the centroid.
      const rebars = concrete?.rebars;
      if (rebars?.exactBarCoordinates && rebars.standaloneBars?.length) {
        const oldDiameterMeters = normalizeSectionLength(Number(linkGrps[0].diameter));
        const newDiameterMeters = diameterMeters;
        const delta = newDiameterMeters - oldDiameterMeters;
        if (Math.abs(delta) > 1e-12) {
          for (const bar of rebars.standaloneBars) {
            const y = Number(bar.startPoint?.y ?? 0);
            const z = Number(bar.startPoint?.z ?? 0);
            let newY = y;
            let newZ = z;
            switch (bar.zone) {
              case ng.design.concrete.BeamSide.BEAM_SIDE_TOP:
                newZ = z - delta;
                break;
              case ng.design.concrete.BeamSide.BEAM_SIDE_BOTTOM:
                newZ = z + delta;
                break;
              case ng.design.concrete.BeamSide.BEAM_SIDE_LEFT:
                newY = y + delta;
                break;
              case ng.design.concrete.BeamSide.BEAM_SIDE_RIGHT:
                newY = y - delta;
                break;
              default:
                continue;
            }
            if (bar.startPoint) {
              bar.startPoint.y = newY;
              bar.startPoint.z = newZ;
            }
          }
        }
      }

      linkGrps[0].diameter = diameterMeters;
      syncStandaloneBarsWithCurrentRebars(state, beam);
      setPrimaryBeamData(state, beam);
    },

    updateStirrupSpacing: (state, action: PayloadAction<{ spacing: number }>) => {
      const spacing = Number(action.payload.spacing);
      if (!Number.isFinite(spacing)) {
        return;
      }

      const beam = getPrimaryBeamData(state);
      const concrete = beam?.getConcrete();
      const linkGrps = concrete?.links?.linkGrps;
      if (!beam || !linkGrps || linkGrps.length === 0) {
        return;
      }

      linkGrps[0].s = spacing;
      setPrimaryBeamData(state, beam);
    },

    setExactBarCoordinates: (state, action: PayloadAction<{ enabled: boolean }>) => {
      const beam = getPrimaryBeamData(state);
      const rebars = beam ? getOrCreateRebars(beam) : null;
      if (!beam || !rebars) {
        return;
      }

      rebars.exactBarCoordinates = Boolean(action.payload.enabled);
      if (!rebars.exactBarCoordinates) {
        enforceMinimumLongitudinalBarsPerDirection(state, beam, 2);
      }
      rebars.standaloneBars = [];
      rebars.standaloneBars = createStandaloneBars(state, beam);
      setPrimaryBeamData(state, beam);
    },

    regenerateStandaloneBarsFromCurrentRebars: (state) => {
      const beam = getPrimaryBeamData(state);
      const rebars = beam ? getOrCreateRebars(beam) : null;
      if (!beam || !rebars) {
        return;
      }

      rebars.standaloneBars = createStandaloneBars(state, beam);
      setPrimaryBeamData(state, beam);
    },

    clearStandaloneBars: (state) => {
      const beam = getPrimaryBeamData(state);
      const concrete = beam?.getConcrete();
      const rebars = concrete?.rebars;
      if (!beam || !rebars) {
        return;
      }

      rebars.standaloneBars = [];
      setPrimaryBeamData(state, beam);
      cleanupUnusedReinforcementMaterialsInDb(state, getReferencedReinforcementMaterialGuids(beam));
    },

    deleteStandaloneBar: (state, action: PayloadAction<string>) => {
      const beam = getPrimaryBeamData(state);
      const concrete = beam?.getConcrete();
      const rebars = concrete?.rebars;
      if (!beam || !rebars) {
        return;
      }

      rebars.standaloneBars = rebars.standaloneBars.filter((bar) => bar.id?.guid !== action.payload);
      setPrimaryBeamData(state, beam);
      cleanupUnusedReinforcementMaterialsInDb(state, getReferencedReinforcementMaterialGuids(beam));
    },

    addStandaloneBar: (
      state,
      action: PayloadAction<{ y?: number; z?: number; diameter?: number; mtrlGuid?: string }>,
    ) => {
      const beam = getPrimaryBeamData(state);
      const concrete = beam?.getConcrete();
      if (!beam || !concrete) {
        return;
      }

      const rebars = concrete.rebars ?? ng.rebar.Data.create();
      const fallbackMaterialGuid =
        action.payload.mtrlGuid ??
        rebars.standaloneBars[0]?.mtrlGuid ??
        rebars.grps.find((group) => typeof group.mtrlGuid === "string" && group.mtrlGuid.length > 0)?.mtrlGuid ??
        "";
      const fallbackDiameter =
        action.payload.diameter ??
        rebars.standaloneBars[0]?.diameter ??
        rebars.grps.find((group) => Number.isFinite(Number(group.diameter)) && Number(group.diameter) > 0)?.diameter ??
        0;

      const y = Number(action.payload.y ?? 0);
      const z = Number(action.payload.z ?? 0);
      const endpoints = createStandaloneBarEndpoints(y, z, getBeamLength(beam));

      rebars.standaloneBars.push(
        ng.reinf.StandAloneBar.create({
          id: ng.utils.ID.create({ guid: CreateGUID() }),
          startPoint: endpoints.startPoint,
          endPoint: endpoints.endPoint,
          mtrlGuid: fallbackMaterialGuid,
          diameter: Number(fallbackDiameter),
          zone: getStandaloneBarZoneFromPosition(y, z),
        }),
      );

      concrete.rebars = rebars;
      setPrimaryBeamData(state, beam);
    },

    updateStandaloneBar: (
      state,
      action: PayloadAction<{ guid: string; y?: number; z?: number; diameter?: number }>,
    ) => {
      const beam = getPrimaryBeamData(state);
      const concrete = beam?.getConcrete();
      const rebars = concrete?.rebars;
      if (!beam || !rebars) {
        return;
      }

      const standaloneBar = rebars.standaloneBars.find((bar) => bar.id?.guid === action.payload.guid);
      if (!standaloneBar) {
        return;
      }

      const currentPoint =
        standaloneBar.startPoint ??
        standaloneBar.endPoint ??
        ng.geometry.Point3D.create({
          x: 0,
          y: Number(action.payload.y ?? 0),
          z: Number(action.payload.z ?? 0),
        });
      const nextY = action.payload.y ?? currentPoint.y;
      const nextZ = action.payload.z ?? currentPoint.z;
      const endpoints = createStandaloneBarEndpoints(nextY, nextZ, getBeamLength(beam));
      standaloneBar.startPoint = endpoints.startPoint;
      standaloneBar.endPoint = endpoints.endPoint;
      if (action.payload.diameter !== undefined) {
        standaloneBar.diameter = Number(action.payload.diameter);
      }
      standaloneBar.zone = getStandaloneBarZoneFromPosition(nextY, nextZ);

      setPrimaryBeamData(state, beam);
    },

    updateLongitudinalMaterialGuid: (state, action: PayloadAction<{ guid: string }>) => {
      const guid = action.payload.guid;
      if (!guid) {
        return;
      }

      const beam = getPrimaryBeamData(state);
      const concrete = beam?.getConcrete();
      const groups = concrete?.rebars?.grps;
      if (!beam || !groups || groups.length === 0) {
        return;
      }

      for (const group of groups) {
        group.mtrlGuid = guid;
      }

      for (const standaloneBar of concrete?.rebars?.standaloneBars ?? []) {
        standaloneBar.mtrlGuid = guid;
      }

      setPrimaryBeamData(state, beam);
      cleanupUnusedReinforcementMaterialsInDb(state, getReferencedReinforcementMaterialGuids(beam));
    },

    updateStirrupMaterialGuid: (state, action: PayloadAction<{ guid: string }>) => {
      const guid = action.payload.guid;
      if (!guid) {
        return;
      }

      const beam = getPrimaryBeamData(state);
      const concrete = beam?.getConcrete();
      const linkGrps = concrete?.links?.linkGrps;
      if (!beam || !linkGrps || linkGrps.length === 0) {
        return;
      }

      for (const linkGrp of linkGrps) {
        linkGrp.mtrlGuid = guid;
      }

      setPrimaryBeamData(state, beam);
      cleanupUnusedReinforcementMaterialsInDb(state, getReferencedReinforcementMaterialGuids(beam));
    },

    setAutoBucklingLength: (state, action: PayloadAction<boolean>) => {
      const endpointCache = ensureEndpointCache(state);
      if (!endpointCache) return;
      endpointCache.autoBucklingLength = action.payload;
    },

    setAutoDesign: (state, action: PayloadAction<unknown>) => {
      const endpointCache = ensureEndpointCache(state);
      if (endpointCache) {
        endpointCache.autoDesign = action.payload;
      }
    },

    setAutoDesignLimitUtilization: (state, action: PayloadAction<number>) => {
      const endpointCache = ensureEndpointCache(state);
      if (!endpointCache) return;

      const value = Number(action.payload);
      if (!Number.isFinite(value) || value < 0) return;

      const autoDesign = getAutoDesignFromEndpointCache(endpointCache);
      if (!autoDesign?.settings?.[0]) return;

      // Keep nested autoDesign object reference and only mutate the active settings item.
      autoDesign.settings[0].limitUtilization = value;
    },

    setCalculationMethod: (state, action: PayloadAction<CalculationMethod>) => {
      const column = ensureRcColumnSettings(state);
      if (!column) return;
      const method = action.payload;
      if (method === "stiffness") {
        column.secondOrderMethod = ng.design.concrete.SecondOrderMethod_ToObject(
          ng.design.concrete.SecondOrderMethod.SECOND_ORDER_METHOD_STIFFNESS,
        ) as unknown as string;
      } else if (method === "curvature") {
        column.secondOrderMethod = ng.design.concrete.SecondOrderMethod_ToObject(
          ng.design.concrete.SecondOrderMethod.SECOND_ORDER_METHOD_CURVATURE,
        ) as unknown as string;
      } else if (method === "general") {
        column.secondOrderMethod = ng.design.concrete.SecondOrderMethod_ToObject(
          ng.design.concrete.SecondOrderMethod.SECOND_ORDER_METHOD_UNSPECIFIED,
        ) as unknown as string;
      }
    },

    setConsiderCracking: (state, action: PayloadAction<boolean>) => {
      const ds = state.project?.data?.data?.input?.structures?.[0]?.designSettings;
      if (!ds?.rc) return;
      ds.rc.crackCheck = action.payload;
    },

    setCrackConvergence: (state, action: PayloadAction<number>) => {
      const ds = state.project?.data?.data?.input?.structures?.[0]?.designSettings;
      if (!ds?.rc) return;

      const value = Number(action.payload);
      // A ratio, not per mille - the edit field converts. Zero would never let the crack loop converge.
      if (!Number.isFinite(value) || value <= 0) return;

      ds.rc.crkConv = value;
    },

    setFireDuration: (state, action: PayloadAction<number>) => {
      const fire = ensureConcreteFireColumn(state);
      if (!fire) return;

      const value = Math.round(Number(action.payload));
      if (!Number.isFinite(value) || value < 0 || value > 5) return;

      fire.fireTime = value;
    },

    setFireExposureSide: (state, action: PayloadAction<{ side: FireExposureSide; enabled: boolean }>) => {
      const fire = ensureConcreteFireColumn(state);
      if (!fire) return;

      const { side, enabled } = action.payload;

      // A fire design with no exposed side is meaningless, so keep at least one side exposed.
      // This also protects the circular-section case, where "upperSide" is the only flag the UI
      // offers (rc-design-api reads it as the entire perimeter).
      if (!enabled && !FIRE_EXPOSURE_SIDES.some((other) => other !== side && fire[other] === true)) {
        return;
      }

      fire[side] = enabled;
    },

    setFireAggregate: (state, action: PayloadAction<ng.design.concrete.Aggregates>) => {
      const fire = ensureConcreteFireColumn(state);
      if (!fire) return;

      const serialized = ng.design.concrete.Aggregates_ToObject(action.payload);
      if (!serialized) return;

      fire.aggregate = serialized;
    },

    setFireManufacturing: (state, action: PayloadAction<ng.design.concrete.ManufacturingType>) => {
      const fire = ensureConcreteFireColumn(state);
      if (!fire) return;

      const serialized = ng.design.concrete.ManufacturingType_ToObject(action.payload);
      if (!serialized) return;

      fire.manufacturingRebars = serialized;
      fire.manufacturingStrands = serialized;
    },

    setFireUse02Epsilon: (state, action: PayloadAction<boolean>) => {
      const fire = ensureConcreteFireColumn(state);
      if (!fire) return;

      fire.use02Epsilon = action.payload;
    },

    /**
     * Fill in concrete fire settings a project arrived without, in one dispatch.
     *
     * Separate from the setters above because of what the middleware does with it: an input change
     * invalidates the calculation output, and SXInputConcreteFireSettings repairs missing or
     * annex-incompatible values on mount — so going through the setters would wipe the results of a
     * project the user has only just opened. This action is excluded from output-invalidation
     * (never from saving), which is only sound because it writes defaults for values that were not
     * there; a user editing one of these still goes through the setter, and still prunes.
     */
    initConcreteFireDefaults: (state, action: PayloadAction<ConcreteFireDefaults>) => {
      const fire = ensureConcreteFireColumn(state);
      if (!fire) return;

      const { fireTime, aggregate, manufacturing, use02Epsilon, sides } = action.payload;

      if (fireTime !== undefined) {
        const value = Math.round(Number(fireTime));
        if (Number.isFinite(value) && value >= 0 && value <= 5) fire.fireTime = value;
      }

      if (aggregate !== undefined) {
        const serialized = ng.design.concrete.Aggregates_ToObject(aggregate);
        if (serialized) fire.aggregate = serialized;
      }

      if (manufacturing !== undefined) {
        const serialized = ng.design.concrete.ManufacturingType_ToObject(manufacturing);
        if (serialized) {
          fire.manufacturingRebars = serialized;
          fire.manufacturingStrands = serialized;
        }
      }

      if (use02Epsilon !== undefined) {
        fire.use02Epsilon = use02Epsilon;
      }

      // The whole exposure patch is applied at once and only then checked, so the caller does not
      // have to order the writes around setFireExposureSide's one-side-at-a-time guard — switching
      // a circular section to its single perimeter flag clears three sides and sets one.
      if (sides) {
        const patched = {} as Record<FireExposureSide, boolean>;
        for (const side of FIRE_EXPOSURE_SIDES) {
          patched[side] = sides[side] ?? fire[side] === true;
        }
        // A fire design with no exposed side is meaningless — same rule as setFireExposureSide.
        if (FIRE_EXPOSURE_SIDES.some((side) => patched[side])) {
          for (const side of FIRE_EXPOSURE_SIDES) fire[side] = patched[side];
        }
      }
    },

    setSteelFireDuration: (state, action: PayloadAction<number>) => {
      const generalFire = ensureGeneralFire(state);
      if (!generalFire) return;
      const value = Math.round(Number(action.payload));
      if (!Number.isFinite(value) || value <= 0) return;
      generalFire.durationOfFire = value;
    },

    setSteelFireTimeStep: (state, action: PayloadAction<number>) => {
      const generalFire = ensureGeneralFire(state);
      if (!generalFire) return;
      const value = Math.round(Number(action.payload));
      if (!Number.isFinite(value) || value <= 0) return;
      generalFire.timeStep = value;
    },

    setSteelFireExposure: (state, action: PayloadAction<ng.design.steel.SectionExposure>) => {
      const fire = ensureSteelFire(state);
      if (!fire) return;
      const serialized = ng.design.steel.SectionExposure_ToObject(action.payload);
      if (!serialized) return;
      fire.sectionExposure = serialized;
    },

    setSteelFireTemperatureCurve: (state, action: PayloadAction<ng.design.general.TemperatureCurve>) => {
      const generalFire = ensureGeneralFire(state);
      if (!generalFire) return;
      const serialized = ng.design.general.TemperatureCurve_ToObject(action.payload);
      if (!serialized) return;
      generalFire.temperatureCurve = serialized;
    },

    setSteelFireCritDeflection: (state, action: PayloadAction<boolean>) => {
      const fire = ensureSteelFire(state);
      if (!fire) return;
      fire.critDeflection = action.payload;
    },

    setSteelFireProtectionMode: (state, action: PayloadAction<"mtrl" | "temp">) => {
      const fire = ensureSteelFire(state);
      if (!fire) return;

      if (action.payload === "mtrl") {
        const currentGuid = fire.protectingMaterial?.guid ?? "none_";
        const currentThickness = Number(fire.protectingMaterial?.thickness ?? 10e-3);
        setSteelFireProtectingMaterialJson(fire, {
          guid: currentGuid,
          thickness: Number.isFinite(currentThickness) ? currentThickness : 10e-3,
        });
        fire.protected = currentGuid !== "none_";
        return;
      }

      const currentTemperature = Number(fire.temperature ?? 400);
      setSteelFireTemperatureJson(fire, Number.isFinite(currentTemperature) ? currentTemperature : 400);
      fire.protected = true;
    },

    setSteelFireProtectingMaterial: (state, action: PayloadAction<string>) => {
      const fire = ensureSteelFire(state);
      if (!fire) return;

      const guid = action.payload || "none_";
      const currentThickness = Number(fire.protectingMaterial?.thickness ?? 10e-3);

      setSteelFireProtectingMaterialJson(fire, {
        guid,
        thickness: Number.isFinite(currentThickness) ? currentThickness : 10e-3,
      });
      fire.protected = guid !== "none_";
    },

    setSteelFireProtectionThickness: (state, action: PayloadAction<number>) => {
      const fire = ensureSteelFire(state);
      if (!fire) return;

      const value = Number(action.payload);
      if (!Number.isFinite(value) || value <= 0) return;

      const currentGuid = fire.protectingMaterial?.guid ?? "none_";
      setSteelFireProtectingMaterialJson(fire, {
        guid: currentGuid,
        thickness: value,
      });
      fire.protected = currentGuid !== "none_";
    },

    setSteelFireProtectionTemperature: (state, action: PayloadAction<number>) => {
      const fire = ensureSteelFire(state);
      if (!fire) return;

      const value = Number(action.payload);
      if (!Number.isFinite(value) || value <= 0) return;

      setSteelFireTemperatureJson(fire, value);
      fire.protected = true;
    },

    setTimberFireDuration: (state, action: PayloadAction<number>) => {
      const generalFire = ensureGeneralFire(state);
      if (!generalFire) return;
      const value = Math.round(Number(action.payload));
      if (!Number.isFinite(value) || value <= 0) return;
      generalFire.durationOfFire = value;
    },

    setTimberCharringRateAuto: (state, action: PayloadAction<boolean>) => {
      const fire = ensureTimberFire(state);
      if (!fire) return;
      fire.charringRate!.calculateAuto = action.payload;
    },

    setTimberCharringRate: (state, action: PayloadAction<number>) => {
      const fire = ensureTimberFire(state);
      if (!fire) return;
      fire.charringRate!.betaN = action.payload;
    },

    setTimberStructProtection: (
      state,
      action: PayloadAction<{ side: "top" | "bottom" | "left" | "right"; enabled: boolean }>,
    ) => {
      const fire = ensureTimberFire(state);
      if (!fire) return;
      const { side, enabled } = action.payload;
      const map = {
        top: "structProtectionTop",
        bottom: "structProtectionBottom",
        left: "structProtectionLeft",
        right: "structProtectionRight",
      } as const;
      (fire.protection as Record<string, unknown>)[map[side]] = enabled;
    },

    setTimberCharringStartTime: (
      state,
      action: PayloadAction<{ side: "top" | "bottom" | "left" | "right"; value: number }>,
    ) => {
      const fire = ensureTimberFire(state);
      if (!fire) return;
      const { side, value } = action.payload;
      const map = {
        top: "charringStartTimeTop",
        bottom: "charringStartTimeBottom",
        left: "charringStartTimeLeft",
        right: "charringStartTimeRight",
      } as const;
      (fire.protection as Record<string, unknown>)[map[side]] = value;
    },

    setTimberFireMaterialType: (state, action: PayloadAction<ng.fire_protection.timber.MaterialType>) => {
      const fire = ensureTimberFire(state);
      if (!fire) return;
      const serialized = ng.fire_protection.timber.MaterialType_ToObject(action.payload);
      if (!serialized) return;
      fire.protection!.mtrl!.properties!.materialType = serialized;
      const isNone =
        action.payload === ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_NONE ||
        action.payload === ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_UNSPECIFIED;
      fire.protected = !isNone;
    },

    setTimberFireMaterialProps: (
      state,
      action: PayloadAction<Partial<Omit<ng.fire_protection.timber.CharacteristicData_json, "materialType">>>,
    ) => {
      const fire = ensureTimberFire(state);
      if (!fire) return;
      Object.assign(fire.protection!.mtrl!.properties!, action.payload);
    },

    updateBucklingBeta: (state, action: PayloadAction<{ axis: "Y" | "Z"; spanIndex: number; beta: number }>) => {
      const { axis, spanIndex, beta } = action.payload;
      const beam = getPrimaryBeamData(state);
      if (!beam) return;
      const fb = getBucklingByAxis(beam, axis);
      if (!fb || spanIndex < 0 || spanIndex >= fb.data.length) return;
      const d = fb.data[spanIndex];
      fb.betas[spanIndex] = beta;
      d.setBeta(beta);
      // The engine reads only bucklingRes.lc, never the betas, so a beta edit must reach it too —
      // exactly as updateBucklingLc does (#415).
      const res = fb.bucklingRes;
      const spanLength = (d.position?.end ?? 0) - (d.position?.start ?? 0);
      if (res && spanLength > 0) {
        res.lc = beta * spanLength;
      }
      setPrimaryBeamData(state, beam);
    },

    updateBucklingLc: (state, action: PayloadAction<{ axis: "Y" | "Z"; spanIndex: number; lc: number }>) => {
      const { axis, spanIndex, lc } = action.payload;
      const beam = getPrimaryBeamData(state);
      if (!beam) return;
      const fb = getBucklingByAxis(beam, axis);
      if (!fb || spanIndex < 0 || spanIndex >= fb.data.length) return;
      const d = fb.data[spanIndex];
      const res = fb.bucklingRes;
      if (res) {
        res.lc = lc;
      }
      const spanLength = (d.position?.end ?? 0) - (d.position?.start ?? 0);
      if (spanLength <= 0) return;
      const beta = lc / spanLength;
      fb.betas[spanIndex] = beta;
      d.setBeta(beta);
      setPrimaryBeamData(state, beam);
    },

    setSteelBucklingLoadPosition: (state, action: PayloadAction<ng.beam.Alignment>) => {
      const beam = getPrimaryBeamData(state);
      if (!beam) return;

      const steel = beam.getSteel();
      if (!steel) return;

      steel.loadPosition = action.payload;
      setPrimaryBeamData(state, beam);
    },

    setTimberBucklingLoadPosition: (state, action: PayloadAction<ng.beam.Alignment>) => {
      const beam = getPrimaryBeamData(state);
      if (!beam) return;

      const timber = beam.getTimber();
      if (!timber) return;

      timber.loadPosition = action.payload;
      setPrimaryBeamData(state, beam);
    },

    updateSteelLtbBeta: (state, action: PayloadAction<{ loc: "top" | "btm"; spanIndex: number; beta: number }>) => {
      const { loc, spanIndex, beta } = action.payload;
      const beam = getPrimaryBeamData(state);
      if (!beam) return;
      const steel = beam.getSteel();
      if (!steel) return;
      const fb = loc === "top" ? steel.ltbTop : steel.ltbBtm;
      if (!fb || spanIndex < 0 || spanIndex >= fb.data.length) return;
      fb.betas[spanIndex] = beta;
      fb.data[spanIndex].setBeta(beta);
      setPrimaryBeamData(state, beam);
    },

    updateSteelLtbLc: (state, action: PayloadAction<{ loc: "top" | "btm"; spanIndex: number; lc: number }>) => {
      const { loc, spanIndex, lc } = action.payload;
      const beam = getPrimaryBeamData(state);
      if (!beam) return;
      const steel = beam.getSteel();
      if (!steel) return;
      const fb = loc === "top" ? steel.ltbTop : steel.ltbBtm;
      if (!fb || spanIndex < 0 || spanIndex >= fb.data.length) return;
      const d = fb.data[spanIndex];
      const spanLength = (d.position?.end ?? 0) - (d.position?.start ?? 0);
      if (spanLength <= 0) return;
      const beta = lc / spanLength;
      fb.betas[spanIndex] = beta;
      d.setBeta(beta);
      setPrimaryBeamData(state, beam);
    },

    setSteelContinuouslyRestrained: (state, action: PayloadAction<{ loc: "top" | "btm"; value: boolean }>) => {
      const { loc, value } = action.payload;
      const beam = getPrimaryBeamData(state);
      if (!beam) return;
      const steel = beam.getSteel();
      if (!steel) return;
      const fb = loc === "top" ? steel.ltbTop : steel.ltbBtm;
      if (!fb) return;
      fb.continouslyRestrained = value;
      setPrimaryBeamData(state, beam);
    },

    updateTimberLtsbLc: (state, action: PayloadAction<{ spanIndex: number; lc: number }>) => {
      const { spanIndex, lc } = action.payload;
      const beam = getPrimaryBeamData(state);
      if (!beam) return;
      const timber = beam.getTimber();
      const ltsb = timber?.ltsb;
      if (!ltsb || spanIndex < 0 || spanIndex >= ltsb.data.length) return;
      const d = ltsb.data[spanIndex];
      const spanLength = (d.position?.end ?? 0) - (d.position?.start ?? 0);
      if (spanLength <= 0) return;
      const beta = lc / spanLength;
      d.setBeta(beta);
      setPrimaryBeamData(state, beam);
    },

    updateTimberLtsbSupportCondition: (
      state,
      action: PayloadAction<{ spanIndex: number; supportCondition: ng.beam.SupportCondition }>,
    ) => {
      const { spanIndex, supportCondition } = action.payload;
      const beam = getPrimaryBeamData(state);
      if (!beam) return;
      const timber = beam.getTimber();
      const ltsb = timber?.ltsb;
      if (!ltsb || spanIndex < 0 || spanIndex >= ltsb.data.length) return;
      if (!ltsb.supportConditions) {
        ltsb.supportConditions = [];
      }
      ltsb.supportConditions[spanIndex] = supportCondition;
      ltsb.data[spanIndex].setSupportCondition(supportCondition);
      setPrimaryBeamData(state, beam);
    },

    setTimberLtsbContinuouslyRestrained: (state, action: PayloadAction<boolean>) => {
      const beam = getPrimaryBeamData(state);
      if (!beam) return;
      const timber = beam.getTimber();
      const ltsb = timber?.ltsb;
      if (!ltsb) return;
      ltsb.continouslyRestrained = action.payload;
      setPrimaryBeamData(state, beam);
    },

    addSupport: (state, action: PayloadAction<ng.stage.Support_json>) => {
      const structure = state.project?.data?.data?.input?.structures?.[0];
      const support = action.payload;
      if (structure) {
        if (!structure.sups) structure.sups = [];
        if (!structure.stages) structure.stages = [];
        if (structure.stages.length === 0) {
          structure.stages.push({
            sups: [],
            elems: [],
            lcombGuids: [],
          } as any);
        }

        if (!structure.stages[0].sups) {
          structure.stages[0].sups = [];
        }

        // Add to input.structures[0].sups (the definition)
        const supportData = ng.support.Data.toObject(
          ng.support.Data.create({
            id: ng.utils.ID.create({ guid: support.guid }),
          }),
        ) as any;

        if (supportData) {
          if (support.coord) {
            supportData.coord = {
              x: support.coord.x || 0,
              y: support.coord.y || 0,
              z: support.coord.z || 0,
            };
          }
          structure.sups.push(supportData as ng.support.Data_json);
        }

        // Add to input.structures[0].stages[0].sups (the instance in stage)
        structure.stages[0].sups.push(support);

        const beam = getPrimaryBeamData(state);
        if (beam) {
          resyncBucklingForBeam(state, beam);
          resyncLateralTorsionalForBeam(state, beam);
          setPrimaryBeamData(state, beam);
        }
      }
    },

    deleteSupport: (state, action: PayloadAction<string>) => {
      const guid = action.payload;
      const structure = state.project?.data?.data?.input?.structures?.[0];
      if (structure) {
        if (structure.sups) {
          structure.sups = structure.sups.filter((sup: any) => sup.id?.guid !== guid);
        }
        if (structure.stages?.[0]?.sups) {
          structure.stages[0].sups = structure.stages[0].sups.filter((sup: any) => sup.guid !== guid);
        }

        const beam = getPrimaryBeamData(state);
        if (beam) {
          resyncBucklingForBeam(state, beam);
          resyncLateralTorsionalForBeam(state, beam);
          setPrimaryBeamData(state, beam);
        }
      }
    },

    updateSupportPosition: (state, action: PayloadAction<{ guid: string; newPos: number }>) => {
      const { guid, newPos } = action.payload;
      const structure = state.project?.data?.data?.input?.structures?.[0];
      if (structure && structure.stages?.[0]?.sups) {
        const sup = structure.stages[0].sups.find((s: any) => s.guid === guid) as any;
        if (sup) {
          if (!sup.coord) sup.coord = { x: 0, y: 0, z: 0 };
          sup.coord.x = newPos;
        }
      }
      if (structure && structure.sups) {
        const supDef = structure.sups.find((s: any) => s.id?.guid === guid) as any;
        if (supDef) {
          if (!supDef.coord) supDef.coord = { x: 0, y: 0, z: 0 };
          supDef.coord.x = newPos;
        }
      }

      const beam = getPrimaryBeamData(state);
      if (beam) {
        resyncBucklingForBeam(state, beam);
        resyncLateralTorsionalForBeam(state, beam);
        setPrimaryBeamData(state, beam);
      }
    },

    updateSupportConnectivity: (
      state,
      action: PayloadAction<{ guid: string; axis: "Y" | "Z"; type: SupportConnectivityType }>,
    ) => {
      const { guid, axis, type } = action.payload;
      const supsJson = state.project?.data?.data?.input?.structures?.[0]?.stages?.[0]?.sups;
      if (supsJson) {
        const supIndex = supsJson.findIndex((s: ng.stage.Support_json) => s.guid === guid);
        if (supIndex !== -1) {
          const sup = ng.stage.Support.fromObject(supsJson[supIndex]);
          if (!sup.con) {
            sup.con = ng.topology.Connectivity.create({
              transX: ng.topology.Stiffness.create({ value: 0 }),
              transY: ng.topology.Stiffness.create({ value: 0 }),
              transZ: ng.topology.Stiffness.create({ value: 0 }),
              rotX: ng.topology.Stiffness.create({ value: 0 }),
              rotY: ng.topology.Stiffness.create({ value: 0 }),
              rotZ: ng.topology.Stiffness.create({ value: 0 }),
            });
          }

          // --- Base / top support detection ---
          const filteredSups = supsJson.filter((s: ng.stage.Support_json) => !s.lateral);
          const minX = Math.min(
            ...filteredSups.map((s: ng.stage.Support_json) => {
              const sObj = ng.stage.Support.fromObject(s);
              return sObj.getCoord()?.x ?? 0;
            }),
          );
          const supX = sup.getCoord()?.x ?? 0;
          const isBaseSupport = supX === minX;
          const beam = getPrimaryBeamData(state);
          const L = beam ? Number(beam.segments?.[0]?.length) : 0;
          const isTopSupport = Math.abs(supX - L) < 1e-3;

          // If it's the base support, prevent setting it to free or roller
          let finalType = type;
          if (isBaseSupport && (type === "free" || type === "roller")) {
            console.warn("Cannot set base support to free or roller. Reverting to pinned.");
            finalType = "pinned";
          }

          const con = sup.con;
          if (axis === "Y") {
            // axis Y: lateral transY, rotation rotZ only (transX recomputed below)
            if (finalType === "free") {
              if (con.transY) con.transY.value = 0;
              if (con.rotZ) con.rotZ.value = 0;
            } else if (finalType === "rigid" || (finalType === "rigid_roller" && isTopSupport)) {
              if (con.transY) con.transY.value = -1;
              if (con.rotZ) con.rotZ.value = -1;
            } else if (finalType === "pinned") {
              if (con.transY) con.transY.value = -1;
              if (con.rotZ) con.rotZ.value = 0;
            } else if (finalType === "roller") {
              if (con.transY) con.transY.value = -1;
              if (con.rotZ) con.rotZ.value = 0;
            } else if (finalType === "rigid_roller") {
              if (con.transY) con.transY.value = 0;
              if (con.rotZ) con.rotZ.value = -1;
            }
          } else if (axis === "Z") {
            // axis Z: lateral transZ, rotation rotY only (transX recomputed below)
            if (finalType === "free") {
              if (con.transZ) con.transZ.value = 0;
              if (con.rotY) con.rotY.value = 0;
            } else if (finalType === "rigid" || (finalType === "rigid_roller" && isTopSupport)) {
              if (con.transZ) con.transZ.value = -1;
              if (con.rotY) con.rotY.value = -1;
            } else if (finalType === "pinned") {
              if (con.transZ) con.transZ.value = -1;
              if (con.rotY) con.rotY.value = 0;
            } else if (finalType === "roller") {
              if (con.transZ) con.transZ.value = -1;
              if (con.rotY) con.rotY.value = 0;
            } else if (finalType === "rigid_roller") {
              if (con.transZ) con.transZ.value = 0;
              if (con.rotY) con.rotY.value = -1;
            }
          }
          // Recompute transX as the stricter axial constraint across both Y and Z axes.
          // This keeps the physical model consistent without coupling the two dropdowns.
          // Top supports never have axial restriction regardless of Y/Z type.
          const yType = classifySupport(sup, "Y", isBaseSupport);
          const zType = classifySupport(sup, "Z", isBaseSupport);
          const needsAxial =
            yType === "rigid" ||
            yType === "pinned" ||
            yType === "rigid_roller" ||
            zType === "rigid" ||
            zType === "pinned" ||
            zType === "rigid_roller";

          if (con.transX) con.transX.value = needsAxial && !isTopSupport ? -1 : 0;
          // Convert back to JSON for Redux state
          supsJson[supIndex] = ng.stage.Support.toObject(sup) as ng.stage.Support_json;

          if (beam) {
            resyncBucklingForBeam(state, beam);
            resyncLateralTorsionalForBeam(state, beam);
            setPrimaryBeamData(state, beam);
          }
        }
      }
    },

    setEc: (state, action) => {
      if (state.project && state.project.data) {
        let projObj = ng.frontend.column.Project.fromObject(state.project.data);
        if (projObj.data && projObj.data.input && projObj.data.input.getEc()) {
          projObj.data.input.setEc(action.payload);
          if (state.project) {
            const projectObj = ng.frontend.column.Project.toObject(projObj);
            if (projectObj) {
              state.project.data = projectObj;
            }
          }
        }
      }
    },

    /**
     * Drops the carbon figure, because an input it is calculated from has just changed.
     *
     * Dispatched by the sync middleware ahead of the recalculation it schedules, so the readout goes
     * empty and then fills in rather than showing the previous column's number as if it were this
     * one's. It is dispatched independently of whether a recalculation can follow: resetting is the
     * part that must not wait for an endpoint, which is what keeps a material without one (none of
     * the three today) empty instead of showing the previous column's number.
     *
     * Excluded from BOTH middleware denylists: it touches nothing but output.carbon, so it must not
     * prune the rest of output, and the input change that caused it already schedules the save.
     */
    resetCarbonFootprint: (state) => {
      if (carbonHasFigure(carbonOf(state))) {
        setCarbon(state, []);
      }
      state.carbonStatus = "idle";
      // Discards the answer of a call still in flight: it was started for the input this reset just
      // superseded (see the requestId guard in updateCarbonFootprint's reducers).
      state.carbonRequestId = null;
    },

    pruneOutput: (state) => {
      const hadOutput = !!state.project?.data?.data?.output;
      if (hadOutput) {
        console.warn(
          "[projectSlice] pruneOutput called — CLEARING existing output!",
          "guid=",
          state.project?.guid,
          "materialType=",
          (
            state.project?.data?.data?.input?.structures?.[0]?.elements?.[0]?.beam as
              | Record<string, unknown>
              | undefined
          )?.concrete
            ? "concrete"
            : "other",
          new Error().stack,
        );
      }
      // Carbon depends only on the input, so it survives the invalidation (see carbonOf) — the
      // readout keeps showing the last figure instead of blanking on every edit. An edit that does
      // change it has already scheduled a recalculation.
      const carbon = carbonSnapshot(state);
      if (state.project?.data?.data) {
        state.project.data.data.output = undefined;
        restoreCarbon(state, carbon);
      }
      state.analysisStatus = "idle";
      state.codeCheckStatus = "idle";
      state.designStatus = "idle";
    },

    setSecondOrder: (state, action: PayloadAction<Boolean>) => {
      const beam = state.project?.data?.data?.input?.structures?.[0]?.elements?.[0]?.beam;
      const ds = state.project?.data?.data?.input?.structures?.[0]?.elements?.[0]?.designSettings;

      if (!beam || !ds) return;

      const consider = Boolean(action.payload);

      if (beam.concrete && ds.rc) {
        ds.rc.consider2ndOrderEffects = consider;
      } else if (beam.steel && ds.steel?.beam) {
        ds.steel.beam.secondOrderAnalysis = ng.design.steel.SecondOrderAnalysis_ToObject(
          consider
            ? ng.design.steel.SecondOrderAnalysis.SECOND_ORDER_ANALYSIS_CONSIDER
            : ng.design.steel.SecondOrderAnalysis.SECOND_ORDER_ANALYSIS_IGNORE,
        )!;
      } else if (beam.timber && ds.timber?.beam) {
        ds.timber.beam.secondOrderAnalysis = ng.design.timber.SecondOrderAnalysis_ToObject(
          consider
            ? ng.design.timber.SecondOrderAnalysis.SECOND_ORDER_ANALYSIS_CONSIDER
            : ng.design.timber.SecondOrderAnalysis.SECOND_ORDER_ANALYSIS_IGNORE,
        )!;
      }

      const combinations = state.project?.data?.data?.input?.structures?.[0]?.loading?.combinations;
      if (combinations) {
        for (const lcomb of combinations) {
          if (lcomb.beamConfig) {
            lcomb.beamConfig.secondOrder = consider;
          }
        }
      }
    },

    setInitBowImp: (state, action: PayloadAction<number>) => {
      const beam = state.project?.data?.data?.input?.structures?.[0]?.elements?.[0]?.beam;

      if (!beam) return;
      beam.initialBowCoef = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(calcSavedRevision, (state, action) => {
      const meta = state.project?.data?.meta;
      // Only ever forwards: a revision is never moved back.
      if (meta && Number(meta.revision ?? 0) < action.payload) meta.revision = action.payload;
    });

    builder.addCase(analyseProject.pending, (state) => {
      state.analysisStatus = "loading";
      state.calcError = null;
    });
    builder.addCase(analyseProject.fulfilled, (state, action) => {
      // The analysis answers with output of its own (which carries no carbon), so keep the figure
      // the readout is already showing — the input it was calculated from has not changed.
      const carbon = carbonSnapshot(state);
      if (state.project) {
        // Cloned: Immer does not draft an assigned subtree, so the payload object itself becomes the
        // new state, and restoreCarbon below writes into it. Plain JSON off the wire tolerates that;
        // a payload read out of Redux state is frozen and throws. See applyRecommendedDesign, where
        // exactly that killed the reducer. The clone costs far less than the response.json() that
        // just produced this payload.
        state.project.data = structuredClone(action.payload);
        restoreCarbon(state, carbon);
      }
      state.status = "succeeded";
      state.analysisStatus = "succeeded";
      state.codeCheckStatus = "idle";
    });
    builder.addCase(analyseProject.rejected, (state, action) => {
      if (wasCancelled(action)) {
        state.analysisStatus = "idle";
        return;
      }
      state.status = "failed";
      state.analysisStatus = "failed";
      state.calcError = action.error?.message ?? "Analysis failed";
    });

    builder.addCase(codeCheckProject.pending, (state) => {
      state.codeCheckStatus = "loading";
      state.calcError = null;
    });
    builder.addCase(codeCheckProject.fulfilled, (state, action) => {
      // A code check normally answers with a carbon result of its own, which supersedes whatever is
      // on screen. When it does not — a design service that does not calculate it yet, or a run that
      // could not produce it — the figure already calculated from this same input is kept rather
      // than dropped. restoreCarbon only fills a gap; it never overwrites a fresh result.
      const carbon = carbonSnapshot(state);
      if (state.project) {
        // Cloned: Immer does not draft an assigned subtree, so the payload object itself becomes the
        // new state, and restoreCarbon below writes into it. Plain JSON off the wire tolerates that;
        // a payload read out of Redux state is frozen and throws. See applyRecommendedDesign, where
        // exactly that killed the reducer. The clone costs far less than the response.json() that
        // just produced this payload.
        state.project.data = structuredClone(action.payload);
        restoreCarbon(state, carbon);
      }
      state.status = "succeeded";
      state.codeCheckStatus = "succeeded";
    });
    builder.addCase(codeCheckProject.rejected, (state, action) => {
      if (wasCancelled(action)) {
        state.codeCheckStatus = "idle";
        return;
      }
      state.status = "failed";
      state.codeCheckStatus = "failed";
      state.calcError = action.error?.message ?? "Code check failed";
    });

    builder.addCase(generateLoadCombsApi.pending, (state) => {
      state.generateLoadCombsStatus = "loading";
    });
    builder.addCase(generateLoadCombsApi.fulfilled, (state) => {
      state.generateLoadCombsStatus = "idle";
    });
    builder.addCase(generateLoadCombsApi.rejected, (state) => {
      state.generateLoadCombsStatus = "idle";
    });

    builder.addCase(updateGammaApi.pending, (state) => {
      state.gammaStatus = "loading";
    });
    builder.addCase(updateGammaApi.fulfilled, (state, action) => {
      // Replace the project's inner Data wholesale with the gamma-updated project from the server.
      // A section change fires both gamma and a carbon recalculation, and gamma snapshots the
      // project when it starts — so without this the (later) gamma response would drop a carbon
      // result that had already landed.
      const carbon = carbonSnapshot(state);
      if (state.project?.data) {
        // Cloned: Immer does not draft an assigned subtree, so the payload object itself becomes the
        // new state, and restoreCarbon below writes into it. Plain JSON off the wire tolerates that;
        // a payload read out of Redux state is frozen and throws. See applyRecommendedDesign, where
        // exactly that killed the reducer. The clone costs far less than the response.json() that
        // just produced this payload.
        state.project.data.data = structuredClone(action.payload);
        restoreCarbon(state, carbon);
      }
      state.gammaStatus = "idle";
    });
    builder.addCase(updateGammaApi.rejected, (state, action) => {
      state.gammaStatus = "idle";
      console.error("updateGamma rejected:", action.error?.message);
    });

    builder.addCase(designProject.pending, (state) => {
      state.designStatus = "loading";
      state.calcError = null;
    });
    builder.addCase(designProject.fulfilled, (state, action) => {
      // Do NOT apply the design result to the project here. The recommended project and the
      // full suggestion list are staged in designReviewSlice (which also listens to this
      // action) and only committed to state.project once the user approves a suggestion in the
      // review modal — via applyRecommendedDesign / applyDesignSuggestion. This keeps the
      // design result from silently overwriting (and auto-saving) the user's project.
      state.status = "succeeded";
      state.designStatus = "succeeded";
    });
    builder.addCase(designProject.rejected, (state, action) => {
      if (wasCancelled(action)) {
        state.designStatus = "idle";
        return;
      }
      state.status = "failed";
      state.designStatus = "failed";
      state.calcError = action.error?.message ?? "Design failed";
    });

    builder.addCase(fetchProject.pending, () => {});
    builder.addCase(fetchProject.fulfilled, (state, action) => {
      const incomingProject = action.payload as IFile;
      const { project, projObj } = normalizeIncomingProject(incomingProject);

      const hadOutput = !!project?.data?.data?.output;
      const beam = project?.data?.data?.input?.structures?.[0]?.elements?.[0]?.beam;
      const materialType = beam?.concrete ? "concrete" : beam?.steel ? "steel" : beam?.timber ? "timber" : "unknown";
      console.log(
        `[projectSlice] fetchProject.fulfilled guid=${project?.guid} hasOutput=${hadOutput} materialType=${materialType}`,
      );

      state.project = project;
      state.status = "succeeded";
      syncStandaloneBarsOnProjectLoad(state);

      const hasOutputAfterSync = !!state.project?.data?.data?.output;
      if (hadOutput && !hasOutputAfterSync) {
        console.warn("[projectSlice] OUTPUT LOST after syncStandaloneBarsOnProjectLoad!");
      }

      if (projObj?.data) {
        const activeLCOs = updateActiveLcosInContext(projObj.data);
        state.activeLCOs = activeLCOs;
      }
    });
    builder.addCase(fetchProject.rejected, (state, action) => {
      state.status = "failed";
    });

    // Standalone carbon recalculation. Deliberately quiet: it neither sets `status` nor produces a
    // calcError, because the user did not ask for a calculation — they changed an input
    // and this keeps the readout honest. Only `carbonStatus` moves, which the readout uses to show
    // that the figure is being updated.
    builder.addCase(updateCarbonFootprint.pending, (state, action) => {
      state.carbonStatus = "loading";
      state.carbonRequestId = action.meta.requestId;
    });
    builder.addCase(updateCarbonFootprint.fulfilled, (state, action) => {
      // Superseded by a newer call — its answer describes input the project no longer has.
      if (action.meta.requestId !== state.carbonRequestId) return;
      setCarbon(state, action.payload);
      state.carbonStatus = "succeeded";
    });
    builder.addCase(updateCarbonFootprint.rejected, (state, action) => {
      if (action.meta.requestId !== state.carbonRequestId) return;
      state.carbonStatus = "failed";
      // Clear the figure rather than leave the previous one standing: this call was made because
      // the input it was calculated from changed, so the old number is known to be wrong. Better an
      // empty readout than a confidently displayed stale value. Only if there is one to clear — no
      // point creating an `output` object just to hold an empty carbon list.
      if (carbonHasFigure(carbonOf(state))) {
        setCarbon(state, []);
      }
      console.error("carbon footprint update rejected:", action.error?.message);
    });

    builder.addCase(fetchAutoBuckling.pending, (state) => {
      state.bucklingStatus = "loading";
    });
    builder.addCase(fetchAutoBuckling.fulfilled, (state, action) => {
      const beam = getPrimaryBeamData(state);
      if (!beam) {
        state.bucklingStatus = "failed";
        return;
      }

      const payload = action.payload as {
        stiff?: { start: number; end: number; beta: number; mode?: number[] }[];
        stiffBucklingLoad?: number;
        weak?: { start: number; end: number; beta: number; mode?: number[] }[];
        weakBucklingLoad?: number;
      };
      const hasStiff = Array.isArray(payload.stiff) && payload.stiff.length > 0;
      const hasWeak = Array.isArray(payload.weak) && payload.weak.length > 0;

      // If the API returned empty arrays for both planes, treat as failure — keep existing values.
      if (!hasStiff && !hasWeak) {
        state.bucklingStatus = "failed";
        return;
      }

      const applySpans = (
        fb: ng.beam.FlexBuckling,
        spans: { start: number; end: number; beta: number; mode?: number[] }[],
        bucklingLoad: number,
      ) => {
        fb.data = spans.map((s) => {
          const beta = Number.isFinite(s.beta) && s.beta > 0 ? s.beta : 1.0;
          return ng.beam.BucklingData.create({
            position: ng.beam.BucklingSpan.create({ start: s.start, end: s.end }),
            beta,
          });
        });
        const L = Number(beam.segments?.[0]?.length);
        fb.betas = fb.data.map((d) => d.getBeta() ?? 1.0);
        let minBeta = Math.min(...fb.betas);
        if (minBeta <= 0) {
          console.warn("Received non-positive beta from buckling API. Defaulting to 1.0.");
          minBeta = 1.0;
        }
        fb.bucklingRes = ng.beam.BucklingRes.create({
          bucklingLoad: Number.isFinite(bucklingLoad) ? bucklingLoad : 0,
          lc: Math.round(minBeta * L * 1e3) / 1e3, // Round to 3 decimals to avoid excessively long numbers in the UI
          vals: [],
          modes: spans.map((s) => ng.beam.BucklingRes.mode.create({ v: s.mode ?? [] })),
          freqs: [],
        });
      };

      const material = beam.getConcrete() ?? beam.getSteel() ?? beam.getTimber();
      if (material) {
        if (hasStiff && material.fbStiff) applySpans(material.fbStiff, payload.stiff!, payload.stiffBucklingLoad ?? 0);
        if (hasWeak && material.fbWeak) applySpans(material.fbWeak, payload.weak!, payload.weakBucklingLoad ?? 0);
      }

      setPrimaryBeamData(state, beam);
      state.bucklingStatus = "succeeded";
    });
    builder.addCase(fetchAutoBuckling.rejected, (state) => {
      state.bucklingStatus = "failed";
    });
  },
  selectors: {
    selectProject: (state) => state.project,
    projects: (state) => state.projects,
    activeLCOs: (state) => state.activeLCOs,
    selectAnalysisStatus: (state) => state.analysisStatus,
    selectCodeCheckStatus: (state) => state.codeCheckStatus,
    selectDesignStatus: (state) => state.designStatus,
    /**
     * Whether an analysis result is on the project. Every input change prunes `output` (see
     * pruneOutput), so this goes false the moment the results no longer belong to the current
     * input — which is exactly when the calculations that consume the section forces (the code
     * check and the member design) must not be started.
     */
    selectHasAnalysisOutput: (state) => !!state.project?.data?.data?.output?.lcombs?.length,
    /**
     * Whether a code check has been run. An analysis on its own fills `output.lcombs` with section
     * forces and no controls at all, so `selectHasAnalysisOutput` says nothing about this — the
     * controls are what a code check adds.
     *
     * Both shapes the controls report reads count (see SXRcControlsReport): the controls the engine
     * returns inside each load combination's element results, and the legacy per-material
     * `DesignSummary.controls` it falls back to. Unlike collectGoverningControls this only answers
     * whether any control exists, so it stops at the first one rather than collecting and deduping
     * them — it is read on every render.
     */
    selectHasCodeCheckOutput: (state) => {
      const output = state.project?.data?.data?.output;
      if (!output) return false;

      for (const lcomb of output.lcombs ?? []) {
        for (const elRes of lcomb.elementResults ?? []) {
          const result = elRes.result?.result;
          if (!result) continue;
          if (
            result.rc?.maxOfControls?.length ||
            result.steel?.maxOfControls?.length ||
            result.timber?.maxOfControls?.length
          ) {
            return true;
          }
        }
      }

      return [...(output.rc ?? []), ...(output.steel ?? []), ...(output.timber ?? [])].some(
        (summary) => summary.controls?.length,
      );
    },
    selectBucklingStatus: (state) => state.bucklingStatus,
    selectCarbonStatus: (state) => state.carbonStatus,
    selectSecondOrder: (state) => {
      const beam = state.project?.data?.data?.input?.structures?.[0]?.elements?.[0]?.beam;
      const ds = state.project?.data?.data?.input?.structures?.[0]?.elements?.[0]?.designSettings;
      if (!beam || !ds) return false;
      if (beam.concrete && ds.rc) return ds.rc.consider2ndOrderEffects ?? false;
      if (beam.steel && ds.steel?.beam)
        return (
          (ds.steel.beam.secondOrderAnalysis as unknown as string) ===
          ng.design.steel.SecondOrderAnalysis_ToObject(
            ng.design.steel.SecondOrderAnalysis.SECOND_ORDER_ANALYSIS_CONSIDER,
          )
        );
      if (beam.timber && ds.timber?.beam)
        return (
          (ds.timber.beam.secondOrderAnalysis as unknown as string) ===
          ng.design.timber.SecondOrderAnalysis_ToObject(
            ng.design.timber.SecondOrderAnalysis.SECOND_ORDER_ANALYSIS_CONSIDER,
          )
        );
      return false;
    },
    selectGenerateLoadCombsStatus: (state) => state.generateLoadCombsStatus,
    selectCalcError: (state) => state.calcError,
  },
});
export const {
  selectProject,
  selectGenerateLoadCombsStatus,
  projects,
  activeLCOs,
  selectAnalysisStatus,
  selectCodeCheckStatus,
  selectDesignStatus,
  selectHasAnalysisOutput,
  selectHasCodeCheckOutput,
  selectBucklingStatus,
  selectCarbonStatus,
  selectSecondOrder,
  selectCalcError,
} = ProjectSlice.selectors;
